import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { CreditCard, Plus, X, Search, DollarSign, Lock, FileText, TrendingUp, ShoppingBag, Store, Clock, Package, Table, ShoppingCart, CheckCircle, Send, ChefHat, Truck, MapPin, Ban, Edit, Trash2, AlertCircle, Power } from 'lucide-react';
import toast from 'react-hot-toast';
import io from 'socket.io-client';
import ModalDelivery from '../../components/ModalDelivery';
import './Caixa.css';

const __impressoCache = new Map();
const __IMPRESSAO_TTL_MS = 60 * 1000;
const __impressaoLock = new Map();
const __IMPRESSAO_LOCK_MS = 6000;

const __hashStr = (input) => {
  const str = (input || '').toString();
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

const __dedupeKey = ({ estabelecimentoId, id, tipo, numero, pedidoNumero, conteudo }) => {
  const est = (estabelecimentoId || '').toString();
  if (id !== undefined && id !== null && `${id}`.length) {
    return `${est}|${id}`;
  }
  const t = (tipo || 'comanda').toString();
  const n = (numero || pedidoNumero || '').toString();
  const h = __hashStr(conteudo);
  return `${est}|${t}|${n}|${h}`;
};

const __dedupeKeyCurta = ({ estabelecimentoId, id, tipo, numero, pedidoNumero }) => {
  const est = (estabelecimentoId || '').toString();
  if (id !== undefined && id !== null && `${id}`.length) {
    return `${est}|${id}`;
  }
  const t = (tipo || 'comanda').toString();
  const n = (numero || pedidoNumero || '').toString();
  return `${est}|${t}|${n}`;
};

// ✅ Para evitar duplicidade (socket + polling) no caixa, imprimir somente via polling.
// O backend sempre grava o comando no /api/agente/comandos.json e o polling confirma/remover,
// então este é o caminho mais determinístico.
const __IMPRIMIR_VIA_SOCKET_NO_CAIXA = false;

const __LS_KEY_IMPRESSO = '__pdv_impresso_v1';
const __LS_TTL_MS = 10 * 60 * 1000;
const __lsRead = () => {
  try {
    const raw = localStorage.getItem(__LS_KEY_IMPRESSO);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};
const __lsWrite = (obj) => {
  try {
    localStorage.setItem(__LS_KEY_IMPRESSO, JSON.stringify(obj));
  } catch (e) {}
};
const __limparLsExpirados = (obj) => {
  const agora = Date.now();
  const novo = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (typeof v === 'number' && (agora - v) <= __LS_TTL_MS) {
      novo[k] = v;
    }
  }
  return novo;
};
const __jaImpressoGlobal = (chave) => {
  if (typeof window === 'undefined') return false;
  if (!chave) return false;
  const obj = __limparLsExpirados(__lsRead());
  __lsWrite(obj);
  return Boolean(obj[chave]);
};
const __marcarImpressoGlobal = (chave) => {
  if (typeof window === 'undefined') return;
  if (!chave) return;
  const obj = __limparLsExpirados(__lsRead());
  obj[chave] = Date.now();
  __lsWrite(obj);
};

const __marcarImpresso = (chave) => {
  if (!chave) return;
  __impressoCache.set(chave, Date.now());
};
const __jaImpresso = (chave) => {
  if (!chave) return false;
  const ts = __impressoCache.get(chave);
  if (!ts) return false;
  if (Date.now() - ts > __IMPRESSAO_TTL_MS) {
    __impressoCache.delete(chave);
    return false;
  }
  return true;
};

const __lockImpressao = (chave) => {
  if (!chave) return;
  __impressaoLock.set(chave, Date.now());
};

const __impressaoTravada = (chave) => {
  if (!chave) return false;
  const ts = __impressaoLock.get(chave);
  if (!ts) return false;
  if (Date.now() - ts > __IMPRESSAO_LOCK_MS) {
    __impressaoLock.delete(chave);
    return false;
  }
  return true;
};

const __agenteOnlineRef = { current: false };

const CATEGORIA_CARNES_DO_DIA = 'CARNES DO DIA';
const CATEGORIA_CARNE_EXTRA = 'CARNES DO DIA - EXTRA';
const NOME_CARNE_EXTRA = 'Carne extra';
const PREFIXO_GUARNICAO = 'GUARNIÇÃO: ';
const PREFIXO_CARNE = 'CARNE: ';

const normalizarTexto = (v) => String(v || '').trim().toLowerCase();

const isProdutoMarmitex = (produto) => {
  const categoria = normalizarTexto(produto?.categoria);
  if (categoria === 'marmitex') return true;
  const nome = normalizarTexto(produto?.nome);
  return nome.includes('marmitex');
};

const obterLimiteCarnes = (produto, variacao) => {
  const base = normalizarTexto(variacao?.nome || produto?.nome);
  const match = base.match(/(\d+)\s*carn/);
  if (match && match[1]) {
    const n = parseInt(match[1], 10);
    if (Number.isFinite(n)) return Math.min(10, Math.max(0, n));
  }
  if (base.includes('pequena')) return 1;
  if (base.includes('media') || base.includes('média')) return 2;
  if (base.includes('grande')) return 3;
  return 1;
};

const contarCarnesSelecionadas = (acrescimos) => {
  const arr = Array.isArray(acrescimos) ? acrescimos : [];
  return arr
    .filter((a) => normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)))
    .reduce((acc, a) => acc + Math.min(20, Math.max(1, Number(a?.quantidade || 1))), 0);
};

const CaixaFullscreen = () => {
  const { user } = useAuth();
  const isMountedRef = useRef(true);
  const instanciaIdRef = useRef(`pdv-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [caixaAberto, setCaixaAberto] = useState(null);
  const [valorAbertura, setValorAbertura] = useState('');
  const [modalAbertura, setModalAbertura] = useState(false);
  const [modalVenda, setModalVenda] = useState(false);
  const [modalFechamento, setModalFechamento] = useState(false);
  const [valorFechamento, setValorFechamento] = useState('');
  const [relatorioFechamento, setRelatorioFechamento] = useState(null);
  const [carrinho, setCarrinho] = useState([]);
  const [cliente, setCliente] = useState({ nome: '', cpf: '', telefone: '' });
  const [formaPagamento, setFormaPagamento] = useState({ tipo: 'dinheiro', valor: 0, troco: 0 });
  const [dataVencimentoFiado, setDataVencimentoFiado] = useState(() => {
    const data = new Date();
    data.setDate(data.getDate() + 30);
    return data.toISOString().split('T')[0];
  });
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [tipoEntrega, setTipoEntrega] = useState('retirada'); // 'delivery' ou 'retirada'
  const [enderecoEntrega, setEnderecoEntrega] = useState({
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: 'Cachoeiro de Itapemirim',
    estado: 'ES',
    cep: '',
    referencia: ''
  });
  const [abaAtiva, setAbaAtiva] = useState('caixa'); // 'caixa', 'mesas', 'pedidos' ou 'marmitex'
  const [mesaSelecionada, setMesaSelecionada] = useState('');
  const [mesaBusca, setMesaBusca] = useState('');
  const [comandaSelecionadaModal, setComandaSelecionadaModal] = useState(null);
  const [modalAdicionarItem, setModalAdicionarItem] = useState(false);
  const [produtoSelecionadoModal, setProdutoSelecionadoModal] = useState(null);
  const [novoItem, setNovoItem] = useState({ produto: null, variacao: null, quantidade: 1, clienteItem: '', observacoes: '', acrescimos: [], marmitexSemCarne: false, marmitexPermitirExtra: false });
  const [categoriaAtiva, setCategoriaAtiva] = useState(null);
  const [modalPagamentoPedido, setModalPagamentoPedido] = useState(false);
  const [pedidoSelecionadoPagamento, setPedidoSelecionadoPagamento] = useState(null);
  const [formaPagamentoPedido, setFormaPagamentoPedido] = useState('dinheiro');
  const [modalClienteRetirada, setModalClienteRetirada] = useState(false);
  const [enderecoRetirada, setEnderecoRetirada] = useState({
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: 'Cachoeiro de Itapemirim',
    estado: 'ES',
    cep: '',
    referencia: ''
  });
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const [modalTrocoProducao, setModalTrocoProducao] = useState(false);
  const [pagamentoProducao, setPagamentoProducao] = useState({ tipo: 'dinheiro', valorRecebido: 0, troco: 0 });
  const [pagamentosSequenciais, setPagamentosSequenciais] = useState([]); // ✅ NOVO: Array de pagamentos múltiplos (produção/delivery)
  const [novoPagamento, setNovoPagamento] = useState({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 }); // ✅ NOVO: Pagamento sendo adicionado (produção/delivery)
  const [pagamentosSequenciaisVenda, setPagamentosSequenciaisVenda] = useState([]); // ✅ NOVO: Array de pagamentos múltiplos (comandas/mesas)
  const [novoPagamentoVenda, setNovoPagamentoVenda] = useState({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 }); // ✅ NOVO: Pagamento sendo adicionado (comandas/mesas)
  const [modoDividirPagamento, setModoDividirPagamento] = useState(false); // ✅ NOVO: Flag para mostrar campo de dividir pagamento (produção)
  const [modoDividirPagamentoVenda, setModoDividirPagamentoVenda] = useState(false); // ✅ NOVO: Flag para mostrar campo de dividir pagamento (comandas)
  const [modalSolicitacao, setModalSolicitacao] = useState(false);
  const [comandaParaSolicitar, setComandaParaSolicitar] = useState(null);
  const [tipoSolicitacao, setTipoSolicitacao] = useState(null); // 'cancelar' ou 'editar'
  const [motivoSolicitacao, setMotivoSolicitacao] = useState('');
  const [modalSolicitacaoRemoverItem, setModalSolicitacaoRemoverItem] = useState(false);
  const [itemParaRemover, setItemParaRemover] = useState(null);
  const [motivoRemocaoItem, setMotivoRemocaoItem] = useState('');
  const [modalDelivery, setModalDelivery] = useState(false);
  const [aceitaPedidos, setAceitaPedidos] = useState(true);
  const [taxaEntregaAtiva, setTaxaEntregaAtiva] = useState(true);
  const [pedidoBusca, setPedidoBusca] = useState('');
  const [pedidoView, setPedidoView] = useState('grid'); // 'grid' | 'list' | 'compact'
  const [pedidoFiltroStatus, setPedidoFiltroStatus] = useState('todos');
  const [pedidoPeriodo, setPedidoPeriodo] = useState('sessao'); // 'sessao' | 'hoje' | '7dias' | 'custom'
  const [pedidoDataInicio, setPedidoDataInicio] = useState('');
  const [pedidoDataFim, setPedidoDataFim] = useState('');
  const [carneExtraForm, setCarneExtraForm] = useState({ preco: 0, disponivel: true });
  const [novaCarneDoDia, setNovaCarneDoDia] = useState({ nome: '', ordem: 0, disponivel: true });
  const [carneDoDiaEditando, setCarneDoDiaEditando] = useState(null);
  const [marmitexProdutoSelecionadoId, setMarmitexProdutoSelecionadoId] = useState('');
  const [marmitexGuarnicoesDraft, setMarmitexGuarnicoesDraft] = useState([]);
  const queryClient = useQueryClient();

  const getPedidosQueryKey = () => [
    'pedidos-caixa',
    estabelecimento?._id,
    pedidoPeriodo,
    pedidoDataInicio,
    pedidoDataFim,
    caixaData?._id
  ];

  // Buscar estabelecimento primeiro
  const { data: estabelecimento } = useQuery(
    'estabelecimento-caixa-fullscreen',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        const est = response.data.data.estabelecimento;
        setAceitaPedidos(est?.configuracoes?.aceitaPedidos !== false);
        setTaxaEntregaAtiva(est?.configuracoes?.taxaEntregaAtiva !== false);
        return est;
      } catch (error) {
        if (error.response?.status === 404) {
          try {
            const response = await api.get('/api/estabelecimentos/padrao');
            const est = response.data.data.estabelecimento;
            setAceitaPedidos(est?.configuracoes?.aceitaPedidos !== false);
            setTaxaEntregaAtiva(est?.configuracoes?.taxaEntregaAtiva !== false);
            return est;
          } catch (padraoError) {
            console.error('Erro ao buscar estabelecimento padrão:', padraoError);
            return null;
          }
        }
        throw error;
      }
    },
    {
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  const toggleTaxaEntregaMutation = useMutation(
    async (novoValor) => {
      return api.patch(`/api/estabelecimentos/${estabelecimento?._id}/taxa-entrega`, {
        ativa: novoValor
      });
    },
    {
      onSuccess: (_, novoValor) => {
        setTaxaEntregaAtiva(novoValor);
        toast.success(novoValor ? '✅ Taxa de entrega ATIVADA' : '🎁 Entrega grátis (taxa DESATIVADA)');
        queryClient.invalidateQueries('estabelecimento-caixa-fullscreen');
        queryClient.invalidateQueries('estabelecimento-padrao');
        queryClient.invalidateQueries('estabelecimento-caixa');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar taxa de entrega');
      }
    }
  );

  const atualizarPedidoNoCache = (pedidoId, patch) => {
    queryClient.setQueriesData({ queryKey: ['pedidos-caixa', estabelecimento?._id] }, (old) => {
      const lista = Array.isArray(old) ? old : [];
      return lista.map((p) => (p?._id === pedidoId ? { ...p, ...patch } : p));
    });
  };

  const handleAtualizarStatusPedido = async (pedidoId, novoStatus) => {
    await api.patch(`/api/pedidos/${pedidoId}/status`, { status: novoStatus });
    atualizarPedidoNoCache(pedidoId, { status: novoStatus });
    try {
      if (typeof refetchPedidos === 'function') {
        await refetchPedidos();
      }
    } catch (e) {}
  };

  const toggleAceitaPedidosMutation = useMutation(
    async (novoValor) => {
      return api.patch(`/api/estabelecimentos/${estabelecimento?._id}/aceita-pedidos`, {
        aceitaPedidos: novoValor
      });
    },
    {
      onSuccess: (_, novoValor) => {
        setAceitaPedidos(novoValor);
        toast.success(novoValor ? '✅ Pedidos online ABERTOS' : '🚫 Pedidos online FECHADOS');
        queryClient.invalidateQueries('estabelecimento-caixa-fullscreen');
        queryClient.invalidateQueries('estabelecimento-padrao');
        queryClient.invalidateQueries('estabelecimento-caixa');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar status');
      }
    }
  );

  const { data: produtos, isLoading: loadingProdutos, error: produtosError } = useQuery(
    ['produtos-caixa-fullscreen', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const params = new URLSearchParams();
      params.append('estabelecimento', estabelecimento._id);
      params.append('disponivel', 'true');
      const response = await api.get(`/api/produtos?${params}`);
      const produtosList = response.data.data.produtos || [];
      return produtosList;
    },
    {
      enabled: !!estabelecimento?._id,
      retry: 2
    }
  );

  // Buscar adicionais globais
  const { data: adicionaisGlobais } = useQuery(
    ['adicionais-globais-caixa', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const response = await api.get(`/api/adicionais?estabelecimento=${estabelecimento._id}&disponivel=true`);
      return response.data.data.adicionais || [];
    },
    {
      enabled: !!estabelecimento?._id,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const { data: adicionaisMarmitexAdmin } = useQuery(
    ['adicionais-marmitex-admin', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const response = await api.get(`/api/adicionais?estabelecimento=${estabelecimento._id}`);
      return response.data.data.adicionais || [];
    },
    {
      enabled: !!estabelecimento?._id,
      staleTime: 30 * 1000,
      cacheTime: 5 * 60 * 1000
    }
  );

  const { data: carneExtraAdmin } = useQuery(
    ['carne-extra-admin', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return null;
      const response = await api.get(`/api/adicionais/carne-extra?estabelecimento=${estabelecimento._id}`);
      return response.data.data?.adicional || null;
    },
    {
      enabled: !!estabelecimento?._id,
      retry: 1
    }
  );

  useEffect(() => {
    if (carneExtraAdmin) {
      setCarneExtraForm({
        preco: Number(carneExtraAdmin.preco) || 0,
        disponivel: carneExtraAdmin.disponivel !== false
      });
    }
  }, [carneExtraAdmin]);

  const { data: produtosMarmitexAdmin } = useQuery(
    ['produtos-marmitex-admin', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const params = new URLSearchParams();
      params.append('estabelecimento', estabelecimento._id);
      params.append('categoria', 'MARMITEX');
      const response = await api.get(`/api/produtos?${params}`);
      return response.data.data.produtos || [];
    },
    {
      enabled: !!estabelecimento?._id,
      retry: 1
    }
  );

  const carnesDoDiaAdmin = useMemo(() => {
    const arr = Array.isArray(adicionaisMarmitexAdmin) ? adicionaisMarmitexAdmin : [];
    return arr
      .filter((a) => a && normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNES_DO_DIA))
      .sort((a, b) => (Number(a?.ordem) || 0) - (Number(b?.ordem) || 0) || String(a?.nome || '').localeCompare(String(b?.nome || '')));
  }, [adicionaisMarmitexAdmin]);

  const salvarCarneExtraMutation = useMutation(
    async (payload) => api.put('/api/adicionais/carne-extra', payload),
    {
      onSuccess: () => {
        toast.success('Carne extra atualizada!');
        queryClient.invalidateQueries(['carne-extra-admin', estabelecimento?._id]);
        queryClient.invalidateQueries(['adicionais-marmitex-admin', estabelecimento?._id]);
        queryClient.invalidateQueries(['adicionais-globais-caixa', estabelecimento?._id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar carne extra');
      }
    }
  );

  const criarCarneDoDiaMutation = useMutation(
    async (payload) => api.post('/api/adicionais', payload),
    {
      onSuccess: () => {
        toast.success('Carne do dia criada!');
        setNovaCarneDoDia({ nome: '', ordem: 0, disponivel: true });
        queryClient.invalidateQueries(['adicionais-marmitex-admin', estabelecimento?._id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao criar carne do dia');
      }
    }
  );

  const atualizarCarneDoDiaMutation = useMutation(
    async ({ id, payload }) => api.put(`/api/adicionais/${id}`, payload),
    {
      onSuccess: () => {
        toast.success('Carne do dia atualizada!');
        setCarneDoDiaEditando(null);
        queryClient.invalidateQueries(['adicionais-marmitex-admin', estabelecimento?._id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar carne do dia');
      }
    }
  );

  const deletarCarneDoDiaMutation = useMutation(
    async (id) => api.delete(`/api/adicionais/${id}`),
    {
      onSuccess: () => {
        toast.success('Carne do dia removida!');
        queryClient.invalidateQueries(['adicionais-marmitex-admin', estabelecimento?._id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao remover carne do dia');
      }
    }
  );

  const salvarGuarnicoesProdutoMutation = useMutation(
    async ({ produtoId, adicionais }) => api.put(`/api/produtos/${produtoId}`, { adicionais }),
    {
      onSuccess: () => {
        toast.success('Guarnições salvas!');
        queryClient.invalidateQueries(['produtos-marmitex-admin', estabelecimento?._id]);
        queryClient.invalidateQueries(['produtos-caixa-fullscreen', estabelecimento?._id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao salvar guarnições');
      }
    }
  );

  useEffect(() => {
    const lista = Array.isArray(produtosMarmitexAdmin) ? produtosMarmitexAdmin : [];
    const atual = marmitexProdutoSelecionadoId
      ? lista.find((p) => String(p?._id) === String(marmitexProdutoSelecionadoId))
      : null;

    if (!atual && lista.length > 0) {
      setMarmitexProdutoSelecionadoId(String(lista[0]._id));
      const adicionais = Array.isArray(lista[0]?.adicionais) ? lista[0].adicionais : [];
      setMarmitexGuarnicoesDraft(adicionais.map((a) => ({ nome: a?.nome || '', preco: Number(a?.preco) || 0, disponivel: a?.disponivel !== false })));
      return;
    }

    if (atual) {
      const adicionais = Array.isArray(atual?.adicionais) ? atual.adicionais : [];
      setMarmitexGuarnicoesDraft(adicionais.map((a) => ({ nome: a?.nome || '', preco: Number(a?.preco) || 0, disponivel: a?.disponivel !== false })));
    }
  }, [produtosMarmitexAdmin, marmitexProdutoSelecionadoId]);

  // Agrupar produtos por categoria
  const produtosPorCategoria = useMemo(() => {
    if (!produtos || produtos.length === 0) return {};
    const agrupados = {};
    produtos.forEach(produto => {
      const categoria = produto.categoria || 'Outros';
      if (!agrupados[categoria]) {
        agrupados[categoria] = [];
      }
      agrupados[categoria].push(produto);
    });
    return agrupados;
  }, [produtos]);

  // Categorias disponíveis
  const categorias = useMemo(() => {
    return Object.keys(produtosPorCategoria).sort();
  }, [produtosPorCategoria]);

  // Resetar estado quando fechar modal
  useEffect(() => {
    if (!modalAdicionarItem) {
      setCategoriaAtiva(null);
      setProdutoSelecionadoModal(null);
      setNovoItem({ produto: null, variacao: null, quantidade: 1, clienteItem: '', observacoes: '', acrescimos: [], marmitexSemCarne: false, marmitexPermitirExtra: false });
    }
  }, [modalAdicionarItem]);

  const { data: caixaData, refetch: refetchCaixa } = useQuery(
    'caixa-atual-fullscreen',
    async () => {
      if (!estabelecimento?._id) return null;
      try {
        const response = await api.get(`/api/caixa/atual?estabelecimento=${estabelecimento._id}`);
        const caixa = response.data.data?.caixa || null;
        if (caixa) {
          console.log('💰 Caixa carregado:', {
            _id: caixa._id,
            valorAbertura: caixa.valorAbertura,
            totalVendas: caixa.vendas?.length || 0,
            vendas: caixa.vendas?.map(v => ({
              numero: v.numero,
              total: v.total,
              status: v.status,
              tipoVenda: v.tipoVenda
            })) || []
          });
        }
        return caixa;
      } catch (error) {
        if (error.response?.status === 404 || error.response?.status === 400) {
          return null;
        }
        throw error;
      }
    },
    {
      enabled: !!estabelecimento?._id,
      refetchInterval: 2000, // Atualizar a cada 2 segundos
      retry: false,
      staleTime: 0, // Sempre considerar stale
      cacheTime: 0 // Não cachear
    }
  );

  useEffect(() => {
    if (caixaData) {
      setCaixaAberto(caixaData);
    } else {
      setCaixaAberto(null);
    }
  }, [caixaData]);

  // ✅ Atualizar cidade/estado do endereço quando estabelecimento mudar
  useEffect(() => {
    if (estabelecimento?.endereco) {
      setEnderecoEntrega(prev => ({
        ...prev,
        cidade: estabelecimento.endereco.cidade || prev.cidade,
        estado: estabelecimento.endereco.estado || prev.estado
      }));
    }
  }, [estabelecimento]);

  const abrirCaixaMutation = useMutation(
    (data) => api.post('/api/caixa/abrir', data),
    {
      onSuccess: () => {
        toast.success('Caixa aberto!');
        setModalAbertura(false);
        setValorAbertura('');
        setModalFechamento(false);
        setRelatorioFechamento(null);
        refetchCaixa();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao abrir caixa');
      }
    }
  );

  const fecharCaixaMutation = useMutation(
    ({ id, data }) => api.post(`/api/caixa/${id}/fechar`, data),
    {
      onSuccess: (response) => {
        toast.success('Caixa fechado!');
        setRelatorioFechamento(response.data?.data?.relatorio || null);
        setModalFechamento(true);
        refetchCaixa();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao fechar caixa');
      }
    }
  );

  const criarVendaMutation = useMutation(
    (data) => api.post('/api/caixa/venda', data),
    {
      onSuccess: () => {
        toast.success('Venda realizada!');
        setCarrinho([]);
        setCliente({ nome: '', cpf: '', telefone: '' });
        setFormaPagamento({ tipo: 'dinheiro', valor: 0, troco: 0 });
        const data = new Date();
        data.setDate(data.getDate() + 30);
        setDataVencimentoFiado(data.toISOString().split('T')[0]);
        setModalVenda(false);
        // Forçar atualização imediata do caixa (múltiplas tentativas)
        setTimeout(() => {
          refetchCaixa();
        }, 300);
        setTimeout(() => {
          refetchCaixa();
        }, 1000);
        setTimeout(() => {
          refetchCaixa();
        }, 2000);
      },
      onError: (error) => {
        console.error('❌ Erro ao criar venda:', error);
        console.error('Response:', error.response?.data);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Erro ao criar venda';
        toast.error(errorMessage);
      }
    }
  );

  // ✅ Mutation para criar pedido de delivery
  const criarPedidoDeliveryMutation = useMutation(
    async (data) => {
      try {
        console.log('🔄 Criando pedido de delivery...', data);
        const response = await api.post('/api/pedidos', {
          ...data,
          status: 'Confirmado' // Pedido do caixa já vem confirmado
        });
        console.log('✅ Pedido de delivery criado:', response.data);
        return response.data.data.pedido;
      } catch (error) {
        console.error('❌ Erro ao criar pedido de delivery:', error);
        throw error;
      }
    },
    {
      onSuccess: (pedido) => {
        console.log('✅ Pedido de delivery criado com sucesso!', pedido);
        toast.success('Pedido de delivery criado e enviado para produção!');
        setCarrinho([]);
        setCliente({ nome: '', cpf: '', telefone: '' });
        setTipoEntrega('retirada');
        setEnderecoEntrega({
          rua: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: 'Cachoeiro de Itapemirim',
          estado: 'ES',
          cep: '',
          referencia: ''
        });
        refetchPedidos();
        refetchCaixa();
      },
      onError: (error) => {
        console.error('❌ Erro ao criar pedido de delivery:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Erro ao criar pedido';
        toast.error(`Erro: ${errorMessage}`, { duration: 5000 });
      }
    }
  );

  // ✅ Mutation para criar comanda e enviar para produção
  const criarComandaBalcaoMutation = useMutation(
    async (data) => {
      try {
        console.log('🔄 Iniciando criação de comanda...', data);
        
        // 1. Criar comanda aberta
        console.log('📝 Criando comanda...', {
          mesa: 'BALCÃO',
          cliente: data.cliente.nome,
          telefone: data.cliente.telefone,
          estabelecimento: data.estabelecimento
        });
        // Log do usuário antes de fazer a requisição
        console.log('👤 Usuário logado:', {
          tipo: user?.tipo,
          nome: user?.nome,
          email: user?.email
        });
        
        const comandaResponse = await api.post('/api/comandas', {
          mesa: 'BALCÃO',
          cliente: data.cliente?.nome || data.cliente || '',
          telefone: data.cliente?.telefone || data.telefone || '',
          estabelecimento: data.estabelecimento,
          formaPagamento: data.formaPagamento,
          valorRecebido: data.valorRecebido,
          troco: data.troco,
          pagamentos: data.pagamentos
        });
        const comanda = comandaResponse.data.data.comanda;
        console.log('✅ Comanda criada:', comanda._id);
        
        // 2. Adicionar todos os itens à comanda
        console.log('📦 Adicionando itens...', data.itens.length);
        for (let i = 0; i < data.itens.length; i++) {
          const item = data.itens[i];
          console.log(`  Adicionando item ${i + 1}/${data.itens.length}:`, item.produto);
          try {
            await api.put(`/api/comandas/${comanda._id}/itens`, {
              produto: item.produto,
              quantidade: item.quantidade,
              clienteItem: item.clienteItem || '',
              observacoes: item.observacoes || '',
              acrescimos: item.acrescimos || [],
              variacao: item.variacao || null
            });
            console.log(`  ✅ Item ${i + 1} adicionado`);
          } catch (itemError) {
            console.error(`  ❌ Erro ao adicionar item ${i + 1}:`, itemError);
            // Continuar com os próximos itens
          }
        }
        
        // 3. Enviar comanda para produção (cria pedido e imprime)
        console.log('🚀 Enviando para produção...');
        console.log('🚀 Comanda ID:', comanda._id);
        let comandoImpressao = null;
        try {
          const producaoResponse = await api.post(`/api/comandas/${comanda._id}/enviar-producao`, {}, {
            timeout: 60000 // 60 segundos para operação de impressão
          });
          console.log('✅ Enviado para produção:', producaoResponse.data);
        } catch (producaoError) {
          console.error('❌ Erro ao enviar para produção:', producaoError);
          // Continuar mesmo se falhar (pode ser apenas timeout)
          if (producaoError.code === 'ECONNABORTED') {
            console.warn('⚠️ Timeout ao enviar para produção, mas comanda foi criada');
            // Aguardar um pouco e verificar se comando foi salvo
            console.log('⏳ Aguardando 2 segundos e verificando comando de impressão...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            try {
              const response = await api.get('/api/agente/comandos.json');
              const dados = response.data;
              const comandos = dados.comandos || [];
              // Buscar comando mais recente para esta comanda
              const comandoRecente = comandos
                .filter(cmd => cmd.numero === comanda.numero || cmd.pedidoNumero === comanda.numero)
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
              
              if (comandoRecente) {
                console.log('✅ Comando de impressão encontrado após timeout!', comandoRecente.numero);
                comandoImpressao = comandoRecente;
              }
            } catch (pollError) {
              console.warn('⚠️ Erro ao verificar comandos após timeout:', pollError);
            }
          }
        }
        
        // Se encontrou comando, imprimir imediatamente
        if (comandoImpressao) {
          console.log('🖨️ Imprimindo comando encontrado:', comandoImpressao.numero);
          if (!__agenteOnlineRef.current) {
            imprimirComandoDireto(comandoImpressao);
          }
        }
        
        return comanda;
      } catch (error) {
        console.error('❌ ERRO COMPLETO:', error);
        console.error('Response:', error.response?.data);
        console.error('Status:', error.response?.status);
        throw error;
      }
    },
    {
      onSuccess: (comanda) => {
        console.log('✅ Sucesso! Comanda criada e enviada:', comanda);
        toast.success('Comanda criada e enviada para produção!');
        setCarrinho([]);
        setCliente({ nome: '', cpf: '', telefone: '' });
        refetchPedidos();
        refetchComandasAbertas();
        refetchCaixa();
      },
      onError: (error) => {
        console.error('❌ Erro ao criar comanda:', error);
        console.error('Response data:', error.response?.data);
        console.error('Response status:', error.response?.status);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Erro ao criar comanda';
        
        // Se for erro 403, mostrar mensagem mais detalhada
        if (error.response?.status === 403) {
          toast.error(`Acesso negado: ${errorMessage}. Verifique se você está logado como operador de caixa.`, { duration: 8000 });
        } else {
          toast.error(`Erro: ${errorMessage}`, { duration: 5000 });
        }
      }
    }
  );

  const criarComandaProducaoMutation = criarComandaBalcaoMutation;

  const handleEnviarProducao = () => {
    if (carrinho.length === 0) {
      toast.error('Adicione itens ao carrinho primeiro!');
      return;
    }

    if (formaPagamento.tipo === 'fiado' && (!cliente.nome || !cliente.telefone)) {
      toast.error('Informe o nome e telefone do cliente!');
      return;
    }

    if (!estabelecimento?._id) {
      toast.error('Estabelecimento não encontrado!');
      return;
    }

    // ✅ VALIDAÇÃO: Se for retirada, abrir modal para buscar cliente/inserir endereço
    if (tipoEntrega === 'retirada') {
      setModalClienteRetirada(true);
      return;
    }

    // ✅ VALIDAÇÃO: Se for delivery, validar endereço (CEP não obrigatório)
    if (tipoEntrega === 'delivery') {
      if (!enderecoEntrega.rua || !enderecoEntrega.numero || !enderecoEntrega.bairro) {
        toast.error('Preencha rua, número e bairro do endereço de entrega!');
        return;
      }
    }

    // ✅ ENVIAR DIRETO PARA PRODUÇÃO SEM MODAL DE PAGAMENTO
    // Preparar itens da comanda/pedido
    const itens = carrinho.map(item => ({
      produto: item.produto._id,
      quantidade: item.quantidade,
      precoUnitario: item.precoUnitario,
      subtotal: item.subtotal || (item.precoUnitario * item.quantidade),
      clienteItem: item.clienteItem || '',
      observacoes: item.observacoes || '',
      acrescimos: item.acrescimos || [],
      variacao: item.variacao || null
    }));

    // ✅ Se for delivery, criar PEDIDO ao invés de comanda
    if (tipoEntrega === 'delivery') {
      // Calcular totais usando o carrinho (que tem os valores corretos)
      const subtotal = carrinho.reduce((acc, item) => {
        const itemTotal = item.subtotal || (item.precoUnitario * item.quantidade);
        return acc + (itemTotal - (item.desconto || 0));
      }, 0);
      const taxaEntrega = calcularTaxaEntrega();
      const total = subtotal + taxaEntrega;
      
      console.log('📤 Dados do pedido delivery sendo enviados:');
      console.log('   Subtotal:', subtotal);
      console.log('   Taxa de entrega:', taxaEntrega);
      console.log('   Total:', total);
      
      const dadosPedido = {
        estabelecimento: estabelecimento._id || estabelecimento,
        cliente: {
          nome: cliente.nome,
          telefone: cliente.telefone,
          whatsapp: cliente.telefone,
          email: ''
        },
        itens: itens,
        enderecoEntrega: {
          rua: enderecoEntrega.rua || '',
          numero: enderecoEntrega.numero || '',
          complemento: enderecoEntrega.complemento || '',
          bairro: enderecoEntrega.bairro || '',
          cidade: enderecoEntrega.cidade || 'Cachoeiro de Itapemirim',
          estado: enderecoEntrega.estado || 'ES',
          cep: enderecoEntrega.cep || '',
          referencia: enderecoEntrega.referencia || ''
        },
        tipoEntrega: 'delivery',
        origem: 'caixa',
        subtotal: subtotal,
        taxaEntrega: taxaEntrega,
        total: total
      };
      
      criarPedidoDeliveryMutation.mutate(dadosPedido);
    } else {
      // Se for retirada, criar comanda normalmente (com endereço se fornecido)
      const dadosComanda = {
        estabelecimento: estabelecimento._id || estabelecimento,
        cliente: {
          nome: cliente.nome,
          telefone: cliente.telefone,
          enderecoRetirada: (enderecoRetirada.rua && enderecoRetirada.numero) ? enderecoRetirada : null
        },
        itens: itens,
        enderecoRetirada: (enderecoRetirada.rua && enderecoRetirada.numero) ? enderecoRetirada : null
      };

      console.log('📋 Dados da comanda:', dadosComanda);
      criarComandaProducaoMutation.mutate(dadosComanda);
      setModalClienteRetirada(false);
    }
  };

  // ✅ NOVO: Função para adicionar pagamento sequencial
  const adicionarPagamentoSequencial = () => {
    const total = calcularTotal();
    const valorPago = pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0);
    const valorRestante = total - valorPago;
    
    if (!novoPagamento.tipo || !novoPagamento.valor || novoPagamento.valor <= 0) {
      toast.error('Informe o tipo e valor do pagamento!');
      return;
    }
    
    if (novoPagamento.valor > valorRestante) {
      toast.error(`Valor excede o restante: R$ ${valorRestante.toFixed(2)}`);
      return;
    }
    
    // Calcular troco se for dinheiro
    let troco = 0;
    if (novoPagamento.tipo === 'dinheiro' && novoPagamento.valorRecebido > novoPagamento.valor) {
      troco = novoPagamento.valorRecebido - novoPagamento.valor;
    }
    
    setPagamentosSequenciais([...pagamentosSequenciais, {
      ...novoPagamento,
      troco: troco
    }]);
    
    // Resetar novo pagamento
    setNovoPagamento({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
  };

  // ✅ NOVO: Função para remover pagamento sequencial
  const removerPagamentoSequencial = (index) => {
    setPagamentosSequenciais(pagamentosSequenciais.filter((_, i) => i !== index));
  };

  const handleConfirmarTrocoProducao = () => {
    const total = calcularTotal();
    
    // ✅ Verificar se há pagamentos sequenciais
    const valorPagoSequenciais = pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0);
    
    // Se não houver pagamentos sequenciais, usar formato antigo (compatibilidade)
    if (pagamentosSequenciais.length === 0) {
      // Validar pagamento único
      if (pagamentoProducao.tipo === 'dinheiro' && pagamentoProducao.valorRecebido < total) {
        toast.error('Valor recebido insuficiente!');
        return;
      }
    } else {
      // Validar pagamentos múltiplos
      if (valorPagoSequenciais < total) {
        toast.error(`Pagamento incompleto. Faltam R$ ${(total - valorPagoSequenciais).toFixed(2)}`);
        return;
      }
    }
    
    // Preparar itens da comanda/pedido
    const itens = carrinho.map(item => ({
      produto: item.produto._id,
      quantidade: item.quantidade,
      precoUnitario: item.precoUnitario,
      subtotal: item.subtotal || (item.precoUnitario * item.quantidade),
      clienteItem: item.clienteItem || '',
      observacoes: item.observacoes || '',
      acrescimos: item.acrescimos || [],
      variacao: item.variacao || null
    }));

    // ✅ Se for delivery, criar PEDIDO ao invés de comanda
    if (tipoEntrega === 'delivery') {
      // Calcular totais (usar carrinho, pois tem preços/subtotais corretos)
      const subtotal = carrinho.reduce((acc, item) => {
        const itemTotal = item.subtotal || (item.precoUnitario * item.quantidade);
        return acc + (itemTotal - (item.desconto || 0));
      }, 0);
      const taxaEntrega = calcularTaxaEntrega();
      const total = subtotal + taxaEntrega;
      
      console.log('📤 Dados do pedido delivery sendo enviados:');
      console.log('   Subtotal:', subtotal);
      console.log('   Taxa de entrega:', taxaEntrega);
      console.log('   Total:', total);
      
      const dadosPedido = {
        estabelecimento: estabelecimento._id || estabelecimento,
        cliente: {
          nome: cliente.nome,
          telefone: cliente.telefone,
          whatsapp: cliente.telefone,
          email: ''
        },
        itens: itens,
        enderecoEntrega: enderecoEntrega,
        tipoEntrega: 'delivery',
        origem: 'caixa',
        subtotal: subtotal,
        taxaEntrega: taxaEntrega, // ✅ ENVIAR TAXA EXPLICITAMENTE
        total: total // ✅ TOTAL JÁ INCLUI A TAXA
      };
      
      // ✅ Adicionar pagamentos múltiplos ou formato antigo
      if (pagamentosSequenciais.length > 0) {
        dadosPedido.pagamentos = pagamentosSequenciais;
      } else {
        dadosPedido.formaPagamento = pagamentoProducao.tipo;
        dadosPedido.valorRecebido = pagamentoProducao.valorRecebido;
        dadosPedido.troco = pagamentoProducao.troco;
      }
      
      criarPedidoDeliveryMutation.mutate(dadosPedido);
    } else {
      // Se for retirada, criar comanda normalmente (com endereço se fornecido)
      const dadosComanda = {
        estabelecimento: estabelecimento._id || estabelecimento,
        cliente: {
          nome: cliente.nome,
          telefone: cliente.telefone,
          enderecoRetirada: (enderecoRetirada.rua && enderecoRetirada.numero) ? enderecoRetirada : null
        },
        itens: itens,
        enderecoRetirada: (enderecoRetirada.rua && enderecoRetirada.numero) ? enderecoRetirada : null
      };
      
      // ✅ Adicionar pagamentos múltiplos ou formato antigo
      if (pagamentosSequenciais.length > 0) {
        dadosComanda.pagamentos = pagamentosSequenciais;
      } else {
        dadosComanda.formaPagamento = pagamentoProducao.tipo;
        dadosComanda.valorRecebido = pagamentoProducao.valorRecebido;
        dadosComanda.troco = pagamentoProducao.troco;
      }

      console.log('📋 Dados da comanda:', dadosComanda);
      criarComandaProducaoMutation.mutate(dadosComanda);
      setModalClienteRetirada(false);
    }
    
    setModalTrocoProducao(false);
    setPagamentosSequenciais([]); // ✅ Limpar pagamentos após finalizar
  };

  // ✅ Função para confirmar retirada com dados do cliente/endereço
  const handleConfirmarRetirada = () => {
    // ✅ ENVIAR DIRETO PARA PRODUÇÃO SEM MODAL DE PAGAMENTO
    setModalClienteRetirada(false);
    
    // Preparar itens da comanda
    const itens = carrinho.map(item => ({
      produto: item.produto._id,
      quantidade: item.quantidade,
      clienteItem: item.clienteItem || '',
      observacoes: item.observacoes || '',
      acrescimos: item.acrescimos || [],
      variacao: item.variacao || null
    }));

    // Criar comanda com endereço de retirada se fornecido
    const dadosComanda = {
      estabelecimento: estabelecimento._id || estabelecimento,
      cliente: {
        nome: cliente.nome,
        telefone: cliente.telefone,
        enderecoRetirada: (enderecoRetirada.rua && enderecoRetirada.numero) ? enderecoRetirada : null
      },
      itens: itens,
      enderecoRetirada: (enderecoRetirada.rua && enderecoRetirada.numero) ? enderecoRetirada : null
    };

    console.log('📋 Dados da comanda (retirada):', dadosComanda);
    criarComandaProducaoMutation.mutate(dadosComanda);
  };

  const handleEnviarProducaoDireto = () => {
    if (carrinho.length === 0) {
      toast.error('Adicione itens ao carrinho primeiro!');
      return;
    }

    if (!estabelecimento?._id) {
      toast.error('Estabelecimento não encontrado!');
      return;
    }

    // Preparar itens da comanda
    const itens = carrinho.map(item => ({
      produto: item.produto._id,
      quantidade: item.quantidade,
      clienteItem: item.clienteItem || '',
      observacoes: item.observacoes || '',
      acrescimos: item.acrescimos || [],
      variacao: item.variacao || null
    }));

    // Criar comanda com endereço de retirada se fornecido
    const dadosComanda = {
      estabelecimento: estabelecimento._id || estabelecimento,
      cliente: {
        nome: cliente.nome,
        telefone: cliente.telefone,
        enderecoRetirada: (enderecoRetirada.rua && enderecoRetirada.numero) ? enderecoRetirada : null
      },
      itens: itens
    };

    console.log('📋 Dados da comanda (retirada):', dadosComanda);
    criarComandaProducaoMutation.mutate(dadosComanda);
    setModalClienteRetirada(false);
  };

  const adicionarProduto = (produto) => {
    // ✅ Produto já está selecionado, abrir modal direto para personalizar
    setProdutoSelecionadoModal(produto);
    
    // ✅ Inicializar novoItem com o produto já selecionado
    setNovoItem({
      produto: produto,
      variacao: produto.temVariacoes && Array.isArray(produto.variacoes) ? (produto.variacoes.find(v => v.disponivel !== false) || null) : null,
      quantidade: 1,
      clienteItem: '',
      observacoes: '',
      acrescimos: [],
      marmitexSemCarne: false,
      marmitexPermitirExtra: false
    });
    
    setModalAdicionarItem(true);
  };

  const toggleAcrescimo = (acrescimo) => {
    const index = novoItem.acrescimos.findIndex(a => a.nome === acrescimo.nome);
    const isMarmitex = isProdutoMarmitex(novoItem.produto);
    const nomeAcrescimo = String(acrescimo?.nome || '');
    const isCarne = normalizarTexto(nomeAcrescimo).startsWith(normalizarTexto(PREFIXO_CARNE));
    const adicionando = index < 0;

    if (adicionando && isMarmitex && isCarne) {
      if (novoItem.marmitexSemCarne) return;

      const limite = obterLimiteCarnes(novoItem.produto, novoItem.variacao);
      const totalAtual = contarCarnesSelecionadas(novoItem.acrescimos);
      const novoTotal = totalAtual + 1;

      if (novoTotal > limite && !novoItem.marmitexPermitirExtra) {
        toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para adicionar mais.`);
        return;
      }

      if (novoTotal > limite) {
        const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
          return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
        });
        if (!carneExtraCfg || carneExtraCfg.disponivel === false) {
          toast.error('Carne extra não configurada/indisponível.');
          return;
        }
      }
    }

    if (index >= 0) {
      setNovoItem({
        ...novoItem,
        acrescimos: novoItem.acrescimos.filter((_, i) => i !== index)
      });
    } else {
      setNovoItem({
        ...novoItem,
        acrescimos: [...novoItem.acrescimos, { nome: acrescimo.nome, preco: acrescimo.preco, quantidade: 1 }]
      });
    }
  };

  const atualizarQuantidadeAcrescimo = (nomeAcrescimo, novaQuantidade) => {
    const qtd = Math.min(20, Math.max(1, Number(novaQuantidade || 1)));

    const isMarmitex = isProdutoMarmitex(novoItem.produto);
    const isCarne = normalizarTexto(nomeAcrescimo).startsWith(normalizarTexto(PREFIXO_CARNE));

    if (isMarmitex && isCarne && !novoItem.marmitexSemCarne) {
      const limite = obterLimiteCarnes(novoItem.produto, novoItem.variacao);
      const selecionada = (novoItem.acrescimos || []).find((a) => a?.nome === nomeAcrescimo);
      const qtdAtual = Math.min(20, Math.max(1, Number(selecionada?.quantidade || 1)));

      const totalCarnesAgora = contarCarnesSelecionadas(novoItem.acrescimos);
      const totalSemEste = totalCarnesAgora - qtdAtual;
      const novoTotal = totalSemEste + qtd;

      if (novoTotal > limite && !novoItem.marmitexPermitirExtra) {
        toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para aumentar a quantidade.`);
        return;
      }

      if (novoTotal > limite) {
        const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
          return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
        });
        if (!carneExtraCfg || carneExtraCfg.disponivel === false) {
          toast.error('Carne extra não configurada/indisponível.');
          return;
        }
      }
    }

    setNovoItem(prev => ({
      ...prev,
      acrescimos: (prev.acrescimos || []).map(a => (a.nome === nomeAcrescimo ? { ...a, quantidade: qtd } : a))
    }));
  };

  const handleAdicionarItemCarrinho = () => {
    if (!novoItem.produto) {
      toast.error('Produto não selecionado!');
      return;
    }

    // Nome de quem está pedindo é opcional (obrigatório apenas para crediário no fechamento)

    const isMarmitex = isProdutoMarmitex(novoItem.produto);

    // ✅ Garantir que sempre tenha um preço válido (número)
    const precoBase = (novoItem.variacao?.preco ?? novoItem.produto.precoPromocional ?? novoItem.produto.preco ?? 0);
    const precoUnitario = typeof precoBase === 'number' ? precoBase : parseFloat(precoBase) || 0;
    
    // ✅ Alertar se o produto não tem preço
    if (precoUnitario <= 0) {
      toast.error(`⚠️ O produto "${novoItem.produto.nome}" não possui preço definido. Defina um preço no cardápio antes de vender.`, {
        duration: 5000
      });
      return;
    }
    
    const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
      return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
    });

    const acrescimosOriginais = Array.isArray(novoItem.acrescimos) ? novoItem.acrescimos : [];
    const acrescimosSemCarneExtra = acrescimosOriginais.filter((a) => normalizarTexto(a?.nome) !== normalizarTexto(NOME_CARNE_EXTRA));
    let acrescimosFinal = acrescimosSemCarneExtra;

    if (isMarmitex) {
      const limite = obterLimiteCarnes(novoItem.produto, novoItem.variacao);
      const totalCarnes = novoItem.marmitexSemCarne ? 0 : contarCarnesSelecionadas(acrescimosSemCarneExtra);
      const excedente = Math.max(0, totalCarnes - limite);

      if (excedente > 0 && !novoItem.marmitexPermitirExtra) {
        toast.error(`Limite de carnes: ${limite}. Marque a opção de carne extra para adicionar mais.`);
        return;
      }

      if (excedente > 0 && (!carneExtraCfg || carneExtraCfg.disponivel === false)) {
        toast.error('Carne extra não configurada/indisponível. Ative e defina o preço antes de adicionar carnes acima do limite.');
        return;
      }

      if (novoItem.marmitexSemCarne) {
        acrescimosFinal = acrescimosFinal.filter((a) => !normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)));
      }

      if (excedente > 0 && carneExtraCfg && carneExtraCfg.disponivel !== false) {
        acrescimosFinal = [
          ...acrescimosFinal,
          { nome: NOME_CARNE_EXTRA, preco: Number(carneExtraCfg.preco) || 0, quantidade: excedente }
        ];
      }
    }

    const precoAcrescimos = acrescimosFinal.reduce((acc, a) => {
      const qtd = Math.min(20, Math.max(1, Number(a?.quantidade || 1)));
      return acc + ((a.preco || 0) * qtd);
    }, 0);
    const subtotalItem = (precoUnitario + precoAcrescimos) * novoItem.quantidade;

    // ✅ Salvar nome do produto antes de limpar o estado
    const nomeProduto = novoItem.produto.nome;
    
    const itemCarrinho = {
      produto: novoItem.produto,
      variacao: novoItem.variacao || null,
      quantidade: novoItem.quantidade,
      precoUnitario: precoUnitario + precoAcrescimos,
      subtotal: subtotalItem,
      clienteItem: novoItem.clienteItem.trim(), // ✅ Garantir que está sem espaços
      observacoes: novoItem.observacoes || '',
      acrescimos: acrescimosFinal,
      desconto: 0
    };

    setCarrinho([...carrinho, itemCarrinho]);
    setModalAdicionarItem(false);
    setProdutoSelecionadoModal(null);
    setNovoItem({ produto: null, variacao: null, quantidade: 1, clienteItem: '', observacoes: '', acrescimos: [], marmitexSemCarne: false, marmitexPermitirExtra: false });
    setCategoriaAtiva(null);
    toast.success(`${itemCarrinho.quantidade}x ${nomeProduto} adicionado ao carrinho!`);
  };

  // Calcular taxa de entrega baseada no bairro
  const calcularTaxaEntrega = () => {
    console.log('🔄 Calculando taxa - tipoEntrega:', tipoEntrega, 'bairro:', enderecoEntrega.bairro);
    
    if (tipoEntrega !== 'delivery') {
      console.log('❌ Não é delivery, taxa = 0');
      return 0;
    }

    if (!enderecoEntrega.bairro || !enderecoEntrega.bairro.trim()) {
      console.log('⚠️ Bairro não informado');
      return 0;
    }

    if (!estabelecimento?.configuracoes) {
      console.log('❌ Estabelecimento sem configurações');
      return 0;
    }

    const taxaEntregaAtivaCfg = estabelecimento?.configuracoes?.taxaEntregaAtiva !== false;
    if (!taxaEntregaAtivaCfg) {
      console.log('🎁 Taxa desativada (Entrega grátis)');
      return 0;
    }

    const taxasPorBairro = estabelecimento.configuracoes.taxasPorBairro || [];
    const taxaPadrao = Number(estabelecimento.configuracoes.taxaEntrega) || 0;

    console.log('📊 Total de bairros cadastrados:', taxasPorBairro.length);
    console.log('💰 Taxa padrão:', taxaPadrao);

    if (taxasPorBairro.length === 0) {
      console.log('⚠️ Nenhum bairro cadastrado, usando taxa padrão:', taxaPadrao);
      return taxaPadrao;
    }

    const bairroNormalizado = enderecoEntrega.bairro.trim().toUpperCase();
    console.log('🔍 Buscando taxa para bairro:', bairroNormalizado);
    
    // Buscar exata primeiro
    let taxaBairro = taxasPorBairro.find(
      t => t.bairro && t.bairro.trim().toUpperCase() === bairroNormalizado
    );

    // Se não encontrar exata, buscar parcial (sem acentos)
    if (!taxaBairro) {
      const normalizarBairro = (bairro) => {
        if (!bairro) return '';
        return bairro.trim().toUpperCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
      };

      const bairroBuscaNormalizado = normalizarBairro(enderecoEntrega.bairro);
      taxaBairro = taxasPorBairro.find(t => {
        if (!t.bairro) return false;
        const bairroTaxaNormalizado = normalizarBairro(t.bairro);
        return bairroTaxaNormalizado === bairroBuscaNormalizado ||
               bairroTaxaNormalizado.includes(bairroBuscaNormalizado) ||
               bairroBuscaNormalizado.includes(bairroTaxaNormalizado);
      });
    }

    if (taxaBairro) {
      const taxa = Number(taxaBairro.taxa) || 0;
      console.log('✅ Taxa encontrada para bairro:', enderecoEntrega.bairro, 'Taxa:', taxa);
      return taxa;
    }

    console.log('⚠️ Bairro não encontrado, usando taxa padrão:', taxaPadrao);
    return taxaPadrao;
  };

  const calcularTotal = () => {
    const subtotal = carrinho.reduce((acc, item) => {
      // ✅ Usar subtotal se disponível (já inclui acréscimos), senão calcular
      const itemTotal = item.subtotal || (item.precoUnitario * item.quantidade);
      return acc + (itemTotal - (item.desconto || 0));
    }, 0);
    const taxaEntrega = calcularTaxaEntrega();
    return subtotal + taxaEntrega;
  };

  // Função para buscar CEP via ViaCEP
  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    
    setBuscandoCEP(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado');
        setBuscandoCEP(false);
        return;
      }
      
      setEnderecoEntrega(prev => ({
        ...prev,
        rua: data.logradouro || prev.rua,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado,
        cep: cepLimpo.replace(/(\d{5})(\d{3})/, '$1-$2')
      }));
      
      toast.success('✅ Endereço encontrado!');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    } finally {
      setBuscandoCEP(false);
    }
  };

  const buscarClientePorTelefone = async (telefone) => {
    if (!telefone || telefone.length < 10 || !estabelecimento?._id) return;
    
    setBuscandoCliente(true);
    try {
      const telefoneLimpo = telefone.replace(/\D/g, '');
      const response = await api.get(`/api/clientes/buscar/${telefoneLimpo}?estabelecimento=${estabelecimento._id}`);
      
      if (response.data.success && response.data.data.cliente) {
        const clienteEncontrado = response.data.data.cliente;
        setCliente({
          nome: clienteEncontrado.nome || '',
          cpf: clienteEncontrado.cpf || '',
          telefone: clienteEncontrado.telefone || telefone,
          email: clienteEncontrado.email || ''
        });
        
        // ✅ Preencher endereço automaticamente se cliente tiver endereços cadastrados e estiver em modo delivery
        if (tipoEntrega === 'delivery' && clienteEncontrado.enderecos && clienteEncontrado.enderecos.length > 0) {
          const primeiroEndereco = clienteEncontrado.enderecos[0];
          setEnderecoEntrega({
            rua: primeiroEndereco.rua || '',
            numero: primeiroEndereco.numero || '',
            complemento: primeiroEndereco.complemento || '',
            bairro: primeiroEndereco.bairro || '',
            cidade: primeiroEndereco.cidade || estabelecimento?.endereco?.cidade || 'Cachoeiro de Itapemirim',
            estado: primeiroEndereco.estado || estabelecimento?.endereco?.estado || 'ES',
            cep: primeiroEndereco.cep || '',
            referencia: primeiroEndereco.referencia || ''
          });
          toast.success(`✅ Cliente encontrado: ${clienteEncontrado.nome} - Endereço preenchido automaticamente!`);
        } else {
          toast.success(`✅ Cliente encontrado: ${clienteEncontrado.nome}`);
        }
      } else {
        // Cliente não encontrado - limpar nome se não foi digitado manualmente
        if (!cliente.nome) {
          setCliente(prev => ({ ...prev, nome: '' }));
        }
      }
    } catch (error) {
      // Cliente não encontrado - limpar nome se não foi digitado manualmente
      if (!cliente.nome) {
        setCliente(prev => ({ ...prev, nome: '' }));
      }
    } finally {
      setBuscandoCliente(false);
    }
  };

  const finalizarVenda = () => {
    if (carrinho.length === 0) {
      toast.error('Adicione produtos ao carrinho');
      return;
    }

    if (!caixaData?._id) {
      toast.error('Caixa não encontrado');
      return;
    }

    if (!estabelecimento?._id) {
      toast.error('Estabelecimento não encontrado');
      return;
    }

    const isFiado = formaPagamento.tipo === 'fiado';
    
    // ✅ Validação obrigatória para vendas fiadas
    if (isFiado) {
      if (!cliente.nome || cliente.nome.trim() === '') {
        toast.error('Nome do cliente é obrigatório para vendas fiadas');
        return;
      }
      if (!cliente.telefone || cliente.telefone.replace(/\D/g, '').length < 10) {
        toast.error('Telefone do cliente é obrigatório para vendas fiadas');
        return;
      }
      if (!dataVencimentoFiado) {
        toast.error('Data de vencimento é obrigatória para vendas fiadas');
        return;
      }
    }

    const total = calcularTotal();

    const dadosVenda = {
      caixa: caixaData._id,
      estabelecimento: estabelecimento._id,
      itens: carrinho.map(item => ({
        produto: item.produto._id,
        quantidade: item.quantidade,
        precoUnitario: item.precoUnitario,
        desconto: item.desconto || 0
      })),
      cliente,
      formaPagamento: {
        ...formaPagamento,
        tipo: formaPagamento.tipo,
        valor: isFiado ? total : (formaPagamento.tipo === 'dinheiro' ? parseFloat(formaPagamento.valor) : total),
        troco: isFiado ? 0 : (formaPagamento.tipo === 'dinheiro' ? parseFloat(formaPagamento.valor) - total : 0)
      }
    };

    // Adicionar dataVencimentoFiado apenas se for fiado
    if (isFiado && dataVencimentoFiado) {
      dadosVenda.dataVencimentoFiado = dataVencimentoFiado;
    }

    console.log('📤 Enviando dados da venda:', dadosVenda);

    criarVendaMutation.mutate(dadosVenda);
  };

  const refreshProdutos = () => {
    queryClient.invalidateQueries(['produtos-caixa-fullscreen', estabelecimento?._id]);
  };

  // Buscar todas as comandas abertas para exibir como cards
  const { data: comandasAbertas, refetch: refetchComandasAbertas, isLoading: loadingComandas } = useQuery(
    ['comandas-abertas-caixa', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) {
        console.log('⚠️ Estabelecimento não encontrado');
        return [];
      }
      try {
        console.log('🔄 Buscando comandas abertas...');
        const response = await api.get(`/api/comandas?estabelecimento=${estabelecimento._id}&status=aberta`);
        const comandas = response.data.data.comandas || [];
        return comandas;
      } catch (error) {
        console.error('❌ Erro ao buscar comandas:', error.response?.data || error.message);
        return [];
      }
    },
    {
      enabled: !!estabelecimento?._id && abaAtiva === 'mesas',
      refetchInterval: 2000, // A cada 2 segundos
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 0,
      cacheTime: 0,
      // Forçar refetch mesmo quando disabled temporariamente
      keepPreviousData: false
    }
  );

  // Monitorar mudanças nas comandas
  useEffect(() => {
    if (comandasAbertas && comandasAbertas.length > 0) {
      console.log('🔄 Comandas atualizadas:', comandasAbertas.length);
      comandasAbertas.forEach(c => {
        console.log(`  📊 Mesa ${c.mesa}: Total R$ ${(c.total || 0).toFixed(2)}, Itens: ${c.itens?.length || 0}, Status: ${c.status}`);
      });
    } else if (comandasAbertas && comandasAbertas.length === 0) {
      console.log('📭 Nenhuma comanda aberta no momento');
    }
  }, [comandasAbertas]);

  // Buscar comanda por mesa (para busca manual)
  const { data: comandaMesaData, refetch: refetchComandaMesa } = useQuery(
    ['comanda-mesa', estabelecimento?._id, mesaSelecionada],
    async () => {
      if (!estabelecimento?._id || !mesaSelecionada) return null;
      try {
        const response = await api.get(`/api/comandas/mesa/${mesaSelecionada}?estabelecimento=${estabelecimento._id}`);
        return response.data.data.comanda;
      } catch (error) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    {
      enabled: !!estabelecimento?._id && !!mesaSelecionada && abaAtiva === 'mesas',
      refetchInterval: 3000
    }
  );

  // ✅ Buscar pedidos quando aba 'pedidos' estiver ativa - APENAS DO DIA ATUAL
  const { data: pedidosData, isLoading: loadingPedidos, refetch: refetchPedidos } = useQuery(
    getPedidosQueryKey(),
    async () => {
      if (!estabelecimento?._id) return [];
      try {
        let dataInicio = null;
        let dataFim = null;

        if (pedidoPeriodo === 'sessao') {
          dataInicio = caixaData?.dataAbertura ? new Date(caixaData.dataAbertura) : null;
        } else if (pedidoPeriodo === 'hoje') {
          const inicioHoje = new Date();
          inicioHoje.setHours(0, 0, 0, 0);
          dataInicio = inicioHoje;
        } else if (pedidoPeriodo === '7dias') {
          const hoje = new Date();
          const seteDiasAtras = new Date(hoje);
          seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);
          seteDiasAtras.setHours(0, 0, 0, 0);
          dataInicio = seteDiasAtras;
        } else if (pedidoPeriodo === 'custom') {
          if (pedidoDataInicio) {
            const inicio = new Date(pedidoDataInicio);
            inicio.setHours(0, 0, 0, 0);
            dataInicio = inicio;
          }
          if (pedidoDataFim) {
            const fim = new Date(pedidoDataFim);
            fim.setHours(23, 59, 59, 999);
            dataFim = fim;
          }
        }

        const params = new URLSearchParams();
        params.append('estabelecimento', estabelecimento._id);
        if (dataInicio) params.append('dataInicio', dataInicio.toISOString());
        if (dataFim) params.append('dataFim', dataFim.toISOString());
        
        console.log(
          `🔍 Buscando pedidos do estabelecimento ${estabelecimento._id}` +
          (dataInicio ? ` desde ${dataInicio.toISOString()}` : '') +
          (dataFim ? ` até ${dataFim.toISOString()}` : '')
        );
        
        const response = await api.get(`/api/pedidos?${params}`);
        const pedidos = response.data.data.pedidos || [];
        
        console.log(`✅ Encontrados ${pedidos.length} pedidos`);
        
        // Filtrar apenas pedidos não cancelados
        // (Não filtrar por statusPagamento aqui, senão pedidos podem "sumir" mesmo com filtro "Todos")
        const pedidosFiltrados = pedidos.filter(p => {
          // Mostrar todos os pedidos exceto cancelados
          if (p.status === 'Cancelado') return false;
          return true;
        });
        
        console.log(`📋 Após filtro: ${pedidosFiltrados.length} pedidos`);
        
        // Ordenar: Pendente primeiro, depois por data (mais recente primeiro)
        // Preferir updatedAt quando existir (evita sensação de “sumir” ao confirmar e o pedido ir lá pra baixo)
        return pedidosFiltrados.sort((a, b) => {
          if (a.status === 'Pendente' && b.status !== 'Pendente') return -1;
          if (a.status !== 'Pendente' && b.status === 'Pendente') return 1;
          const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bTime - aTime;
        });
      } catch (error) {
        console.error('❌ Erro ao buscar pedidos:', error);
        return [];
      }
    },
    {
      enabled:
        !!estabelecimento?._id &&
        abaAtiva === 'pedidos' &&
        (pedidoPeriodo !== 'sessao' || !!caixaData?.dataAbertura),
      refetchInterval: 5000, // Atualizar a cada 5 segundos
      refetchOnWindowFocus: true
    }
  );

  const pedidosExibidos = useMemo(() => {
    const lista = Array.isArray(pedidosData) ? pedidosData : [];
    const termo = (pedidoBusca || '').trim().toLowerCase();

    let filtrados = lista;
    if (pedidoFiltroStatus !== 'todos') {
      filtrados = filtrados.filter((p) => (p?.status || '') === pedidoFiltroStatus);
    }

    if (termo) {
      filtrados = filtrados.filter((p) => {
        const numero = String(p?.numero || '').toLowerCase();
        const nome = String(p?.cliente?.nome || '').toLowerCase();
        const tel = String(p?.cliente?.telefone || '').toLowerCase();
        return numero.includes(termo) || nome.includes(termo) || tel.includes(termo);
      });
    }

    return filtrados;
  }, [pedidosData, pedidoBusca, pedidoFiltroStatus]);

  // Atualizar formaPagamento quando mudar comanda selecionada
  useEffect(() => {
    if (abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)) {
      const comanda = comandaSelecionadaModal || comandaMesaData;
      if (comanda) {
        const total = parseFloat(comanda.total) || 0;
        setFormaPagamento(prev => ({
          ...prev,
          valor: prev.tipo === 'dinheiro' ? (prev.valor > 0 ? prev.valor : total) : total,
          troco: prev.tipo === 'dinheiro' ? (prev.troco || 0) : 0
        }));
      }
    }
  }, [comandaSelecionadaModal, comandaMesaData, abaAtiva]);

  // Limpar busca manual quando houver comandas abertas
  useEffect(() => {
    if (abaAtiva === 'mesas' && comandasAbertas && comandasAbertas.length > 0) {
      // Limpar busca manual se houver comandas abertas
      if (mesaSelecionada) {
        setMesaSelecionada('');
      }
    }
  }, [comandasAbertas, abaAtiva, mesaSelecionada]);

  // Socket.io para receber comandos de impressão automática (igual iFood/K2)
  useEffect(() => {
    if (!estabelecimento?._id) return;

    isMountedRef.current = true;

    try {
      if (typeof window !== 'undefined' && window.__pdvSocket) {
        window.__pdvSocket.disconnect();
        window.__pdvSocket = null;
      }
    } catch (e) {}

    const socketConectadoRef = { current: false };

    // Usar mesma lógica do api.js para determinar URL
    let apiUrl;
    if (process.env.NODE_ENV === 'production') {
      apiUrl = typeof window !== 'undefined' ? window.location.origin : '';
    } else {
      apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    }
    
    const socket = io(apiUrl, {
      transports: ['polling'],
      path: '/socket.io/',
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
      timeout: 20000, // 20 segundos de timeout
      forceNew: false,
      upgrade: false
    });

    try {
      if (typeof window !== 'undefined') {
        window.__pdvSocket = socket;
      }
    } catch (e) {}

    socket.on('connect', () => {
      console.log(' PDV conectado ao Socket.io para impressão automática');
      socketConectadoRef.current = true;
      // Entrar na sala do estabelecimento
      socket.emit('join-estabelecimento', estabelecimento._id);

      try {
        if (typeof refetchPedidos === 'function') {
          refetchPedidos();
        }
      } catch (e) {}
    });

    socket.on('novo-pedido', (pedido) => {
      try {
        const pedidoEstabelecimentoId = pedido?.estabelecimento?._id || pedido?.estabelecimento;
        if (pedidoEstabelecimentoId && pedidoEstabelecimentoId.toString() !== estabelecimento._id.toString()) {
          return;
        }
      } catch (e) {}

      try {
        if (typeof refetchPedidos === 'function') {
          refetchPedidos();
        }
      } catch (e) {}
    });

    socket.on('taxa-entrega-atualizada', (data) => {
      try {
        const estabelecimentoId = data?.estabelecimentoId;
        if (estabelecimentoId && estabelecimentoId.toString() !== estabelecimento._id.toString()) {
          return;
        }
        const ativa = data?.ativa !== false;
        setTaxaEntregaAtiva(ativa);
        queryClient.invalidateQueries('estabelecimento-caixa-fullscreen');
      } catch (e) {}
    });

    socket.on('imprimir-comanda-usb', (dados) => {
      console.log(' Recebido comando de impressão via Socket.io:', dados.pedidoNumero);
      console.log('   Tipo:', dados.tipo || 'comanda');

      const chaveDedupe = __dedupeKey({
        estabelecimentoId: dados.estabelecimentoId,
        id: dados.id,
        tipo: dados.tipo,
        numero: dados.numero,
        pedidoNumero: dados.pedidoNumero,
        conteudo: dados.conteudo
      });
      if (__jaImpresso(chaveDedupe)) {
        console.log(' [IMPRESSÃO] Ignorando comando já impresso (socket):', chaveDedupe);
        return;
      }
      
      // Verificar se é do mesmo estabelecimento
      if (dados.estabelecimentoId !== estabelecimento._id.toString()) {
        console.log(' Comando de outro estabelecimento, ignorando');
        return;
      }
      
      // ✅ No PDV do caixa, imprimir apenas a via do cliente/caixa.
      // Cozinha deve ser tratada por outro terminal/agente.
      const tipoValido = !dados.tipo || ['comanda', 'comprovante_baixa'].includes(dados.tipo);
      if (!tipoValido) {
        console.log(' Tipo de comando inválido, ignorando:', dados.tipo);
        return;
      }

      if (!__IMPRIMIR_VIA_SOCKET_NO_CAIXA) {
        console.log('🛑 [IMPRESSÃO] Caixa configurado para imprimir via polling. Ignorando impressão via socket.');
        return;
      }

      if (__impressaoTravada(chaveDedupe)) {
        console.log(' [IMPRESSÃO] Travado (anti-rajada), ignorando (socket):', chaveDedupe);
        return;
      }
      __lockImpressao(chaveDedupe);

      // Marcar imediatamente para evitar corrida com o polling (que roda a cada 2s)
      __marcarImpresso(chaveDedupe);

      const larguraImpressora = Number(estabelecimento?.impressora?.largura) || 80;
      const is58 = larguraImpressora === 58;
      const pageMm = is58 ? 58 : 80;
      const bodyMm = is58 ? 44 : 68;
      const fontPrintPx = is58 ? 16 : 17;
      const fontPreviewPx = is58 ? 13 : 14;

      // Criar janela oculta com conteúdo para impressão
      const printWindow = window.open('', '_blank', `width=${pageMm}mm,height=auto`);
      if (!printWindow) {
        console.error(' Não foi possível abrir janela de impressão');
        toast.error('Erro ao abrir impressão. Verifique bloqueador de pop-ups.');
        return;
      }

      const escapeHtml = (str) => (str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      const normalizarSeparadores = (str) => {
        const alvo = is58 ? 26 : 32;
        return (str || '')
          .replace(/\r\n/g, '\n')
          .replace(/\r/g, '\n')
          .replace(/\n+$/g, '\n')
          .split('\n')
          .map((linha) => {
            const l = (linha || '').toString();
            const t = l.trim();
            if (/^[-=]{20,}$/.test(t)) return t.slice(0, alvo);

            const idx = l.lastIndexOf('R$');
            if (idx !== -1 && l.length > alvo) {
              const valor = l.slice(idx).trim();
              let esquerda = l.slice(0, idx).trimEnd();
              const maxEsq = Math.max(0, alvo - valor.length - 1);
              if (esquerda.length > maxEsq) esquerda = esquerda.slice(0, maxEsq).trimEnd();
              const espacos = Math.max(1, alvo - esquerda.length - valor.length);
              return (esquerda + ' '.repeat(espacos) + valor).slice(0, alvo);
            }

            // Não truncar linhas comuns (endereços/itens). Deixe o CSS quebrar linha.
            return l;
          })
          .join('\n');
      };

      // Formatar conteúdo para impressão (preservar quebras de linha)
      const conteudoFormatado = escapeHtml(normalizarSeparadores(dados.conteudo));

      const logoUrl = window.location.origin + '/logo-cantinho.png';

      // Escrever HTML na janela com melhor formatação
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Comanda ${dados.numero}</title>
            <meta charset="UTF-8">
            <style>
              @media print {
                @page {
                  size: ${pageMm}mm auto;
                  margin: 0;
                  padding: 0;
                }
                * {
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                body {
                  margin: 0 !important;
                  padding: 0.5mm 8mm 0.5mm 0.5mm !important;
                  width: ${bodyMm}mm !important;
                  box-sizing: border-box !important;
                  font-family: 'Courier New', monospace !important;
                  font-size: ${fontPrintPx}px !important;
                  font-weight: normal !important;
                  line-height: 1.22 !important;
                  color: #000 !important;
                  background: #fff !important;
                }
                .logo {
                  width: 40mm;
                  height: 40mm;
                  object-fit: contain;
                  margin: 0 auto 2mm;
                  display: block;
                  filter: grayscale(100%) contrast(200%);
                }
                .conteudo {
                  font-size: ${fontPrintPx}px !important;
                  font-weight: 700 !important;
                  text-shadow: 0 0 0.6px #000 !important;
                  color: #000 !important;
                  line-height: 1.22 !important;
                  margin: 0 !important;
                  white-space: pre-wrap !important;
                  overflow-wrap: anywhere !important;
                  word-break: break-word !important;
                }
              }
              body {
                margin: 0 !important;
                padding: 0.5mm 8mm 0.5mm 0.5mm !important;
                width: ${bodyMm}mm !important;
                box-sizing: border-box !important;
                font-family: 'Courier New', monospace !important;
                font-size: ${fontPreviewPx}px !important;
                font-weight: normal !important;
                white-space: pre-wrap;
                line-height: 1.22 !important;
                color: #000 !important;
                background: #fff !important;
              }
              .logo {
                width: 40mm;
                height: 40mm;
                object-fit: contain;
                margin: 0 auto 2mm;
                display: block;
                filter: grayscale(100%) contrast(200%);
              }
              .conteudo {
                font-size: ${fontPreviewPx}px !important;
                font-weight: 700 !important;
                text-shadow: 0 0 0.6px #000 !important;
                color: #000 !important;
                line-height: 1.22 !important;
                margin: 0 !important;
                white-space: pre-wrap !important;
                overflow-wrap: anywhere !important;
                word-break: break-word !important;
              }
            </style>
          </head>
          <body>
            <img src="${logoUrl}" alt="Logo" class="logo" onerror="this.style.display='none';" />
            <pre class="conteudo">${conteudoFormatado}</pre>
            <script>
              (function() {
                let jaImprimiu = false;
                const tentarImprimir = () => {
                  if (jaImprimiu) return;
                  try {
                    jaImprimiu = true;
                    window.focus();
                    window.print();
                  } catch (e) {
                    jaImprimiu = false;
                    setTimeout(tentarImprimir, 300);
                  }
                };
                window.addEventListener('load', () => setTimeout(tentarImprimir, 200));
                window.onafterprint = () => setTimeout(() => window.close(), 100);
              })();
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();

      // Aguardar carregamento e exibir toast (a impressão é disparada dentro da própria janela)
      setTimeout(() => {
        const tipoComando = dados.tipo || 'comanda';
        const mensagem = tipoComando === 'comprovante_baixa' 
          ? `Comprovante de Baixa #${dados.numero} enviado para impressão`
          : `Comanda #${dados.numero} enviada para impressão`;
        toast.success(mensagem, { duration: 2000 });
      }, 500);
    });

    socket.on('disconnect', () => {
      console.log(' PDV desconectado do Socket.io');
      socketConectadoRef.current = false;
    });
    socket.on('connect_error', (error) => {
  console.error(' Erro de conexão Socket.io:', error);
});

return () => {
  isMountedRef.current = false;
  socket.disconnect();
};
}, [estabelecimento?._id]);

useEffect(() => {
  let intervaloId = null;

  const verificarAgentes = async () => {
    try {
      const resp = await api.get('/api/impressora/agentes/status');
      __agenteOnlineRef.current = Boolean(resp?.data?.online);
    } catch (e) {
      __agenteOnlineRef.current = false;
    }
  };

  verificarAgentes();
  intervaloId = setInterval(verificarAgentes, 5000);

  return () => {
    if (intervaloId) clearInterval(intervaloId);
  };
}, []);

// Função para imprimir comando diretamente
const imprimirComandoDireto = (cmd) => {
  console.log(' [IMPRESSÃO] Imprimindo comando:', cmd.numero || cmd.pedidoNumero);
  console.log('   Tipo:', cmd.tipo || 'comanda');
  console.log('   Estabelecimento:', cmd.estabelecimentoId);

  const chaveDedupe = __dedupeKey({
    estabelecimentoId: cmd.estabelecimentoId,
    id: cmd.id,
    tipo: cmd.tipo,
    numero: cmd.numero,
    pedidoNumero: cmd.pedidoNumero,
    conteudo: cmd.conteudo
  });
  const chaveCurta = __dedupeKeyCurta({
    estabelecimentoId: cmd.estabelecimentoId,
    id: cmd.id,
    tipo: cmd.tipo,
    numero: cmd.numero,
    pedidoNumero: cmd.pedidoNumero
  });
  if (__jaImpresso(chaveDedupe) || __jaImpressoGlobal(chaveDedupe) || __jaImpressoGlobal(chaveCurta)) {
    console.log(' [IMPRESSÃO] Ignorando comando já impresso (polling):', chaveDedupe);
    return;
  }

  __marcarImpressoGlobal(chaveDedupe);
  __marcarImpressoGlobal(chaveCurta);
  
  const larguraImpressora = Number(estabelecimento?.impressora?.largura) || 80;
  const is58 = larguraImpressora === 58;
  const pageMm = is58 ? 58 : 80;
  const bodyMm = is58 ? 58 : 80;
  const fontPrintPx = is58 ? 11 : 12;
  const fontPreviewPx = is58 ? 11 : 12;

  const escapeHtml = (str) => (str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const normalizarSeparadores = (str) => {
    const alvo = is58 ? 26 : 32;
    return (str || '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\n+$/g, '\n')
      .split('\n')
      .map((linha) => {
        const l = (linha || '').toString();
        const t = l.trim();
        if (/^[-=]{20,}$/.test(t)) return t.slice(0, alvo);

        const idx = l.lastIndexOf('R$');
        if (idx !== -1 && l.length > alvo) {
          const valor = l.slice(idx).trim();
          let esquerda = l.slice(0, idx).trimEnd();
          const maxEsq = Math.max(0, alvo - valor.length - 1);
          if (esquerda.length > maxEsq) esquerda = esquerda.slice(0, maxEsq).trimEnd();
          const espacos = Math.max(1, alvo - esquerda.length - valor.length);
          return (esquerda + ' '.repeat(espacos) + valor).slice(0, alvo);
        }

        return l;
      })
      .join('\n');
  };

  // Formatar conteúdo (preservar quebras de linha em <pre>)
  const conteudoFormatado = escapeHtml(normalizarSeparadores(cmd.conteudo));

  const logoUrl = window.location.origin + '/logo-cantinho.png';

  const htmlImpressao = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Comanda ${cmd.numero}</title>
        <meta charset="UTF-8">
        <style>
          @media print {
            @page { size: ${pageMm}mm auto; margin: 0; padding: 0; }
            * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
            body { width: ${bodyMm}mm !important; margin: 0 !important; padding: 2mm !important; box-sizing: border-box !important; font-family: 'Courier New', monospace !important; font-size: ${fontPrintPx}px !important; font-weight: normal !important; line-height: 1.18 !important; color: #000 !important; background: #fff !important; }
            .logo { width: 40mm; height: 40mm; object-fit: contain; object-position: center; margin: 0 auto 2mm; display: block; image-rendering: -webkit-optimize-contrast; filter: grayscale(100%) contrast(200%); }
            .conteudo { font-size: ${fontPrintPx}px !important; font-weight: 800 !important; -webkit-text-stroke: 0.15px #000 !important; text-shadow: 0 0 0.9px #000 !important; color: #000 !important; line-height: 1.18 !important; margin: 0 !important; white-space: pre-wrap !important; overflow-wrap: anywhere !important; word-break: break-word !important; }
          }
          body { width: ${bodyMm}mm !important; margin: 0 !important; padding: 2mm !important; box-sizing: border-box !important; font-family: 'Courier New', monospace !important; font-size: ${fontPreviewPx}px !important; font-weight: normal !important; white-space: pre-wrap; line-height: 1.18 !important; color: #000 !important; background: #fff !important; }
          .logo { width: 40mm; height: 40mm; object-fit: contain; object-position: center; margin: 0 auto 2mm; display: block; image-rendering: -webkit-optimize-contrast; filter: grayscale(100%) contrast(200%); }
          .conteudo { font-size: ${fontPreviewPx}px !important; font-weight: 800 !important; -webkit-text-stroke: 0.15px #000 !important; text-shadow: 0 0 0.9px #000 !important; color: #000 !important; line-height: 1.18 !important; margin: 0 !important; white-space: pre-wrap !important; overflow-wrap: anywhere !important; word-break: break-word !important; }
        </style>
      </head>
      <body>
        <img src="${logoUrl}" alt="Logo" class="logo" onerror="this.style.display='none';" />
        <pre class="conteudo">${conteudoFormatado}</pre>
      </body>
    </html>
  `;

  let disparou = false;
  try {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(iframe);
      throw new Error('iframe sem document');
    }

    doc.open();
    doc.write(htmlImpressao);
    doc.close();

    const win = iframe.contentWindow;
    const disparar = () => {
      if (disparou) return;
      try {
        win?.focus();
        win?.print();
        disparou = true;
      } catch (e2) {}
      setTimeout(() => {
        try { document.body.removeChild(iframe); } catch (e3) {}
      }, 2000);
    };

    let maxWait = null;
    try {
      const img = doc.querySelector('img.logo');
      maxWait = setTimeout(disparar, 1200);
      if (img) {
        img.onload = () => {
          if (maxWait) clearTimeout(maxWait);
          setTimeout(disparar, 50);
        };
        img.onerror = () => {
          if (maxWait) clearTimeout(maxWait);
          setTimeout(disparar, 50);
        };
      } else {
        setTimeout(disparar, 200);
      }
    } catch (e4) {
      setTimeout(disparar, 200);
    }
  } catch (e) {
    // fallback para pop-up
    const printWindow = window.open('', '_blank', `width=${pageMm}mm,height=auto`);
    if (!printWindow) {
      console.error(' Não foi possível abrir janela de impressão');
      toast.error('Impressão bloqueada pelo navegador. Permita pop-ups para o PDV.');
      return;
    }
    printWindow.document.write(htmlImpressao);
    printWindow.document.close();

    const dispararPopup = () => {
      if (disparou) return;
      try { printWindow.focus(); } catch (e2) {}
      try { printWindow.print(); } catch (e2) {}
      try { printWindow.close(); } catch (e2) {}
      disparou = true;
    };

    try {
      const img = printWindow.document.querySelector('img.logo');
      const maxWait = setTimeout(dispararPopup, 1200);
      if (img) {
        img.onload = () => {
          clearTimeout(maxWait);
          setTimeout(dispararPopup, 50);
        };
        img.onerror = () => {
          clearTimeout(maxWait);
          setTimeout(dispararPopup, 50);
        };
      } else {
        setTimeout(dispararPopup, 250);
      }
    } catch (e2) {
      setTimeout(dispararPopup, 250);
    }
  }

  setTimeout(() => {
    if (disparou) {
      toast.success(`Comanda #${cmd.numero} enviada para impressão`, { duration: 2000 });
      __marcarImpresso(chaveDedupe);
    }
  }, 300);
};

useEffect(() => {
  if (!estabelecimento?._id) return;

  let intervaloId = null;
  const LOCK_KEY = '__pdv_print_lock_v1';
  const LOCK_TTL_MS = 5000;

  const getLock = () => {
    try {
      const raw = localStorage.getItem(LOCK_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  const setLock = (lock) => {
    try {
      localStorage.setItem(LOCK_KEY, JSON.stringify(lock));
    } catch (e) {}
  };

  const tryAcquire = () => {
    const lock = getLock();
    if (!lock || !lock.id || !lock.ts || (Date.now() - lock.ts > LOCK_TTL_MS) || lock.id === instanciaIdRef.current) {
      setLock({ id: instanciaIdRef.current, ts: Date.now() });
      return true;
    }
    return false;
  };

  const touchLock = () => {
    const lock = getLock();
    if (lock && lock.id === instanciaIdRef.current) {
      setLock({ id: instanciaIdRef.current, ts: Date.now() });
    }
  };

  const verificarComandos = async () => {
    try {
      if (!tryAcquire()) return;

      // Se houver um agente de impressão (Socket/HTTP) online, o PDV NÃO deve imprimir via navegador
      // e nem confirmar/remover comandos da fila.
      if (false && __agenteOnlineRef?.current) {
        touchLock();
        return;
      }

      const response = await api.get('/api/agente/comandos.json', { timeout: 15000 });
      const comandos = response?.data?.comandos || [];

      const cmd = comandos.find((c) => !c.estabelecimentoId || c.estabelecimentoId === estabelecimento._id.toString());
      if (!cmd) {
        touchLock();
        return;
      }

      const chave = __dedupeKey({ estabelecimentoId: cmd.estabelecimentoId, id: cmd.id, tipo: cmd.tipo, numero: cmd.numero, pedidoNumero: cmd.pedidoNumero, conteudo: cmd.conteudo });
      if (__jaImpressoGlobal(chave) || __jaImpresso(chave)) {
        try {
          await api.post('/api/agente/confirmar', { id: cmd.id }, { timeout: 15000 });
        } catch (e) {}
        touchLock();
        return;
      }

      imprimirComandoDireto(cmd);

      setTimeout(async () => {
        try {
          await api.post('/api/agente/confirmar', { id: cmd.id }, { timeout: 15000 });
        } catch (e) {}
      }, 1200);

      touchLock();
    } catch (e) {
      // ignorar
    }
  };

  verificarComandos();
  intervaloId = setInterval(verificarComandos, 2000);
  const hb = setInterval(touchLock, 1000);

  return () => {
    if (intervaloId) clearInterval(intervaloId);
    if (hb) clearInterval(hb);
  };
 }, [estabelecimento?._id]);

// Fechar comanda e processar pagamento
const fecharComandaMutation = useMutation(
  ({ comandaId, formaPagamento, valorRecebido, pagamentos }) => {
    console.log(' Fechando comanda:', {
      comandaId,
      formaPagamento,
      valorRecebido,
      pagamentos: pagamentos?.length || 0
    });
    
    // Se houver múltiplos pagamentos, enviar array
    if (pagamentos && Array.isArray(pagamentos) && pagamentos.length > 0) {
      return api.post(`/api/comandas/${comandaId}/fechar`, {
        pagamentos: pagamentos
      });
    }
    
    // Formato antigo (pagamento único)
    return api.post(`/api/comandas/${comandaId}/fechar`, {
      formaPagamento: formaPagamento || {
        tipo: 'dinheiro',
        valor: valorRecebido || 0,
        troco: 0
      },
      valorRecebido: valorRecebido
    });
  },
  {
    onSuccess: async (response, variables) => {
      const comanda = response.data.data.comanda;
      
      console.log(' Comanda fechada com sucesso:', comanda._id);
      console.log(' Itens da comanda:', comanda.itens?.length || 0);
      console.log(' Total da comanda:', comanda.total);
      console.log(' Forma de pagamento:', variables.formaPagamento);
      
      // O backend já cria a venda automaticamente, então não precisamos criar aqui
      // Apenas atualizar a UI e limpar o cache
      
      const mensagemSucesso = comanda.itens?.length === 0 
        ? 'Comanda vazia cancelada com sucesso!' 
        : 'Comanda fechada e venda registrada com sucesso!';
      toast.success(mensagemSucesso);
      
      // Limpar estados
      setMesaSelecionada('');
      setComandaSelecionadaModal(null);
      setModalVenda(false);
      // Resetar estados de pagamento múltiplo
      setPagamentosSequenciaisVenda([]);
      setNovoPagamentoVenda({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
      setModoDividirPagamentoVenda(false);
      
      // REMOVER queries do cache completamente e invalidar
      queryClient.removeQueries(['comanda-mesa']);
      queryClient.removeQueries(['comandas-abertas-caixa']);
      queryClient.removeQueries(['comandas-abertas']);
      queryClient.removeQueries(['caixa-atual']);
      
      // Invalidar queries para forçar refetch
      queryClient.invalidateQueries(['comandas-abertas-caixa']);
      queryClient.invalidateQueries(['comandas-abertas']);
      queryClient.invalidateQueries(['caixa-atual']);
      
      // Refetch imediato e agressivo
      console.log(' Forçando atualização imediata após fechar comanda...');
      await Promise.all([
        refetchCaixa(),
        refetchComandaMesa(),
        refetchComandasAbertas()
      ]);
      
      // Refetch adicional em intervalos para garantir
      setTimeout(() => {
        refetchComandasAbertas();
        refetchCaixa();
        console.log(' Refetch 1 (300ms)');
      }, 300);
      setTimeout(() => {
        refetchComandasAbertas();
        refetchCaixa();
        console.log(' Refetch 2 (800ms)');
      }, 800);
      setTimeout(() => {
        refetchComandasAbertas();
        refetchCaixa();
        console.log(' Refetch 3 (1500ms)');
      }, 1500);
    },
    onError: (error) => {
      console.error('❌ Erro ao fechar comanda:', error);
      console.error('❌ Detalhes:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao fechar comanda';
      toast.error(errorMessage);
      
      // Mesmo em caso de erro, tentar atualizar a lista
      setTimeout(() => {
        refetchComandasAbertas();
      }, 500);
    }
  }
);

  // ✅ NOVO: Helper para renderizar nome do cliente
  function getNomeCliente(cliente) {
    if (!cliente) return 'Cliente';
    if (typeof cliente === 'string') return cliente;
    if (typeof cliente === 'object' && cliente?.nome) return cliente.nome;
    return 'Cliente';
  }

  // ✅ NOVO: Verificar se comanda está em produção
  const comandaEmProducao = (comanda) => {
    // Se não tem pedido, não está em produção
    if (!comanda.pedido) {
      return false;
    }
    
    // Verificar se pedido é objeto populado ou apenas ID
    let pedidoStatus = null;
    
    if (typeof comanda.pedido === 'object' && comanda.pedido !== null && comanda.pedido.status) {
      // Pedido está populado e tem status
      pedidoStatus = comanda.pedido.status;
    } else {
      // Pedido não está populado ou não tem status
      // Isso não deveria acontecer se o backend estiver populando corretamente
      return false;
    }
    
    // Verificar se status está em produção
    const statusProducao = ['Confirmado', 'Em preparo'];
    return statusProducao.includes(pedidoStatus);
  };

  // ✅ NOVO: Solicitar cancelamento
  const solicitarCancelamentoMutation = useMutation(
    ({ comandaId, motivo }) => api.post(`/api/comandas/${comandaId}/solicitar-cancelamento`, { motivo }),
    {
      onSuccess: () => {
        toast.success('Solicitação de cancelamento enviada! Aguardando aprovação do administrador.');
        setModalSolicitacao(false);
        setComandaParaSolicitar(null);
        setMotivoSolicitacao('');
        queryClient.invalidateQueries(['comandas-abertas-caixa']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao solicitar cancelamento');
      }
    }
  );

  // ✅ NOVO: Solicitar edição
  const solicitarEdicaoMutation = useMutation(
    ({ comandaId, motivo }) => api.post(`/api/comandas/${comandaId}/solicitar-edicao`, { motivo }),
    {
      onSuccess: () => {
        toast.success('Solicitação de edição enviada! Aguardando aprovação do administrador.');
        setModalSolicitacao(false);
        setComandaParaSolicitar(null);
        setMotivoSolicitacao('');
        queryClient.invalidateQueries(['comandas-abertas-caixa']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao solicitar edição');
      }
    }
  );

  // ✅ NOVO: Solicitar remoção de item
  const solicitarRemoverItemMutation = useMutation(
    ({ comandaId, itemId, motivo }) => api.post(`/api/comandas/${comandaId}/solicitar-remover-item`, { itemId, motivo }),
    {
      onSuccess: () => {
        toast.success('Solicitação de remoção de item enviada! Aguardando aprovação do administrador.');
        setModalSolicitacaoRemoverItem(false);
        setItemParaRemover(null);
        setMotivoRemocaoItem('');
        queryClient.invalidateQueries(['comandas-abertas-caixa']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao solicitar remoção de item');
      }
    }
  );

  // ✅ NOVO: Abrir modal de solicitação
  const abrirModalSolicitacao = (comanda, tipo) => {
    setComandaParaSolicitar(comanda);
    setTipoSolicitacao(tipo);
    setMotivoSolicitacao('');
    setModalSolicitacao(true);
  };

  // ✅ NOVO: Enviar solicitação
  const enviarSolicitacao = () => {
    if (!comandaParaSolicitar || !tipoSolicitacao) return;
    
    if (tipoSolicitacao === 'cancelar') {
      solicitarCancelamentoMutation.mutate({
        comandaId: comandaParaSolicitar._id,
        motivo: motivoSolicitacao
      });
    } else {
      solicitarEdicaoMutation.mutate({
        comandaId: comandaParaSolicitar._id,
        motivo: motivoSolicitacao
      });
    }
  };

  // ✅ NOVO: Abrir modal para solicitar remoção de item
  const abrirModalSolicitacaoRemoverItem = (comanda, item) => {
    setComandaParaSolicitar(comanda);
    setItemParaRemover(item);
    setMotivoRemocaoItem('');
    setModalSolicitacaoRemoverItem(true);
  };

  // ✅ NOVO: Enviar solicitação de remoção de item
  const enviarSolicitacaoRemoverItem = () => {
    if (!comandaParaSolicitar || !itemParaRemover) return;
    
    solicitarRemoverItemMutation.mutate({
      comandaId: comandaParaSolicitar._id,
      itemId: itemParaRemover._id,
      motivo: motivoRemocaoItem
    });
  };

  // Imprimir comanda
  const imprimirComandaMutation = useMutation(
    (comandaId) => api.post(`/api/comandas/${comandaId}/imprimir`),
    {
      onSuccess: () => {
        toast.success('Comanda impressa com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao imprimir comanda');
      }
    }
  );

  const handleBuscarMesa = () => {
    if (!mesaSelecionada.trim()) {
      toast.error('Informe o número da mesa');
      return;
    }
    refetchComandaMesa();
  };

  const [modalPreviewImpressao, setModalPreviewImpressao] = useState(false);
  const [textoPreviewImpressao, setTextoPreviewImpressao] = useState('');

  const handlePagarComanda = () => {
    if (!comandaMesaData) {
      toast.error('Comanda não encontrada');
      return;
    }
    setComandaSelecionadaModal(comandaMesaData);
    // ✅ Resetar estados de pagamento múltiplo
    setPagamentosSequenciaisVenda([]);
    setNovoPagamentoVenda({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
    setModoDividirPagamentoVenda(false);
    setModalVenda(true);
  };

  const handleSelecionarMesa = (comanda) => {
    setComandaSelecionadaModal(comanda);
    // ✅ Resetar estados de pagamento múltiplo
    setPagamentosSequenciaisVenda([]);
    setNovoPagamentoVenda({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
    setModoDividirPagamentoVenda(false);
    setModalVenda(true);
  };

  const handlePreviewImpressao = async (comandaId) => {
    try {
      // Buscar comanda completa
      const response = await api.get(`/api/comandas/${comandaId}`);
      const comanda = response.data.data.comanda;
      
      // Buscar estabelecimento
      const estabelecimentoResponse = await api.get('/api/estabelecimentos/me/estabelecimento');
      const estabelecimentoData = estabelecimentoResponse.data.data.estabelecimento;
      
      // Formatar texto da comanda (simular impressão)
      // Usar o mesmo formato do backend
      const dataHora = new Date(comanda.dataAbertura).toLocaleString('pt-BR');
      let texto = '';
      
      texto += '='.repeat(50) + '\n';
      texto += '        [LOGO CANTINHO DO SABOR]\n';
      texto += '        (logo-cantinho-print.svg)\n';
      texto += '='.repeat(50) + '\n';
      texto += `${estabelecimentoData?.nome || 'CANTINHO DO SABOR'}\n`;
      texto += 'COMANDA DE CONFERÊNCIA\n';
      texto += '-'.repeat(50) + '\n';
      texto += `COMANDA: ${comanda.numero}\n`;
      texto += `MESA: ${comanda.mesa}\n`;
      texto += `DATA: ${dataHora}\n`;
      texto += '-'.repeat(50) + '\n';
      texto += `CLIENTE: ${getNomeCliente(comanda.cliente)}\n`;
      texto += '-'.repeat(50) + '\n';
      texto += 'ITENS:\n';
      texto += '-'.repeat(50) + '\n';

      comanda.itens.forEach((item, index) => {
        const produto = item.produto || {};
        texto += `${item.quantidade}x ${produto.nome || 'Produto'}\n`;
        
        if (item.clienteItem) {
          texto += `   👤 ${item.clienteItem}\n`;
        }
        
        if (item.observacoes) {
          texto += `   📝 ${item.observacoes}\n`;
        }
        
        if (item.acrescimos && item.acrescimos.length > 0) {
          const acrescimosTexto = item.acrescimos.map(a => `${Math.min(20, Math.max(1, Number(a?.quantidade || 1)))}x ${a.nome} (+R$ ${a.preco.toFixed(2)})`).join(', ');
          texto += `   ➕ ${acrescimosTexto}\n`;
        }
        
        texto += `   R$ ${item.precoUnitario.toFixed(2)} = R$ ${item.subtotal.toFixed(2)}\n`;
        
        if (index < comanda.itens.length - 1) {
          texto += '\n';
        }
      });

      texto += '-'.repeat(50) + '\n';
      texto += `SUBTOTAL: R$ ${comanda.subtotal.toFixed(2)}\n`;
      texto += `TOTAL: R$ ${comanda.total.toFixed(2)}\n`;
      texto += '='.repeat(50) + '\n';
      
      setTextoPreviewImpressao(texto);
      setModalPreviewImpressao(true);
    } catch (error) {
      toast.error('Erro ao gerar preview: ' + (error.response?.data?.message || error.message));
    }
  };

  // Se não houver caixa aberto, mostrar modal de abertura
  if (!caixaData) {
    return (
      <div className="caixa-fullscreen">
        <div className="caixa-fechado">
          <Lock size={64} />
          <h2>Caixa Fechado</h2>
          <p>Abra o caixa para começar a trabalhar</p>
          <button
            className="btn btn-primary"
            onClick={() => {
              if (!estabelecimento?._id) {
                toast.error('Estabelecimento não encontrado. Aguarde o carregamento...');
                return;
              }
              setModalAbertura(true);
            }}
            disabled={!estabelecimento?._id}
          >
            Abrir Caixa
          </button>

          {modalAbertura && (
            <div className="modal-overlay" onClick={() => setModalAbertura(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h3>Abrir Caixa</h3>
                <div className="form-group">
                  <label>Troco (valor inicial do caixa)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={valorAbertura}
                    onChange={(e) => setValorAbertura(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setModalAbertura(false)}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (!estabelecimento?._id) {
                        toast.error('Estabelecimento não encontrado. Aguarde o carregamento...');
                        return;
                      }
                      
                      const valor = parseFloat(valorAbertura) || 0;
                      if (isNaN(valor) || valor < 0) {
                        toast.error('Valor de abertura inválido');
                        return;
                      }
                      
                      abrirCaixaMutation.mutate({
                        estabelecimento: estabelecimento._id,
                        valorAbertura: valor
                      });
                    }}
                    disabled={abrirCaixaMutation.isLoading || !estabelecimento?._id}
                  >
                    {abrirCaixaMutation.isLoading ? 'Abrindo...' : 'Abrir'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {modalFechamento && relatorioFechamento && (
            <div className="modal-overlay">
              <div className="modal-content modal-fechamento" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Relatório de Fechamento</h3>
                </div>

                <div className="relatorio-content">
                  <div className="relatorio-section">
                    <h4>Resumo Geral</h4>
                    <div className="relatorio-item">
                      <span>Troco (inicial):</span>
                      <strong>R$ {relatorioFechamento.valorAbertura.toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>Total de Vendas:</span>
                      <strong>R$ {relatorioFechamento.totalVendas.toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>Quantidade de Vendas:</span>
                      <strong>{relatorioFechamento.quantidadeVendas}</strong>
                    </div>
                    <div className="relatorio-item highlight">
                      <span>Valor Esperado (troco + vendas):</span>
                      <strong>R$ {relatorioFechamento.valorEsperado.toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>Valor de Fechamento:</span>
                      <strong>R$ {relatorioFechamento.valorFechamento.toFixed(2)}</strong>
                    </div>
                    <div className={`relatorio-item ${relatorioFechamento.diferenca >= 0 ? 'positive' : 'negative'}`}>
                      <span>Diferença:</span>
                      <strong>R$ {relatorioFechamento.diferenca.toFixed(2)}</strong>
                    </div>
                  </div>
                  <div className="relatorio-section">
                    <h4>Detalhamento por Forma de Pagamento</h4>
                    <div className="relatorio-item">
                      <span>💵 Dinheiro:</span>
                      <strong>R$ {Number(relatorioFechamento?.totaisPorFormaPagamento?.dinheiro?.total || 0).toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>📱 PIX:</span>
                      <strong>R$ {Number(relatorioFechamento?.totaisPorFormaPagamento?.pix?.total || 0).toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>💳 Débito:</span>
                      <strong>R$ {Number(relatorioFechamento?.totaisPorFormaPagamento?.cartao_debito?.total || 0).toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>💳 Crédito:</span>
                      <strong>R$ {Number(relatorioFechamento?.totaisPorFormaPagamento?.cartao_credito?.total || 0).toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>⏳ Crediário:</span>
                      <strong>R$ {Number(relatorioFechamento?.vendasFiadas?.total || 0).toFixed(2)}</strong>
                    </div>
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={() => {
                    setModalFechamento(false);
                    setRelatorioFechamento(null);
                  }}>
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Calcular estatísticas
  // Usar dados do backend que já vem com estatísticas completas
  const totalVendasCount = caixaData?.totalVendas || caixaData?.vendas?.length || 0;
  const vendasFinalizadasCount = caixaData?.vendasFinalizadas || caixaData?.vendas?.filter(v => v?.status === 'finalizada').length || 0;
  
  // Garantir que vendas estejam populadas e filtradas corretamente
  const vendasArray = caixaData?.vendas || [];
  const vendasFinalizadas = vendasArray.filter(v => {
    // Verificar se venda existe e tem status finalizada
    if (!v) return false;
    // Se for objeto, verificar status
    if (typeof v === 'object') {
      return v.status === 'finalizada';
    }
    return false;
  });
  
  // ✅ Separar vendas fiadas das vendas normais
  const vendasFiadas = vendasFinalizadas.filter(v => v.isFiado === true);
  const vendasNormais = vendasFinalizadas.filter(v => !v.isFiado || v.isFiado === false);
  
  // Calcular totais (APENAS vendas normais, não incluir fiadas)
  const totalVendas = vendasNormais.reduce((acc, v) => acc + (parseFloat(v.total) || 0), 0);
  const totalVendasFiadas = vendasFiadas.reduce((acc, v) => acc + (parseFloat(v.total) || 0), 0);
  const valorEmCaixa = (parseFloat(caixaData?.valorAbertura) || 0) + totalVendas;
  
  // Usar dados do backend se disponível
  const vendasFiadasData = caixaData?.vendasFiadas || { quantidade: vendasFiadas.length, total: totalVendasFiadas };
  
  console.log('📊 Vendas no caixa:', {
    totalVendas: totalVendasCount,
    vendasFinalizadas: vendasFinalizadasCount,
    vendasNormais: vendasNormais.length,
    vendasFiadas: vendasFiadas.length,
    totalVendasFiadas: totalVendasFiadas,
    vendasNoArray: vendasArray.length
  });

  const statsCards = [
    {
      title: 'Total em Caixa',
      value: `R$ ${valorEmCaixa.toFixed(2)}`,
      icon: DollarSign,
      premiumClass: 'premium-green',
      subtitle: `Abertura: R$ ${(caixaData?.valorAbertura || 0).toFixed(2)}`
    },
    {
      title: 'Total de Vendas',
      value: totalVendasCount,
      icon: ShoppingBag,
      premiumClass: 'premium-blue',
      subtitle: `${vendasFinalizadasCount} finalizadas`
    },
    {
      title: 'Total Vendido',
      value: `R$ ${totalVendas.toFixed(2)}`,
      icon: TrendingUp,
      premiumClass: 'premium-green',
      subtitle: 'Vendas finalizadas'
    },
    {
      title: 'Tipo de Venda',
      value: 'BALCÃO',
      icon: Store,
      premiumClass: 'premium-orange',
      subtitle: 'PDV - Ponto de Venda'
    }
  ];

  const comandasAbertasExibidas = (() => {
    const lista = Array.isArray(comandasAbertas) ? comandasAbertas : [];
    const termo = (mesaBusca || '').trim().toLowerCase();
    if (!termo) return lista;
    return lista.filter((c) => {
      const mesa = String(c?.mesa || '').toLowerCase();
      const numero = String(c?.numero || '').toLowerCase();
      const cliente = String(getNomeCliente(c?.cliente) || '').toLowerCase();
      return mesa.includes(termo) || numero.includes(termo) || cliente.includes(termo);
    });
  })();

  return (
    <div className="caixa-fullscreen">
      {/* Abas */}
      <div className="caixa-abas">
        <button
          className={`caixa-aba ${abaAtiva === 'caixa' ? 'active' : ''}`}
          onClick={() => setAbaAtiva('caixa')}
        >
          <ShoppingBag size={18} />
          Caixa
        </button>
        <button
          className={`caixa-aba ${abaAtiva === 'mesas' ? 'active' : ''}`}
          onClick={() => {
            setAbaAtiva('mesas');
            // Forçar refetch ao mudar para aba mesas
            setTimeout(() => {
              refetchComandasAbertas();
            }, 100);
          }}
        >
          <Table size={18} />
          Mesas
        </button>
        <button
          className={`caixa-aba ${abaAtiva === 'pedidos' ? 'active' : ''}`}
          onClick={() => {
            setAbaAtiva('pedidos');
            // Forçar refetch ao mudar para aba pedidos
            setTimeout(() => {
              refetchPedidos();
            }, 100);
          }}
        >
          <ShoppingCart size={18} />
          Pedidos
        </button>
        <button
          className={`caixa-aba ${abaAtiva === 'marmitex' ? 'active' : ''}`}
          onClick={() => setAbaAtiva('marmitex')}
        >
          <Store size={18} />
          Marmitex
        </button>
      </div>

      {/* Header Compacto */}
      <div className="caixa-header-compact">
        <div className="caixa-header-left">
          <h2>💳 PDV - Ponto de Venda</h2>
          <span className="caixa-status">
            <Clock size={14} />
            Caixa #{caixaData?.numero || 'Aberto'} - {new Date().toLocaleTimeString('pt-BR')}
          </span>
        </div>
        
        <div className="stats-grid-compact">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className={`stat-card-compact ${stat.premiumClass}`}>
                <Icon size={18} />
                <div className="stat-content-compact">
                  <p className="stat-title-compact">{stat.title}</p>
                  <p className="stat-value-compact">{stat.value}</p>
                </div>
              </div>
            );
          })}
          {/* ✅ Card de Vendas Fiadas */}
          {vendasFiadasData.quantidade > 0 && (
            <div className="stat-card-compact premium-orange" style={{ border: '2px solid #f59e0b' }}>
              <Clock size={18} />
              <div className="stat-content-compact">
                <p className="stat-title-compact">Vendas Fiadas</p>
                <p className="stat-value-compact">{vendasFiadasData.quantidade}</p>
                <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                  R$ {vendasFiadasData.total?.toFixed(2) || totalVendasFiadas.toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {estabelecimento?._id && (
            <button
              onClick={() => toggleAceitaPedidosMutation.mutate(!aceitaPedidos)}
              disabled={toggleAceitaPedidosMutation.isLoading}
              style={{
                padding: '0.75rem 1.25rem',
                background: aceitaPedidos ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: toggleAceitaPedidosMutation.isLoading ? 'not-allowed' : 'pointer',
                fontWeight: 800,
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                whiteSpace: 'nowrap',
                opacity: toggleAceitaPedidosMutation.isLoading ? 0.7 : 1
              }}
              title={aceitaPedidos ? 'Fechar pedidos online (site/WhatsApp)' : 'Abrir pedidos online (site/WhatsApp)'}
            >
              <Power size={18} />
              {aceitaPedidos ? 'FECHAR' : 'ABRIR'} PEDIDOS
            </button>
          )}
          {estabelecimento?._id && (
            <button
              onClick={() => {
                const novoValor = !taxaEntregaAtiva;
                const ok = window.confirm(
                  novoValor
                    ? 'Ativar cobrança de taxa de entrega? (vale apenas para pedidos novos)'
                    : 'Desativar cobrança de taxa de entrega? (Entrega grátis, vale apenas para pedidos novos)'
                );
                if (!ok) return;
                toggleTaxaEntregaMutation.mutate(novoValor);
              }}
              disabled={toggleTaxaEntregaMutation.isLoading}
              style={{
                padding: '0.75rem 1.25rem',
                background: taxaEntregaAtiva ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: toggleTaxaEntregaMutation.isLoading ? 'not-allowed' : 'pointer',
                fontWeight: 800,
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                whiteSpace: 'nowrap',
                opacity: toggleTaxaEntregaMutation.isLoading ? 0.7 : 1
              }}
              title={taxaEntregaAtiva ? 'Desativar taxa de entrega (Entrega grátis)' : 'Ativar taxa de entrega'}
            >
              <DollarSign size={18} />
              {taxaEntregaAtiva ? 'DESLIGAR' : 'LIGAR'} TAXA
            </button>
          )}
          <button 
            className="btn-delivery-caixa"
            onClick={() => setModalDelivery(true)}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              fontWeight: '700',
              fontSize: '0.9375rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
            }}
          >
            <Truck size={20} />
            DELIVERY
          </button>
          <button 
            className="btn-fechar-caixa-compact"
            onClick={() => {
              const valorEsperado = valorEmCaixa;
              setValorFechamento(valorEsperado.toFixed(2));
              setModalFechamento(true);
            }}
          >
            <Lock size={16} />
            Fechar
          </button>
        </div>
      </div>

      {abaAtiva === 'mesas' ? (
        <div className="mesas-section">
          <div className="mesas-header">
            <h3>🍽️ Pagamento de Mesas</h3>
            <div className="mesas-header-actions">
              <div className="buscar-mesa">
                <input
                  type="text"
                  value={mesaBusca}
                  onChange={(e) => setMesaBusca(e.target.value)}
                  placeholder="Filtrar mesas (mesa, cliente ou comanda)..."
                />
                <button className="btn-buscar-mesa" onClick={() => setMesaBusca('')}>
                  <X size={18} />
                  Limpar
                </button>
              </div>
            </div>
          </div>

          {/* Cards de Mesas */}
          {loadingComandas ? (
            <div className="empty-mesa">
              <Package size={48} />
              <p>Carregando comandas...</p>
            </div>
          ) : comandasAbertas && comandasAbertas.length > 0 ? (
            comandasAbertasExibidas.length > 0 ? (
              <div className="mesas-grid">
                {comandasAbertasExibidas.map((comanda) => (
                  <div key={comanda._id} className="mesa-card-caixa" onClick={() => handleSelecionarMesa(comanda)}>
                    <div className="mesa-card-header">
                      <h4>Mesa {comanda.mesa}</h4>
                      <span className="mesa-status-badge aberta">Aberta</span>
                    </div>
                    <div className="mesa-card-info">
                      <p><strong>Cliente:</strong> {getNomeCliente(comanda.cliente)}</p>
                      <p><strong>Comanda:</strong> #{comanda.numero}</p>
                      <p><strong>Itens:</strong> {comanda.itens?.length || 0}</p>
                    </div>
                    <div className="mesa-card-total">
                      <span>Total:</span>
                      <strong>R$ {(comanda.total || 0).toFixed(2)}</strong>
                    </div>
                    <div className="mesa-card-acoes">
                      {/* ✅ Mostrar botões de solicitação se comanda estiver em produção */}
                      {comandaEmProducao(comanda) ? (
                        <>
                          <button 
                            className="btn-solicitar-cancelamento"
                            onClick={(e) => {
                              e.stopPropagation();
                              abrirModalSolicitacao(comanda, 'cancelar');
                            }}
                            style={{
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: '1px solid #dc2626',
                              fontSize: '0.75rem',
                              padding: '0.5rem 0.75rem'
                            }}
                          >
                            <Ban size={14} />
                            Cancelar
                          </button>
                          <button 
                            className="btn-solicitar-edicao"
                            onClick={(e) => {
                              e.stopPropagation();
                              abrirModalSolicitacao(comanda, 'editar');
                            }}
                            style={{
                              background: '#dbeafe',
                              color: '#2563eb',
                              border: '1px solid #2563eb',
                              fontSize: '0.75rem',
                              padding: '0.5rem 0.75rem'
                            }}
                          >
                            <Edit size={14} />
                            Editar
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="btn-preview-mesa"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreviewImpressao(comanda._id);
                            }}
                          >
                            <FileText size={16} />
                            Preview
                          </button>
                          <button 
                            className="btn-pagar-mesa"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelecionarMesa(comanda);
                            }}
                          >
                            <CreditCard size={16} />
                            Pagar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-mesa">
                <Table size={48} />
                <p>Nenhuma mesa encontrada para o filtro</p>
              </div>
            )
          ) : (
            <div className="empty-mesa">
              <Table size={48} />
              <p>Nenhuma comanda aberta no momento</p>
              <button 
                className="btn-refresh-mesas"
                onClick={() => {
                  refetchComandasAbertas();
                  toast.success('Atualizando...');
                }}
                style={{ marginTop: '1rem' }}
              >
                🔄 Atualizar
              </button>
            </div>
          )}

          {/* Busca manual - APENAS se não houver comandas abertas nos cards */}
          {(!comandasAbertas || comandasAbertas.length === 0) && !loadingComandas && (
            <>
              <div className="buscar-mesa" style={{ marginTop: '1rem' }}>
                <input
                  type="text"
                  value={mesaSelecionada}
                  onChange={(e) => setMesaSelecionada(e.target.value)}
                  placeholder="Buscar mesa específica..."
                  onKeyPress={(e) => e.key === 'Enter' && handleBuscarMesa()}
                />
                <button className="btn-buscar-mesa" onClick={handleBuscarMesa}>
                  <Search size={18} />
                  Buscar
                </button>
              </div>
              {comandaMesaData ? (
                <div className="comanda-mesa-detalhes">
                  <div className="comanda-mesa-header">
                    <div>
                      <h4>{comandaMesaData.mesa}</h4>
                      <p>Cliente: {getNomeCliente(comandaMesaData.cliente)}</p>
                      <span className="comanda-numero">#{comandaMesaData.numero}</span>
                    </div>
                    <button 
                      className="btn-imprimir-comanda" 
                      onClick={() => handlePreviewImpressao(comandaMesaData._id)}
                      disabled={imprimirComandaMutation.isLoading}
                    >
                      <FileText size={18} />
                      Preview / Imprimir
                    </button>
                  </div>

                  <div className="comanda-mesa-itens">
                    <h5>Itens da Comanda:</h5>
                    {comandaMesaData.itens.map((item, idx) => (
                      <div key={idx} className="item-mesa">
                        <div className="item-mesa-info">
                          <strong>{item.quantidade}x</strong>
                          <div>
                            <span>{item.produto?.nome || 'Produto'}</span>
                            {item.clienteItem && <small>👤 {item.clienteItem}</small>}
                            {item.observacoes && <small>📝 {item.observacoes}</small>}
                            {item.acrescimos && item.acrescimos.length > 0 && (
                              <small>➕ {item.acrescimos.map(a => `${Math.min(20, Math.max(1, Number(a?.quantidade || 1)))}x ${a.nome}`).join(', ')}</small>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span className="item-mesa-preco">R$ {item.subtotal.toFixed(2)}</span>
                          {/* ✅ Mostrar botão de remover ou solicitar remoção */}
                          {!comandaEmProducao(comandaMesaData) ? (
                            <button
                              className="btn-remover-item-mesa"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Remover item diretamente (comanda não está em produção)
                                if (window.confirm(`Deseja remover ${item.quantidade}x ${item.produto?.nome || 'Produto'}?`)) {
                                  api.delete(`/api/comandas/${comandaMesaData._id}/itens/${item._id}`)
                                    .then(() => {
                                      toast.success('Item removido!');
                                      refetchComandaMesa();
                                    })
                                    .catch(error => {
                                      toast.error(error.response?.data?.message || 'Erro ao remover item');
                                    });
                                }
                              }}
                              style={{
                                background: '#fee2e2',
                                color: '#dc2626',
                                border: '1px solid #dc2626',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                cursor: 'pointer'
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          ) : (
                            <button
                              className="btn-solicitar-remocao-item-mesa"
                              onClick={(e) => {
                                e.stopPropagation();
                                abrirModalSolicitacaoRemoverItem(comandaMesaData, item);
                              }}
                              style={{
                                background: '#fef3c7',
                                color: '#d97706',
                                border: '1px solid #d97706',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                cursor: 'pointer'
                              }}
                              title="Solicitar remoção (requer aprovação do admin)"
                            >
                              <AlertCircle size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="comanda-mesa-total">
                    <span>Total a Pagar:</span>
                    <strong>R$ {comandaMesaData.total.toFixed(2)}</strong>
                  </div>

                  <button className="btn-pagar-comanda" onClick={handlePagarComanda}>
                    <CreditCard size={20} />
                    Processar Pagamento
                  </button>
                </div>
              ) : mesaSelecionada ? (
                <div className="empty-mesa">
                  <Table size={48} />
                  <p>Nenhuma comanda aberta encontrada para {mesaSelecionada}</p>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : abaAtiva === 'pedidos' ? (
        <div className="pedidos-section-caixa">
          <div className="pedidos-header-caixa">
            <h3>🛒 Pedidos</h3>
            <div className="pedidos-controls-caixa">
              <div className="pedidos-search-caixa">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Buscar pedido, cliente ou telefone..."
                  value={pedidoBusca}
                  onChange={(e) => setPedidoBusca(e.target.value)}
                />
              </div>
              <select
                className="pedidos-filter-select-caixa"
                value={pedidoPeriodo}
                onChange={(e) => {
                  const valor = e.target.value;
                  setPedidoPeriodo(valor);
                  if (valor !== 'custom') {
                    setPedidoDataInicio('');
                    setPedidoDataFim('');
                  }
                }}
                title="Período"
              >
                <option value="sessao">Sessão (caixa)</option>
                <option value="hoje">Hoje</option>
                <option value="7dias">Últimos 7 dias</option>
                <option value="custom">Personalizado</option>
              </select>
              {pedidoPeriodo === 'custom' && (
                <>
                  <input
                    type="date"
                    value={pedidoDataInicio}
                    onChange={(e) => setPedidoDataInicio(e.target.value)}
                    className="pedidos-filter-select-caixa"
                    title="Data início"
                  />
                  <input
                    type="date"
                    value={pedidoDataFim}
                    onChange={(e) => setPedidoDataFim(e.target.value)}
                    className="pedidos-filter-select-caixa"
                    title="Data fim"
                  />
                </>
              )}
              <select
                className="pedidos-filter-select-caixa"
                value={pedidoFiltroStatus}
                onChange={(e) => setPedidoFiltroStatus(e.target.value)}
                title="Filtrar por status"
              >
                <option value="todos">Todos</option>
                <option value="Pendente">Pendente</option>
                <option value="Confirmado">Confirmado</option>
                <option value="Em preparo">Em preparo</option>
                <option value="Pronto">Pronto</option>
                <option value="Saiu para entrega">Saiu para entrega</option>
                <option value="Entregue">Entregue</option>
              </select>
              <div className="pedidos-view-toggle-caixa">
                <button
                  type="button"
                  className={pedidoView === 'grid' ? 'active' : ''}
                  onClick={() => setPedidoView('grid')}
                  title="Grade"
                >
                  Grade
                </button>
                <button
                  type="button"
                  className={pedidoView === 'list' ? 'active' : ''}
                  onClick={() => setPedidoView('list')}
                  title="Lista"
                >
                  Lista
                </button>
                <button
                  type="button"
                  className={pedidoView === 'compact' ? 'active' : ''}
                  onClick={() => setPedidoView('compact')}
                  title="Compacto"
                >
                  Compacto
                </button>
              </div>
              <button 
                className="btn-refresh-pedidos"
                onClick={() => {
                  refetchPedidos();
                  toast.success('Atualizando pedidos...');
                }}
                title="Atualizar lista de pedidos"
              >
                🔄 Atualizar
              </button>
            </div>
          </div>

          {loadingPedidos ? (
            <div className="empty-pedidos">
              <ShoppingCart size={48} />
              <p>Carregando pedidos...</p>
            </div>
          ) : pedidosExibidos && pedidosExibidos.length > 0 ? (
            <div className={`pedidos-grid-caixa pedidos-view-${pedidoView}`}>
              {pedidosExibidos.map((pedido) => (
                <div key={pedido._id} className="pedido-card-caixa">
                  <div className="pedido-card-header-caixa">
                    <div>
                      <h4>Pedido #{pedido.numero}</h4>
                      <span className={`pedido-status-badge-caixa ${pedido.status?.toLowerCase()}`}>
                        {pedido.status}
                      </span>
                    </div>
                    <span className="pedido-data-caixa">
                      {new Date(pedido.createdAt).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  
                  <div className="pedido-card-info-caixa">
                    <div className="info-item-caixa">
                      <strong>Cliente:</strong>
                      <span>{pedido.cliente?.nome || 'Não informado'}</span>
                    </div>
                    {pedido.cliente?.telefone && (
                      <div className="info-item-caixa">
                        <strong>Telefone:</strong>
                        <span>{pedido.cliente.telefone}</span>
                      </div>
                    )}
                    <div className="info-item-caixa">
                      <strong>Tipo:</strong>
                      <span>
                        {pedido.tipoEntrega === 'delivery' ? '🚴 Delivery' : 
                         pedido.tipoEntrega === 'retirada' ? '📦 Retirada' :
                         // Fallback: se não tem tipoEntrega, tentar inferir pelo enderecoEntrega
                         (pedido.enderecoEntrega && pedido.enderecoEntrega.rua) ? '🚴 Delivery' :
                         pedido.origem === 'balcao' ? '🛒 Balcão' : 
                         pedido.origem === 'chat' ? '💬 Chat' : 
                         pedido.origem === 'site' ? '📦 Retirada' : '📦 Retirada'}
                      </span>
                    </div>
                    <div className="info-item-caixa">
                      <strong>Pagamento:</strong>
                      <span className={`pagamento-status-caixa ${pedido.statusPagamento?.toLowerCase()}`}>
                        {pedido.statusPagamento === 'Pago' ? '✅ Pago' : 
                         pedido.statusPagamento === 'Pendente' ? '⏳ Pendente' : 
                         pedido.statusPagamento || 'Não informado'}
                      </span>
                    </div>
                  </div>

                  <div className="pedido-card-itens-caixa">
                    <strong>Itens ({pedido.itens?.length || 0}):</strong>
                    <div className="itens-list-caixa">
                      {pedido.itens?.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="item-pedido-caixa">
                          <span>{item.quantidade}x {item.produto?.nome || 'Produto'}</span>
                          <span>R$ {item.subtotal?.toFixed(2) || '0.00'}</span>
                        </div>
                      ))}
                      {pedido.itens?.length > 3 && (
                        <div className="item-pedido-caixa">
                          <span>+ {pedido.itens.length - 3} item(s)</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pedido-card-total-caixa">
                    <span>Total:</span>
                    <strong>R$ {pedido.total?.toFixed(2) || '0.00'}</strong>
                  </div>

                  <div className="pedido-card-acoes-caixa" style={{ padding: '1rem', background: '#fff', borderRadius: '0.5rem' }}>
                    {/* ✅ Dropdown de Status - IGUAL AO ADMIN */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                        📋 Status do Pedido:
                      </label>
                      <select
                        value={pedido.status}
                        onChange={async (e) => {
                          const novoStatus = e.target.value;
                          if (window.confirm(`Deseja realmente alterar o status para "${novoStatus}"?`)) {
                            try {
                              await handleAtualizarStatusPedido(pedido._id, novoStatus);
                              toast.success(`Status alterado para "${novoStatus}"!`);
                            } catch (error) {
                              toast.error(error.response?.data?.message || 'Erro ao atualizar status');
                            }
                          } else {
                              // Resetar select para o valor original
                              e.target.value = pedido.status;
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          borderRadius: '0.375rem',
                          border: '2px solid #e5e7eb',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          background: '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="Pendente">⏳ Pendente</option>
                        <option value="Confirmado">✅ Confirmado</option>
                        <option value="Em preparo">👨‍🍳 Em preparo</option>
                        <option value="Pronto">📦 Pronto</option>
                        {pedido.tipoEntrega === 'delivery' && (
                          <>
                            <option value="Saiu para entrega">🚚 Saiu para entrega</option>
                            <option value="Chegou no endereço">📍 Chegou no endereço</option>
                          </>
                        )}
                        <option value="Entregue">✅ Entregue</option>
                        <option value="Cancelado">❌ Cancelado</option>
                      </select>
                    </div>

                    {/* ✅ Botões Rápidos de Status (Próximo Status) */}
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '0.5rem', 
                      marginBottom: '0.75rem',
                      padding: '0.75rem',
                      background: '#f9fafb',
                      borderRadius: '0.375rem',
                      border: '1px solid #e5e7eb'
                    }}>
                      <strong style={{ width: '100%', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                        ⚡ Ações Rápidas:
                      </strong>
                      
                      {/* Função para obter próximo status */}
                      {(() => {
                        const getProximoStatus = (status) => {
                          const fluxo = {
                            'Pendente': 'Confirmado',
                            'Confirmado': 'Em preparo',
                            'Em preparo': 'Pronto',
                            'Pronto': pedido.tipoEntrega === 'delivery' ? 'Saiu para entrega' : null,
                            'Saiu para entrega': 'Chegou no endereço',
                            'Chegou no endereço': 'Entregue'
                          };
                          return fluxo[status];
                        };
                        
                        const proximoStatus = getProximoStatus(pedido.status);
                        
                        if (!proximoStatus) return null;
                        
                        const getStatusInfo = (status) => {
                          const info = {
                            'Confirmado': { icon: CheckCircle, color: '#3b82f6', label: 'Confirmar' },
                            'Em preparo': { icon: ChefHat, color: '#f59e0b', label: 'Em Preparo' },
                            'Pronto': { icon: Package, color: '#10b981', label: 'Pronto' },
                            'Saiu para entrega': { icon: Truck, color: '#3b82f6', label: 'Saiu p/ Entrega' },
                            'Chegou no endereço': { icon: MapPin, color: '#8b5cf6', label: 'Chegou' },
                            'Entregue': { icon: CheckCircle, color: '#10b981', label: 'Entregue' }
                          };
                          return info[status] || { icon: CheckCircle, color: '#6b7280', label: status };
                        };
                        
                        const statusInfo = getProximoStatus(pedido.status) ? getStatusInfo(proximoStatus) : null;
                        const StatusIcon = statusInfo?.icon || CheckCircle;
                        
                        return proximoStatus ? (
                          <button
                            style={{ 
                              background: statusInfo.color, 
                              color: 'white',
                              border: 'none',
                              padding: '0.5rem 1rem',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              flex: '1 1 auto',
                              minWidth: 'fit-content'
                            }}
                            onClick={async () => {
                              if (window.confirm(`Avançar status para "${proximoStatus}"?`)) {
                                try {
                                  await handleAtualizarStatusPedido(pedido._id, proximoStatus);
                                  toast.success(`Status alterado para "${proximoStatus}"!`);
                                } catch (error) {
                                  toast.error(error.response?.data?.message || 'Erro ao atualizar status');
                                }
                              }
                            }}
                          >
                            <StatusIcon size={16} />
                            {statusInfo.label}
                          </button>
                        ) : null;
                      })()}
                    </div>
                    
                    {/* ✅ Botão de Pagamento */}
                    <div style={{ 
                      padding: '0.75rem',
                      background: pedido.statusPagamento === 'Pago' ? '#d1fae5' : '#fef3c7',
                      borderRadius: '0.5rem',
                      border: `1px solid ${pedido.statusPagamento === 'Pago' ? '#10b981' : '#f59e0b'}`
                    }}>
                      {pedido.statusPagamento !== 'Pago' ? (
                        <button
                          className="btn-marcar-pago-caixa"
                          style={{
                            width: '100%',
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            padding: '0.75rem 1rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                          }}
                          onClick={() => {
                            setPedidoSelecionadoPagamento(pedido);
                            setFormaPagamentoPedido(pedido.formaPagamento || 'dinheiro');
                            setModalPagamentoPedido(true);
                          }}
                        >
                          <CheckCircle size={18} />
                          Marcar como Pago
                        </button>
                      ) : (
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          color: '#10b981',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          <CheckCircle size={18} />
                          ✅ Pago
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-pedidos">
              <ShoppingCart size={48} />
              <p>Nenhum pedido encontrado</p>
            </div>
          )}
        </div>
      ) : abaAtiva === 'marmitex' ? (
        <div className="pedidos-section-caixa">
          <div className="pedidos-header-caixa">
            <h3>🍱 Marmitex</h3>
          </div>
          <div className="card" style={{ padding: '1rem' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
              <h4 style={{ marginTop: 0 }}>Carne extra</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Preço (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={Number(carneExtraForm?.preco || 0)}
                    onChange={(e) => setCarneExtraForm((prev) => ({ ...prev, preco: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={carneExtraForm?.disponivel !== false}
                      onChange={(e) => setCarneExtraForm((prev) => ({ ...prev, disponivel: e.target.checked }))}
                    />
                    {' '}Disponível
                  </label>
                </div>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!estabelecimento?._id) {
                    toast.error('Estabelecimento não carregado');
                    return;
                  }
                  salvarCarneExtraMutation.mutate({
                    estabelecimento: estabelecimento._id,
                    preco: Number(carneExtraForm?.preco) || 0,
                    disponivel: carneExtraForm?.disponivel !== false
                  });
                }}
                disabled={salvarCarneExtraMutation.isLoading || !estabelecimento?._id}
              >
                {salvarCarneExtraMutation.isLoading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem', marginTop: '1rem' }}>
              <h4 style={{ marginTop: 0 }}>Carnes do Dia</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    value={String(novaCarneDoDia?.nome || '')}
                    onChange={(e) => setNovaCarneDoDia((prev) => ({ ...prev, nome: e.target.value }))}
                    placeholder="Ex: Frango, Bovina"
                  />
                </div>
                <div className="form-group">
                  <label>Ordem</label>
                  <input
                    type="number"
                    value={Number(novaCarneDoDia?.ordem || 0)}
                    onChange={(e) => setNovaCarneDoDia((prev) => ({ ...prev, ordem: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={novaCarneDoDia?.disponivel !== false}
                      onChange={(e) => setNovaCarneDoDia((prev) => ({ ...prev, disponivel: e.target.checked }))}
                    />
                    {' '}Disponível
                  </label>
                </div>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!estabelecimento?._id) {
                    toast.error('Estabelecimento não carregado');
                    return;
                  }
                  const nome = String(novaCarneDoDia?.nome || '').trim();
                  if (nome.length < 2) {
                    toast.error('Nome deve ter no mínimo 2 caracteres');
                    return;
                  }
                  criarCarneDoDiaMutation.mutate({
                    nome,
                    preco: 0,
                    categoria: CATEGORIA_CARNES_DO_DIA,
                    estabelecimento: estabelecimento._id,
                    disponivel: novaCarneDoDia?.disponivel !== false,
                    ordem: Number(novaCarneDoDia?.ordem) || 0
                  });
                }}
                disabled={criarCarneDoDiaMutation.isLoading || !estabelecimento?._id}
              >
                {criarCarneDoDiaMutation.isLoading ? 'Criando...' : 'Adicionar'}
              </button>

              <div style={{ marginTop: '1rem' }}>
                {(Array.isArray(carnesDoDiaAdmin) ? carnesDoDiaAdmin : []).length === 0 ? (
                  <p style={{ margin: 0, opacity: 0.8 }}>Nenhuma carne cadastrada.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '28px 1fr 90px auto',
                        gap: '0.5rem',
                        alignItems: 'center',
                        fontSize: '0.85rem',
                        color: '#6B7280',
                        padding: '0 0.25rem'
                      }}
                    >
                      <div />
                      <div>Nome</div>
                      <div>Ordem</div>
                      <div style={{ textAlign: 'right' }}>Ações</div>
                    </div>
                    {(Array.isArray(carnesDoDiaAdmin) ? carnesDoDiaAdmin : []).map((carne) => {
                      const editando = carneDoDiaEditando && String(carneDoDiaEditando._id) === String(carne._id);
                      const draft = editando ? carneDoDiaEditando : carne;

                      return (
                        <div
                          key={carne._id}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            background: '#ffffff',
                            display: 'grid',
                            gridTemplateColumns: '28px 1fr 90px auto',
                            gap: '0.5rem',
                            alignItems: 'center'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={draft?.disponivel !== false}
                            onChange={(e) => {
                              if (editando) {
                                setCarneDoDiaEditando((prev) => ({ ...prev, disponivel: e.target.checked }));
                                return;
                              }
                              const nome = String(carne?.nome || '').trim();
                              if (nome.length < 2) {
                                toast.error('Nome deve ter no mínimo 2 caracteres');
                                return;
                              }
                              atualizarCarneDoDiaMutation.mutate({
                                id: carne._id,
                                payload: {
                                  nome,
                                  categoria: CATEGORIA_CARNES_DO_DIA,
                                  preco: 0,
                                  disponivel: e.target.checked,
                                  ordem: Number(carne?.ordem) || 0
                                }
                              });
                            }}
                            disabled={atualizarCarneDoDiaMutation.isLoading}
                          />

                          <input
                            type="text"
                            value={String(draft?.nome || '')}
                            onChange={(e) => {
                              if (!editando) return;
                              setCarneDoDiaEditando((prev) => ({ ...prev, nome: e.target.value }));
                            }}
                            disabled={!editando}
                            style={{
                              fontSize: '1rem',
                              padding: '0.5rem 0.6rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.5rem'
                            }}
                          />

                          <input
                            type="number"
                            value={Number(draft?.ordem || 0)}
                            onChange={(e) => {
                              if (!editando) return;
                              setCarneDoDiaEditando((prev) => ({ ...prev, ordem: parseInt(e.target.value) || 0 }));
                            }}
                            disabled={!editando}
                            style={{
                              fontSize: '1rem',
                              padding: '0.5rem 0.6rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.5rem'
                            }}
                          />

                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                            {!editando ? (
                              <button className="btn btn-secondary" onClick={() => setCarneDoDiaEditando({ ...carne })}>
                                Editar
                              </button>
                            ) : (
                              <>
                                <button
                                  className="btn btn-primary"
                                  onClick={() => {
                                    const nome = String(carneDoDiaEditando?.nome || '').trim();
                                    if (nome.length < 2) {
                                      toast.error('Nome deve ter no mínimo 2 caracteres');
                                      return;
                                    }
                                    atualizarCarneDoDiaMutation.mutate({
                                      id: carne._id,
                                      payload: {
                                        nome,
                                        categoria: CATEGORIA_CARNES_DO_DIA,
                                        preco: 0,
                                        disponivel: carneDoDiaEditando?.disponivel !== false,
                                        ordem: Number(carneDoDiaEditando?.ordem) || 0
                                      }
                                    });
                                  }}
                                  disabled={atualizarCarneDoDiaMutation.isLoading}
                                >
                                  {atualizarCarneDoDiaMutation.isLoading ? 'Salvando...' : 'Salvar'}
                                </button>
                                <button className="btn btn-secondary" onClick={() => setCarneDoDiaEditando(null)}>
                                  Cancelar
                                </button>
                              </>
                            )}

                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                if (window.confirm(`Remover carne do dia "${carne.nome}"?`)) {
                                  deletarCarneDoDiaMutation.mutate(carne._id);
                                }
                              }}
                              disabled={deletarCarneDoDiaMutation.isLoading}
                            >
                              Remover
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem', marginTop: '1rem' }}>
              <h4 style={{ marginTop: 0 }}>Guarnições (por produto Marmitex)</h4>

              {(Array.isArray(produtosMarmitexAdmin) ? produtosMarmitexAdmin : []).length === 0 ? (
                <p style={{ margin: 0, opacity: 0.8 }}>Nenhum produto Marmitex encontrado.</p>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Produto</label>
                      <select
                        value={marmitexProdutoSelecionadoId}
                        onChange={(e) => setMarmitexProdutoSelecionadoId(e.target.value)}
                      >
                        {(Array.isArray(produtosMarmitexAdmin) ? produtosMarmitexAdmin : []).map((p) => (
                          <option key={p._id} value={p._id}>{p.nome}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          const atual = Array.isArray(marmitexGuarnicoesDraft) ? marmitexGuarnicoesDraft : [];
                          setMarmitexGuarnicoesDraft([...atual, { nome: '', preco: 0, disponivel: true }]);
                        }}
                        disabled={!marmitexProdutoSelecionadoId}
                      >
                        + Adicionar
                      </button>
                    </div>
                  </div>

                  {(Array.isArray(marmitexGuarnicoesDraft) ? marmitexGuarnicoesDraft : []).length === 0 ? (
                    <p style={{ margin: 0, opacity: 0.8 }}>Nenhuma guarnição cadastrada neste produto.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {(Array.isArray(marmitexGuarnicoesDraft) ? marmitexGuarnicoesDraft : []).map((g, idx) => (
                        <div
                          key={idx}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            background: '#ffffff',
                            display: 'grid',
                            gridTemplateColumns: '28px 1fr 110px auto',
                            gap: '0.5rem',
                            alignItems: 'center'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={g?.disponivel !== false}
                            onChange={(e) => {
                              const atual = Array.isArray(marmitexGuarnicoesDraft) ? [...marmitexGuarnicoesDraft] : [];
                              atual[idx] = { ...atual[idx], disponivel: e.target.checked };
                              setMarmitexGuarnicoesDraft(atual);
                            }}
                          />

                          <input
                            type="text"
                            value={String(g?.nome || '')}
                            onChange={(e) => {
                              const atual = Array.isArray(marmitexGuarnicoesDraft) ? [...marmitexGuarnicoesDraft] : [];
                              atual[idx] = { ...atual[idx], nome: e.target.value };
                              setMarmitexGuarnicoesDraft(atual);
                            }}
                            placeholder="Ex: Arroz, Feijão, Macarrão"
                            style={{
                              fontSize: '1rem',
                              padding: '0.5rem 0.6rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.5rem'
                            }}
                          />

                          <input
                            type="number"
                            step="0.01"
                            value={Number(g?.preco || 0)}
                            onChange={(e) => {
                              const atual = Array.isArray(marmitexGuarnicoesDraft) ? [...marmitexGuarnicoesDraft] : [];
                              atual[idx] = { ...atual[idx], preco: parseFloat(e.target.value) || 0 };
                              setMarmitexGuarnicoesDraft(atual);
                            }}
                            style={{
                              fontSize: '1rem',
                              padding: '0.5rem 0.6rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.5rem'
                            }}
                          />

                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                const atual = Array.isArray(marmitexGuarnicoesDraft) ? marmitexGuarnicoesDraft : [];
                                setMarmitexGuarnicoesDraft(atual.filter((_, i) => i !== idx));
                              }}
                            >
                              Remover
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    className="btn btn-primary"
                    style={{ marginTop: '0.75rem' }}
                    onClick={() => {
                      if (!marmitexProdutoSelecionadoId) {
                        toast.error('Selecione um produto');
                        return;
                      }
                      const adicionais = (Array.isArray(marmitexGuarnicoesDraft) ? marmitexGuarnicoesDraft : [])
                        .filter((a) => a && String(a.nome || '').trim() !== '')
                        .map((a) => ({
                          nome: String(a.nome || '').trim(),
                          preco: Number(a.preco) || 0,
                          disponivel: a?.disponivel !== false
                        }));
                      salvarGuarnicoesProdutoMutation.mutate({ produtoId: marmitexProdutoSelecionadoId, adicionais });
                    }}
                    disabled={salvarGuarnicoesProdutoMutation.isLoading || !marmitexProdutoSelecionadoId}
                  >
                    {salvarGuarnicoesProdutoMutation.isLoading ? 'Salvando...' : 'Salvar guarnições'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="caixa-layout-premium">
        {/* Produtos - Área Principal */}
        <div className="produtos-section-premium">
          <div className="section-header-compact">
            <h3>📦 Produtos ({produtos?.length || 0})</h3>
            <div className="search-controls">
              <div className="search-produtos-premium">
                <Search size={18} />
                <input type="text" placeholder="Buscar..." />
              </div>
              <button 
                onClick={refreshProdutos}
                className="btn-refresh-produtos"
                title="Atualizar produtos"
              >
                🔄
              </button>
            </div>
          </div>
          
          {/* Abas de Categorias */}
          {categorias && categorias.length > 0 && (
            <div className="categorias-tabs-pdv">
              <button
                className={`categoria-tab-pdv ${!categoriaAtiva ? 'active' : ''}`}
                onClick={() => setCategoriaAtiva(null)}
              >
                Todos ({produtos?.length || 0})
              </button>
              {categorias.map(categoria => (
                <button
                  key={categoria}
                  className={`categoria-tab-pdv ${categoriaAtiva === categoria ? 'active' : ''}`}
                  onClick={() => setCategoriaAtiva(categoria)}
                >
                  {categoria} ({produtosPorCategoria[categoria]?.length || 0})
                </button>
              ))}
            </div>
          )}
          
          <div className="produtos-grid-premium">
            {loadingProdutos ? (
              <div className="loading-produtos" style={{ gridColumn: '1 / -1' }}>
                <p>Carregando produtos...</p>
              </div>
            ) : produtosError ? (
              <div className="empty-produtos" style={{ gridColumn: '1 / -1' }}>
                <Package size={48} />
                <p>Erro ao carregar produtos</p>
                <span>{produtosError.message || 'Tente recarregar a página'}</span>
              </div>
            ) : !estabelecimento?._id ? (
              <div className="empty-produtos" style={{ gridColumn: '1 / -1' }}>
                <Package size={48} />
                <p>Carregando estabelecimento...</p>
              </div>
            ) : produtos && Array.isArray(produtos) && produtos.length > 0 ? (
              (() => {
                // Filtrar produtos por categoria
                const produtosFiltrados = categoriaAtiva 
                  ? produtosPorCategoria[categoriaAtiva] || []
                  : produtos;
                
                return produtosFiltrados.length > 0 ? (
                  produtosFiltrados.map((produto, index) => {
                    const preco = produto.precoPromocional ?? produto.preco ?? 0;
                    const precoNum = typeof preco === 'number' ? preco : parseFloat(preco) || 0;
                    const temEstoque = produto.estoque && produto.estoque.quantidade > 0;
                    
                    return (
                      <div
                        key={produto._id || index}
                        className="produto-card-premium"
                        onClick={() => adicionarProduto(produto)}
                      >
                        <div className="produto-info-premium">
                          <h4>{produto.nome}</h4>
                          <p className="produto-preco-premium">
                            {precoNum.toFixed(2)}
                          </p>
                          {temEstoque && (
                            <span className="produto-estoque-badge">
                              📦 {produto.estoque.quantidade} un
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-produtos" style={{ gridColumn: '1 / -1' }}>
                    <Package size={48} />
                    <p>Nenhum produto nesta categoria</p>
                  </div>
                );
              })()
            ) : (
              <div className="empty-produtos" style={{ gridColumn: '1 / -1' }}>
                <Package size={48} />
                <p>Nenhum produto disponível</p>
                <span>Adicione produtos no menu Cardápio</span>
              </div>
            )}
          </div>
        </div>

        {/* Carrinho Premium */}
        <div className="carrinho-section-premium">
          <div className="section-header-compact">
            <h3>🛒 Carrinho</h3>
            <span className="badge-tipo-venda">{tipoEntrega === 'delivery' ? 'DELIVERY' : 'BALCÃO'}</span>
          </div>
          
          <div className="carrinho-items-premium">
            {carrinho.length === 0 ? (
              <div className="carrinho-vazio">
                <ShoppingBag size={40} />
                <p>Carrinho vazio</p>
                <span>Adicione produtos</span>
              </div>
            ) : (
              carrinho.map((item, idx) => (
                <div key={idx} className="carrinho-item-premium">
                  <div className="item-info-premium">
                    <strong>{item.quantidade}x</strong>
                    <div>
                      <span>{item.produto.nome}</span>
                      {item.clienteItem && (
                        <small className="item-obs-carrinho">👤 {item.clienteItem}</small>
                      )}
                      {item.observacoes && (
                        <small className="item-obs-carrinho">📝 {item.observacoes}</small>
                      )}
                      {item.acrescimos && item.acrescimos.length > 0 && (
                        <small className="item-acrescimos-carrinho">
                          ➕ {item.acrescimos.map(a => `${Math.min(20, Math.max(1, Number(a?.quantidade || 1)))}x ${a.nome}`).join(', ')}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="item-actions-premium">
                    <span className="item-preco-premium">
                      R$ {(item.subtotal || (item.precoUnitario * item.quantidade)).toFixed(2)}
                    </span>
                    <button 
                      className="btn-remove-item"
                      onClick={() => setCarrinho(carrinho.filter((_, i) => i !== idx))}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {carrinho.length > 0 && (
            <div className="carrinho-footer-premium">
              {/* ✅ Campos de Cliente para Produção */}
              <div className="cliente-info-carrinho" style={{ 
                padding: '1rem', 
                background: '#f9fafb', 
                borderRadius: '0.5rem', 
                marginBottom: '1rem',
                border: (!cliente.nome || !cliente.telefone) ? '2px solid #fca5a5' : '2px solid #e5e7eb',
                maxHeight: '250px',
                overflowY: 'auto',
                overflowX: 'hidden',
                flexShrink: 0
              }}>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
                  📋 Dados do Cliente (para produção)
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {/* Tipo de Entrega - Apenas Retirada no PDV */}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: '#374151' }}>
                      Tipo de Entrega
                    </label>
                    <div style={{ 
                      padding: '0.75rem', 
                      border: '2px solid #10b981', 
                      borderRadius: '0.375rem', 
                      background: '#d1fae5', 
                      color: '#065f46',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textAlign: 'center'
                    }}>
                      🏪 Retirada (PDV)
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>
                      Nome do Cliente {formaPagamento.tipo === 'fiado' && <span style={{ color: '#ef4444' }}>*</span>}
                    </label>
                    <input
                      type="text"
                      value={cliente.nome}
                      onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                      placeholder="Nome do cliente"
                      required={formaPagamento.tipo === 'fiado'}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '0.375rem',
                        fontSize: '0.9375rem',
                        borderColor: formaPagamento.tipo === 'fiado' && !cliente.nome ? '#ef4444' : '#e5e7eb'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>
                      Telefone {formaPagamento.tipo === 'fiado' && <span style={{ color: '#ef4444' }}>*</span>}
                    </label>
                    <input
                      type="text"
                      value={cliente.telefone}
                      onChange={(e) => {
                        const telefone = e.target.value;
                        setCliente({ ...cliente, telefone });
                        // ✅ Buscar cliente automaticamente quando telefone tiver 10+ caracteres
                        if (telefone.length >= 10) {
                          buscarClientePorTelefone(telefone);
                        }
                      }}
                      placeholder="(00) 00000-0000"
                      required={formaPagamento.tipo === 'fiado'}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '0.375rem',
                        fontSize: '0.9375rem',
                        borderColor: formaPagamento.tipo === 'fiado' && (!cliente.telefone || cliente.telefone.replace(/\D/g, '').length < 10) ? '#ef4444' : '#e5e7eb'
                      }}
                      disabled={buscandoCliente}
                    />
                    {buscandoCliente && <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>Buscando cliente...</small>}
                    {formaPagamento.tipo === 'fiado' && cliente.telefone && cliente.telefone.replace(/\D/g, '').length < 10 && (
                      <small style={{ color: '#ef4444', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>Telefone inválido</small>
                    )}
                  </div>
                  
                  {/* Campos de Endereço (apenas se for delivery) */}
                  {tipoEntrega === 'delivery' && (
                    <>
                      <div style={{ marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                        <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                          📍 Endereço de Entrega
                        </h5>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>
                              CEP {buscandoCEP && <span style={{ color: '#3b82f6', fontSize: '0.7rem' }}>(Buscando...)</span>}
                            </label>
                            <input
                              type="text"
                              value={enderecoEntrega.cep}
                              onChange={(e) => {
                                const cep = e.target.value.replace(/\D/g, '');
                                const cepFormatado = cep.replace(/(\d{5})(\d{3})/, '$1-$2');
                                setEnderecoEntrega({ ...enderecoEntrega, cep: cepFormatado });
                                if (cep.length === 8) {
                                  buscarCEP(cep);
                                }
                              }}
                              placeholder="00000-000"
                              maxLength={9}
                              disabled={buscandoCEP}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                opacity: buscandoCEP ? 0.6 : 1
                              }}
                            />
                            {buscandoCEP && (
                              <small style={{ color: '#3b82f6', fontSize: '0.7rem', display: 'block', marginTop: '0.25rem' }}>
                                Buscando endereço...
                              </small>
                            )}
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>
                              Rua <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={enderecoEntrega.rua}
                              onChange={(e) => setEnderecoEntrega({ ...enderecoEntrega, rua: e.target.value })}
                              placeholder="Nome da rua"
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem'
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>
                                Número <span style={{ color: '#ef4444' }}>*</span>
                              </label>
                              <input
                                type="text"
                                value={enderecoEntrega.numero}
                                onChange={(e) => setEnderecoEntrega({ ...enderecoEntrega, numero: e.target.value })}
                                placeholder="123"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '2px solid #e5e7eb',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                            </div>
                            <div style={{ flex: 2 }}>
                              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>
                                Complemento
                              </label>
                              <input
                                type="text"
                                value={enderecoEntrega.complemento}
                                onChange={(e) => setEnderecoEntrega({ ...enderecoEntrega, complemento: e.target.value })}
                                placeholder="Apto, Bloco..."
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '2px solid #e5e7eb',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                            </div>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>
                              Bairro <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                              type="text"
                              value={enderecoEntrega.bairro}
                              onChange={(e) => setEnderecoEntrega({ ...enderecoEntrega, bairro: e.target.value })}
                              placeholder="Nome do bairro"
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>
                              Referência
                            </label>
                            <input
                              type="text"
                              value={enderecoEntrega.referencia}
                              onChange={(e) => setEnderecoEntrega({ ...enderecoEntrega, referencia: e.target.value })}
                              placeholder="Ponto de referência"
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {formaPagamento.tipo === 'fiado' && (!cliente.nome || !cliente.telefone) && (
                    <small style={{ color: '#ef4444', fontSize: '0.75rem', display: 'block' }}>
                      ⚠️ Preencha nome e telefone para enviar para produção
                    </small>
                  )}
                  {tipoEntrega === 'delivery' && (!enderecoEntrega.rua || !enderecoEntrega.numero || !enderecoEntrega.bairro) && (
                    <small style={{ color: '#ef4444', fontSize: '0.75rem', display: 'block' }}>
                      ⚠️ Preencha rua, número e bairro do endereço de entrega
                    </small>
                  )}
                </div>
              </div>

              <div className="total-premium">
                <span>Total</span>
                <strong>R$ {calcularTotal().toFixed(2)}</strong>
              </div>
              <div className="carrinho-buttons-premium">
                <button
                  className="btn-enviar-producao-premium"
                  onClick={handleEnviarProducao}
                  disabled={
                    criarComandaProducaoMutation.isLoading || 
                    criarPedidoDeliveryMutation.isLoading || 
                    (formaPagamento.tipo === 'fiado' && (!cliente.nome || !cliente.telefone)) ||
                    (tipoEntrega === 'delivery' && (!enderecoEntrega.rua || !enderecoEntrega.numero || !enderecoEntrega.bairro))
                  }
                  title={
                    formaPagamento.tipo === 'fiado' && (!cliente.nome || !cliente.telefone) 
                      ? 'Informe nome e telefone do cliente' 
                      : tipoEntrega === 'delivery' && (!enderecoEntrega.rua || !enderecoEntrega.numero || !enderecoEntrega.bairro || !enderecoEntrega.cep)
                      ? 'Preencha todos os campos do endereço'
                      : tipoEntrega === 'delivery'
                      ? 'Criar pedido de delivery e enviar para produção'
                      : 'Criar comanda e enviar para produção'
                  }
                >
                  <Send size={18} />
                  {(criarComandaProducaoMutation.isLoading || criarPedidoDeliveryMutation.isLoading) 
                    ? 'Enviando...' 
                    : 'Enviar para Produção'}
                </button>
                <button
                  className="btn-finalizar-premium"
                  onClick={() => setModalVenda(true)}
                >
                  <CreditCard size={18} />
                  Finalizar Venda
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {modalVenda && (
        <div className="modal-overlay" onClick={() => {
          setModalVenda(false);
          // ✅ Resetar estados ao fechar modal
          setPagamentosSequenciaisVenda([]);
          setNovoPagamentoVenda({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
          setModoDividirPagamentoVenda(false);
        }}>
          <div className="modal-content modal-venda" onClick={(e) => e.stopPropagation()}>
            <h3>{abaAtiva === 'mesas' ? 'Pagar Comanda' : 'Finalizar Venda de Balcão'}</h3>
            <p className="badge-modal-balcao">
              {abaAtiva === 'mesas' ? `Comanda ${(comandaSelecionadaModal || comandaMesaData)?.mesa}` : 'Venda direta no PDV'}
            </p>
            {abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData) && (
              <div className="comanda-resumo-modal">
                <p><strong>Cliente:</strong> {getNomeCliente((comandaSelecionadaModal || comandaMesaData)?.cliente)}</p>
                <p><strong>Total:</strong> R$ {(comandaSelecionadaModal || comandaMesaData)?.total?.toFixed(2) || '0.00'}</p>
              </div>
            )}
            <div className="form-group">
              <label>
                Nome do Cliente {formaPagamento.tipo === 'fiado' && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="text"
                value={cliente.nome}
                onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                placeholder="Nome do cliente"
                required={formaPagamento.tipo === 'fiado'}
                style={formaPagamento.tipo === 'fiado' && !cliente.nome ? { borderColor: '#ef4444' } : {}}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>CPF</label>
                <input
                  type="text"
                  value={cliente.cpf}
                  onChange={(e) => setCliente({ ...cliente, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                />
              </div>
              <div className="form-group">
                <label>
                  Telefone {formaPagamento.tipo === 'fiado' && <span style={{ color: '#ef4444' }}>*</span>}
                  {formaPagamento.tipo !== 'fiado' && <span style={{ fontSize: '0.75rem', opacity: 0.7 }}> (opcional - para cadastro automático)</span>}
                </label>
                <input
                  type="text"
                  value={cliente.telefone}
                  onChange={(e) => {
                    const telefone = e.target.value;
                    setCliente({ ...cliente, telefone });
                    if (telefone.length >= 10) {
                      buscarClientePorTelefone(telefone);
                    }
                  }}
                  placeholder="(00) 00000-0000"
                  disabled={buscandoCliente}
                  required={formaPagamento.tipo === 'fiado'}
                  style={formaPagamento.tipo === 'fiado' && (!cliente.telefone || cliente.telefone.replace(/\D/g, '').length < 10) ? { borderColor: '#ef4444' } : {}}
                />
                {buscandoCliente && <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>Buscando cliente...</small>}
                {formaPagamento.tipo === 'fiado' && cliente.telefone && cliente.telefone.replace(/\D/g, '').length < 10 && (
                  <small style={{ color: '#ef4444', fontSize: '0.75rem', display: 'block', marginTop: '0.25rem' }}>Telefone inválido</small>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Forma de Pagamento</label>
              <select
                value={formaPagamento.tipo}
                onChange={(e) => {
                  const novoTipo = e.target.value;
                  const total = abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)
                    ? (comandaSelecionadaModal || comandaMesaData).total 
                    : calcularTotal();
                  
                  setFormaPagamento({
                    ...formaPagamento,
                    tipo: novoTipo,
                    valor: novoTipo === 'dinheiro' ? (formaPagamento.valor || total) : total,
                    troco: 0
                  });
                }}
              >
                <option value="dinheiro">Dinheiro</option>
                <option value="pix">PIX</option>
                <option value="cartao_credito">Cartão de Crédito</option>
                <option value="cartao_debito">Cartão de Débito</option>
                <option value="vale_refeicao">Vale Refeição</option>
                <option value="fiado">Fiado</option>
              </select>
            </div>
            {formaPagamento.tipo === 'dinheiro' && (
              <div className="form-group">
                <label>Valor Recebido</label>
                <input
                  type="number"
                  step="0.01"
                  value={formaPagamento.valor}
                  onChange={(e) => {
                    const valor = parseFloat(e.target.value) || 0;
                    const total = abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)
                      ? (comandaSelecionadaModal || comandaMesaData).total 
                      : calcularTotal();
                    setFormaPagamento({
                      ...formaPagamento,
                      valor,
                      troco: Math.max(0, valor - total)
                    });
                  }}
                />
                {formaPagamento.troco > 0 && (
                  <p className="troco">Troco: R$ {formaPagamento.troco.toFixed(2)}</p>
                )}
              </div>
            )}
            {formaPagamento.tipo === 'fiado' && (
              <div className="form-group">
                <label>
                  Data de Vencimento {formaPagamento.tipo === 'fiado' && <span style={{ color: '#ef4444' }}>*</span>}
                </label>
                <input
                  type="date"
                  value={dataVencimentoFiado}
                  onChange={(e) => setDataVencimentoFiado(e.target.value)}
                  required={formaPagamento.tipo === 'fiado'}
                  min={new Date().toISOString().split('T')[0]}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd' }}
                />
                <small style={{ fontSize: '0.75rem', opacity: 0.7, display: 'block', marginTop: '0.25rem' }}>
                  Data em que o cliente deve pagar a conta
                </small>
              </div>
            )}

            {/* ✅ NOVO: Lista de pagamentos sequenciais para comandas */}
            {pagamentosSequenciaisVenda.length > 0 && (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f3f4f6', borderRadius: '0.5rem' }}>
                <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>Pagamentos Adicionados:</h4>
                {pagamentosSequenciaisVenda.map((pag, idx) => {
                  const formaPagamentoTexto = {
                    'dinheiro': '💵 Dinheiro',
                    'pix': '📱 PIX',
                    'cartao_credito': '💳 Cartão de Crédito',
                    'cartao_debito': '💳 Cartão de Débito',
                    'vale_refeicao': '🍽️ Vale Refeição'
                  }[pag.tipo] || pag.tipo;
                  
                  return (
                    <div key={idx} style={{ 
                      background: 'white', 
                      padding: '0.75rem', 
                      borderRadius: '0.5rem', 
                      marginBottom: '0.5rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>
                          {formaPagamentoTexto}: R$ {pag.valor.toFixed(2)}
                        </p>
                        {pag.tipo === 'dinheiro' && pag.troco > 0 && (
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#059669' }}>
                            Troco: R$ {pag.troco.toFixed(2)}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => setPagamentosSequenciaisVenda(pagamentosSequenciaisVenda.filter((_, i) => i !== idx))}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          cursor: 'pointer',
                          fontSize: '0.75rem'
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ✅ NOVO: Opção para dividir pagamento - SEMPRE VISÍVEL quando não está no modo dividir */}
            {!modoDividirPagamentoVenda && pagamentosSequenciaisVenda.length === 0 && formaPagamento.tipo !== 'fiado' && (
              <div style={{ 
                background: '#dbeafe', 
                padding: '1rem', 
                borderRadius: '0.5rem', 
                marginBottom: '1rem',
                border: '2px solid #3b82f6',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => {
                const total = abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)
                  ? (comandaSelecionadaModal || comandaMesaData).total 
                  : calcularTotal();
                setModoDividirPagamentoVenda(true);
                setNovoPagamentoVenda({ 
                  tipo: 'dinheiro', 
                  valor: total, 
                  valorRecebido: total, 
                  troco: 0 
                });
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#bfdbfe'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#dbeafe'}
              >
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af', fontWeight: 600, textAlign: 'center' }}>
                  💡 Clique aqui para dividir o pagamento em múltiplas formas
                </p>
              </div>
            )}

            {/* ✅ NOVO: Campo para adicionar pagamentos sequenciais */}
            {(modoDividirPagamentoVenda || pagamentosSequenciaisVenda.length > 0) && formaPagamento.tipo !== 'fiado' && (
              <div style={{ 
                border: '2px dashed #d1d5db', 
                padding: '1rem', 
                borderRadius: '0.5rem', 
                marginBottom: '1rem',
                background: pagamentosSequenciaisVenda.length > 0 ? '#fef3c7' : '#f0f9ff'
              }}>
                <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>
                  {pagamentosSequenciaisVenda.length > 0 ? '➕ Adicionar Outro Pagamento' : '💳 Adicionar Pagamento'}
                </h4>
                
                <div className="form-group">
                  <label>Forma de Pagamento <span style={{ color: '#ef4444' }}>*</span></label>
                  <select
                    value={novoPagamentoVenda.tipo}
                    onChange={(e) => {
                      const novoTipo = e.target.value;
                      const total = abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)
                        ? (comandaSelecionadaModal || comandaMesaData).total 
                        : calcularTotal();
                      const valorPago = pagamentosSequenciaisVenda.reduce((acc, p) => acc + p.valor, 0);
                      const valorRestante = total - valorPago;
                      setNovoPagamentoVenda({
                        tipo: novoTipo,
                        valor: novoPagamentoVenda.valor || valorRestante,
                        valorRecebido: novoTipo === 'dinheiro' ? (novoPagamentoVenda.valorRecebido || novoPagamentoVenda.valor || valorRestante) : (novoPagamentoVenda.valor || valorRestante),
                        troco: 0
                      });
                    }}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                  >
                    <option value="dinheiro">💵 Dinheiro</option>
                    <option value="pix">📱 PIX</option>
                    <option value="cartao_credito">💳 Cartão de Crédito</option>
                    <option value="cartao_debito">💳 Cartão de Débito</option>
                    <option value="vale_refeicao">🍽️ Vale Refeição</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Valor <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={novoPagamentoVenda.valor}
                    onChange={(e) => {
                      const valor = parseFloat(e.target.value) || 0;
                      setNovoPagamentoVenda({
                        ...novoPagamentoVenda,
                        valor: valor,
                        valorRecebido: novoPagamentoVenda.tipo === 'dinheiro' ? (novoPagamentoVenda.valorRecebido || valor) : valor
                      });
                    }}
                    placeholder="0.00"
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                  />
                </div>

                {novoPagamentoVenda.tipo === 'dinheiro' && (
                  <>
                    <div className="form-group">
                      <label>Valor Recebido <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="number"
                        step="0.01"
                        min={novoPagamentoVenda.valor}
                        value={novoPagamentoVenda.valorRecebido}
                        onChange={(e) => {
                          const valor = parseFloat(e.target.value) || novoPagamentoVenda.valor;
                          setNovoPagamentoVenda({
                            ...novoPagamentoVenda,
                            valorRecebido: valor,
                            troco: Math.max(0, valor - novoPagamentoVenda.valor)
                          });
                        }}
                        placeholder="0.00"
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                      />
                    </div>
                    
                    {novoPagamentoVenda.troco > 0 && (
                      <div style={{ background: '#d1fae5', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '0.75rem', border: '2px solid #10b981' }}>
                        <p style={{ margin: 0, fontSize: '0.875rem', color: '#065f46', fontWeight: 600 }}>
                          💵 Troco: R$ {novoPagamentoVenda.troco.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </>
                )}

                <button
                  onClick={() => {
                    const total = abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)
                      ? (comandaSelecionadaModal || comandaMesaData).total 
                      : calcularTotal();
                    const valorPago = pagamentosSequenciaisVenda.reduce((acc, p) => acc + p.valor, 0);
                    const valorRestante = total - valorPago;
                    
                    if (!novoPagamentoVenda.tipo || !novoPagamentoVenda.valor || novoPagamentoVenda.valor <= 0) {
                      toast.error('Informe o tipo e valor do pagamento!');
                      return;
                    }
                    
                    if (novoPagamentoVenda.valor > valorRestante) {
                      toast.error(`Valor excede o restante: R$ ${valorRestante.toFixed(2)}`);
                      return;
                    }
                    
                    let troco = 0;
                    if (novoPagamentoVenda.tipo === 'dinheiro' && novoPagamentoVenda.valorRecebido > novoPagamentoVenda.valor) {
                      troco = novoPagamentoVenda.valorRecebido - novoPagamentoVenda.valor;
                    }
                    
                    setPagamentosSequenciaisVenda([...pagamentosSequenciaisVenda, {
                      ...novoPagamentoVenda,
                      troco: troco
                    }]);
                    
                    const totalRestante = total - valorPago - novoPagamentoVenda.valor;
                    setNovoPagamentoVenda({ tipo: 'dinheiro', valor: totalRestante > 0 ? totalRestante : 0, valorRecebido: totalRestante > 0 ? totalRestante : 0, troco: 0 });
                  }}
                  disabled={!novoPagamentoVenda.tipo || !novoPagamentoVenda.valor || novoPagamentoVenda.valor <= 0}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    background: (!novoPagamentoVenda.tipo || !novoPagamentoVenda.valor || novoPagamentoVenda.valor <= 0) ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    fontWeight: 600,
                    cursor: (!novoPagamentoVenda.tipo || !novoPagamentoVenda.valor || novoPagamentoVenda.valor <= 0) ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    marginTop: '0.5rem'
                  }}
                >
                  ➕ {pagamentosSequenciaisVenda.length === 0 ? 'Adicionar Primeiro Pagamento' : 'Adicionar Pagamento'}
                </button>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => {
                setModalVenda(false);
                // ✅ Resetar estados ao cancelar
                setPagamentosSequenciaisVenda([]);
                setNovoPagamentoVenda({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
                setModoDividirPagamentoVenda(false);
              }}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={() => {
                if (abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData)) {
                  // Processar pagamento da comanda
                  const comanda = comandaSelecionadaModal || comandaMesaData;
                  const totalComanda = parseFloat(comanda.total) || 0;
                  
                  // ✅ Verificar se há pagamentos sequenciais
                  if (pagamentosSequenciaisVenda.length > 0) {
                    const valorPago = pagamentosSequenciaisVenda.reduce((acc, p) => acc + p.valor, 0);
                    
                    if (valorPago < totalComanda) {
                      toast.error(`Pagamento incompleto. Faltam R$ ${(totalComanda - valorPago).toFixed(2)}`);
                      return;
                    }
                    
                    // Enviar múltiplos pagamentos
                    fecharComandaMutation.mutate({
                      comandaId: comanda._id,
                      pagamentos: pagamentosSequenciaisVenda
                    });
                    setModalVenda(false);
                    setPagamentosSequenciaisVenda([]);
                    setModoDividirPagamentoVenda(false);
                    setNovoPagamentoVenda({ tipo: 'dinheiro', valor: 0, valorRecebido: 0, troco: 0 });
                    return;
                  }
                  
                  // Formato antigo (pagamento único)
                  let valorRecebido = totalComanda;
                  let troco = 0;
                  
                  if (formaPagamento.tipo === 'dinheiro') {
                    valorRecebido = parseFloat(formaPagamento.valor) || totalComanda;
                    troco = Math.max(0, valorRecebido - totalComanda);
                  } else {
                    valorRecebido = totalComanda;
                    troco = 0;
                  }
                  
                  // Garantir que formaPagamento tenha todos os campos
                  const formaPagamentoCompleta = {
                    tipo: formaPagamento.tipo || 'dinheiro',
                    valor: valorRecebido,
                    troco: troco
                  };
                  
                  console.log('💳 Processando pagamento:', {
                    comandaId: comanda._id,
                    totalComanda,
                    formaPagamento: formaPagamentoCompleta
                  });
                  
                  fecharComandaMutation.mutate({
                    comandaId: comanda._id,
                    formaPagamento: formaPagamentoCompleta,
                    valorRecebido: valorRecebido
                  });
                  setModalVenda(false);
                } else {
                  finalizarVenda();
                }
              }}
              disabled={
                (pagamentosSequenciaisVenda.length > 0 && pagamentosSequenciaisVenda.reduce((acc, p) => acc + p.valor, 0) < (abaAtiva === 'mesas' && (comandaSelecionadaModal || comandaMesaData) ? (comandaSelecionadaModal || comandaMesaData).total : calcularTotal()))
              }>
                {abaAtiva === 'mesas' ? 'Confirmar Pagamento' : 'Confirmar Venda'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalFechamento && (
        <div className="modal-overlay" onClick={() => {
          if (!relatorioFechamento) {
            setModalFechamento(false);
          }
        }}>
          <div className="modal-content modal-fechamento" onClick={(e) => e.stopPropagation()}>
            {!relatorioFechamento ? (
              <>
                <h3>Fechar Caixa</h3>
                <div className="form-group">
                  <label>Valor de Fechamento</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={valorFechamento}
                    onChange={(e) => setValorFechamento(e.target.value)}
                  />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setModalFechamento(false)}>
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      fecharCaixaMutation.mutate({
                        id: caixaData._id,
                        data: {
                          valorFechamento: parseFloat(valorFechamento) || valorEmCaixa,
                          observacoes: ''
                        }
                      });
                    }}
                  >
                    Fechar Caixa
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-header">
                  <h3>Relatório de Fechamento</h3>
                  <p>Caixa #{caixaData?.numero}</p>
                </div>

                <div className="relatorio-content">
                  <div className="relatorio-section">
                    <h4>Resumo Geral</h4>
                    <div className="relatorio-item">
                      <span>Troco (inicial):</span>
                      <strong>R$ {relatorioFechamento.valorAbertura.toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>Total de Vendas:</span>
                      <strong>R$ {relatorioFechamento.totalVendas.toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>Quantidade de Vendas:</span>
                      <strong>{relatorioFechamento.quantidadeVendas}</strong>
                    </div>
                    <div className="relatorio-item highlight">
                      <span>Valor Esperado (troco + vendas):</span>
                      <strong>R$ {relatorioFechamento.valorEsperado.toFixed(2)}</strong>
                    </div>
                    <div className="relatorio-item">
                      <span>Valor de Fechamento:</span>
                      <strong>R$ {relatorioFechamento.valorFechamento.toFixed(2)}</strong>
                    </div>
                    <div className={`relatorio-item ${relatorioFechamento.diferenca >= 0 ? 'positive' : 'negative'}`}>
                      <span>Diferença:</span>
                      <strong>R$ {relatorioFechamento.diferenca.toFixed(2)}</strong>
                    </div>
                  </div>

                  <div className="relatorio-section">
                    <h4>Detalhamento por Forma de Pagamento</h4>
                    
                    {relatorioFechamento.totaisPorFormaPagamento?.dinheiro && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>💵 Dinheiro</span>
                          <strong>{Number(relatorioFechamento.totaisPorFormaPagamento.dinheiro.quantidade || 0)} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {Number(relatorioFechamento.totaisPorFormaPagamento.dinheiro.total || 0).toFixed(2)}</strong>
                          </div>
                          {Number(relatorioFechamento.totaisPorFormaPagamento.dinheiro.troco || 0) > 0 && (
                            <div className="detalhe-item">
                              <span>Troco Total:</span>
                              <strong>R$ {Number(relatorioFechamento.totaisPorFormaPagamento.dinheiro.troco || 0).toFixed(2)}</strong>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {relatorioFechamento.totaisPorFormaPagamento?.pix && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>📱 PIX</span>
                          <strong>{Number(relatorioFechamento.totaisPorFormaPagamento.pix.quantidade || 0)} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {Number(relatorioFechamento.totaisPorFormaPagamento.pix.total || 0).toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {relatorioFechamento.totaisPorFormaPagamento?.cartao_credito && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>💳 Cartão de Crédito</span>
                          <strong>{Number(relatorioFechamento.totaisPorFormaPagamento.cartao_credito.quantidade || 0)} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {Number(relatorioFechamento.totaisPorFormaPagamento.cartao_credito.total || 0).toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {relatorioFechamento.totaisPorFormaPagamento?.cartao_debito && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>💳 Cartão de Débito</span>
                          <strong>{Number(relatorioFechamento.totaisPorFormaPagamento.cartao_debito.quantidade || 0)} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {Number(relatorioFechamento.totaisPorFormaPagamento.cartao_debito.total || 0).toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {(relatorioFechamento.vendasFiadas || (relatorioFechamento?.vendasFiadas?.quantidade > 0)) && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>⏳ Crediário</span>
                          <strong>{Number(relatorioFechamento?.vendasFiadas?.quantidade || 0)} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {Number(relatorioFechamento?.vendasFiadas?.total || 0).toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    {relatorioFechamento.totaisPorFormaPagamento?.vale_refeicao?.quantidade > 0 && (
                      <div className="forma-pagamento-item">
                        <div className="forma-pagamento-header">
                          <span>🍽️ Vale Refeição</span>
                          <strong>{relatorioFechamento.totaisPorFormaPagamento.vale_refeicao.quantidade} venda(s)</strong>
                        </div>
                        <div className="forma-pagamento-detalhes">
                          <div className="detalhe-item">
                            <span>Total:</span>
                            <strong>R$ {relatorioFechamento.totaisPorFormaPagamento.vale_refeicao.total.toFixed(2)}</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {relatorioFechamento.movimentacoes && (
                    <div className="relatorio-section">
                      <h4>Movimentações do Período</h4>
                      <div className="relatorio-item">
                        <span>Total de Entradas:</span>
                        <strong>R$ {relatorioFechamento.movimentacoes.totalEntradas.toFixed(2)}</strong>
                      </div>
                      <div className="relatorio-item">
                        <span>Total de Saídas:</span>
                        <strong>R$ {relatorioFechamento.movimentacoes.totalSaidas.toFixed(2)}</strong>
                      </div>

                      {Array.isArray(relatorioFechamento.movimentacoes.itens) && relatorioFechamento.movimentacoes.itens.length > 0 ? (
                        <div style={{ marginTop: '1rem' }}>
                          {relatorioFechamento.movimentacoes.itens.map((m) => (
                            <div
                              key={m.id}
                              style={{
                                padding: '0.75rem',
                                background: 'rgba(255,255,255,0.05)',
                                borderRadius: '6px',
                                marginBottom: '0.5rem',
                                borderLeft: `4px solid ${m.tipo === 'entrada' ? '#10B981' : '#EF4444'}`
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                                <span style={{ fontWeight: 700 }}>
                                  {m.tipo === 'entrada' ? '⬆️ Entrada' : '⬇️ Saída'}
                                  {m.categoria ? ` • ${m.categoria}` : ''}
                                </span>
                                <strong>
                                  R$ {Number(m.valor || 0).toFixed(2)}
                                </strong>
                              </div>
                              <div style={{ fontSize: '0.85rem', opacity: 0.85, marginTop: '0.25rem' }}>
                                {m.descricao}
                              </div>
                              <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                {m.banco ? `${m.banco}` : ''}{m.conta ? ` • ${m.conta}` : ''}
                                {m.data ? ` • ${new Date(m.data).toLocaleString('pt-BR')}` : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{ opacity: 0.8 }}>Nenhuma movimentação registrada no período.</p>
                      )}
                    </div>
                  )}

                  {/* ✅ Seção de Vendas Fiadas */}
                  {relatorioFechamento.vendasFiadas && relatorioFechamento.vendasFiadas.quantidade > 0 && (
                    <div className="relatorio-section" style={{ border: '2px solid #f59e0b', borderRadius: '8px', padding: '1rem', background: 'rgba(245, 158, 11, 0.1)' }}>
                      <h4 style={{ color: '#f59e0b', marginBottom: '1rem' }}>⏳ Vendas Fiadas (Não somam ao total do dia)</h4>
                      <div className="relatorio-item highlight" style={{ marginBottom: '1rem' }}>
                        <span>Quantidade de Vendas Fiadas:</span>
                        <strong style={{ color: '#f59e0b' }}>{relatorioFechamento.vendasFiadas.quantidade}</strong>
                      </div>
                      <div className="relatorio-item highlight" style={{ marginBottom: '1rem' }}>
                        <span>Total em Fiado:</span>
                        <strong style={{ color: '#f59e0b' }}>R$ {relatorioFechamento.vendasFiadas.total.toFixed(2)}</strong>
                      </div>
                      {relatorioFechamento.vendasFiadas.detalhes && relatorioFechamento.vendasFiadas.detalhes.length > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                          <h5 style={{ marginBottom: '0.75rem', fontSize: '0.9rem', color: '#f59e0b' }}>Detalhamento:</h5>
                          {relatorioFechamento.vendasFiadas.detalhes.map((venda, idx) => (
                            <div key={idx} style={{ 
                              padding: '0.75rem', 
                              background: 'rgba(255,255,255,0.05)', 
                              borderRadius: '4px', 
                              marginBottom: '0.5rem',
                              borderLeft: '3px solid #f59e0b'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                <span style={{ fontWeight: 'bold' }}>#{venda.numero}</span>
                                <strong style={{ color: '#f59e0b' }}>R$ {venda.total.toFixed(2)}</strong>
                              </div>
                              <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                                Cliente: {venda.cliente} | {new Date(venda.data).toLocaleDateString('pt-BR')}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={() => {
                    setModalFechamento(false);
                    setRelatorioFechamento(null);
                  }}>
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Preview de Impressão */}
      {modalPreviewImpressao && (
        <div className="modal-overlay" onClick={() => setModalPreviewImpressao(false)}>
          <div className="modal-content modal-preview-impressao" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📄 Preview de Impressão</h3>
              <button className="btn-close-modal" onClick={() => setModalPreviewImpressao(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <pre className="preview-texto">{textoPreviewImpressao}</pre>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  // Imprimir realmente
                  const comandaId = comandaMesaData?._id || comandaSelecionadaModal?._id;
                  if (comandaId) {
                    imprimirComandaMutation.mutate(comandaId);
                  }
                  setModalPreviewImpressao(false);
                }}
                disabled={imprimirComandaMutation.isLoading}
              >
                <FileText size={18} />
                {imprimirComandaMutation.isLoading ? 'Imprimindo...' : 'Imprimir Agora'}
              </button>
              <button className="btn btn-primary" onClick={() => setModalPreviewImpressao(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Modal Adicionar Item - Produto já selecionado */}
      {modalAdicionarItem && produtoSelecionadoModal && (
        <div className="modal-overlay" onClick={() => setModalAdicionarItem(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{produtoSelecionadoModal.nome}</h3>
              <button className="btn-close-modal" onClick={() => setModalAdicionarItem(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* ✅ Mostrar produto selecionado no topo */}
              <div className="produto-selecionado-info" style={{
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem',
                border: '2px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {produtoSelecionadoModal.imagens?.[0]?.url ? (
                    <img 
                      src={produtoSelecionadoModal.imagens[0].url} 
                      alt={produtoSelecionadoModal.nome}
                      style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '0.5rem' }}
                    />
                  ) : (
                    <div style={{ width: '60px', height: '60px', background: '#e5e7eb', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Package size={24} color="#9ca3af" />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '1.125rem', display: 'block', marginBottom: '0.25rem' }}>
                      {produtoSelecionadoModal.nome}
                    </strong>
                    <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10b981' }}>
                      R$ {(((novoItem.variacao?.preco) ?? produtoSelecionadoModal.precoPromocional ?? produtoSelecionadoModal.preco ?? 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Variações (quando existir) */}
              {produtoSelecionadoModal.temVariacoes && Array.isArray(produtoSelecionadoModal.variacoes) && produtoSelecionadoModal.variacoes.length > 0 && (
                <div className="form-group">
                  <label>Tamanho</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {produtoSelecionadoModal.variacoes
                      .filter((v) => v && v.disponivel !== false)
                      .map((variacao, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`btn ${novoItem.variacao?.nome === variacao.nome ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => setNovoItem({ ...novoItem, variacao })}
                          style={{ padding: '0.5rem 0.75rem' }}
                        >
                          {variacao.nome} (R$ {Number(variacao.preco || 0).toFixed(2)})
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* ✅ Nome de quem está pedindo - opcional */}
              <div className="form-group">
                <label>
                  Nome de quem está pedindo
                </label>
                <input
                  type="text"
                  value={novoItem.clienteItem}
                  onChange={(e) => setNovoItem({ ...novoItem, clienteItem: e.target.value })}
                  placeholder="Ex: João, Maria, etc"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #ddd'
                  }}
                />
              </div>

              {/* Quantidade */}
              <div className="form-group">
                <label>Quantidade</label>
                <div className="quantidade-controls">
                  <button onClick={() => setNovoItem({ ...novoItem, quantidade: Math.max(1, novoItem.quantidade - 1) })}>
                    -
                  </button>
                  <span>{novoItem.quantidade}</span>
                  <button onClick={() => setNovoItem({ ...novoItem, quantidade: novoItem.quantidade + 1 })}>
                    +
                  </button>
                </div>
              </div>

              {isProdutoMarmitex(produtoSelecionadoModal) && (
                (() => {
                  const carneExtraCfg = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : []).find((a) => {
                    return normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNE_EXTRA) && normalizarTexto(a?.nome) === normalizarTexto(NOME_CARNE_EXTRA);
                  });
                  const precoExtra = Number(carneExtraCfg?.preco) || 0;
                  const limiteCarnes = obterLimiteCarnes(produtoSelecionadoModal, novoItem.variacao);
                  const totalCarnes = novoItem.marmitexSemCarne ? 0 : contarCarnesSelecionadas(novoItem.acrescimos);
                  const carnesDoDia = (Array.isArray(adicionaisGlobais) ? adicionaisGlobais : [])
                    .filter((a) => a && a.disponivel !== false && normalizarTexto(a?.categoria) === normalizarTexto(CATEGORIA_CARNES_DO_DIA))
                    .sort((a, b) => (Number(a?.ordem) || 0) - (Number(b?.ordem) || 0) || String(a?.nome || '').localeCompare(String(b?.nome || '')));

                  return (
                    <>
                      {produtoSelecionadoModal.adicionais && produtoSelecionadoModal.adicionais.length > 0 && (
                        <div className="form-group">
                          <label>Guarnições (opcional)</label>
                          <div className="acrescimos-grid">
                            {produtoSelecionadoModal.adicionais
                              .filter((g) => g && g.disponivel !== false)
                              .map((guarnicao, idx) => {
                                const nomeMarcado = `${PREFIXO_GUARNICAO}${guarnicao.nome}`;
                                const selecionado = (novoItem.acrescimos || []).find((a) => normalizarTexto(a?.nome) === normalizarTexto(nomeMarcado));

                                return (
                                  <div
                                    key={`${guarnicao.nome}-${idx}`}
                                    className={`acrescimo-item ${selecionado ? 'selected' : ''}`}
                                    onClick={() => toggleAcrescimo({ nome: nomeMarcado, preco: Number(guarnicao.preco) || 0 })}
                                  >
                                    <span>{guarnicao.nome}</span>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}

                      <div className="form-group">
                        <label>Carnes do Dia</label>
                        <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#6B7280' }}>
                          Carnes: {totalCarnes}/{limiteCarnes}
                        </div>
                        <div className="acrescimos-grid" style={{ marginBottom: '0.5rem' }}>
                          <div
                            className={`acrescimo-item ${novoItem.marmitexSemCarne ? 'selected' : ''}`}
                            onClick={() => {
                              const semCarne = !novoItem.marmitexSemCarne;
                              const acrescimosSemCarnes = semCarne
                                ? (novoItem.acrescimos || []).filter((a) => !normalizarTexto(a?.nome).startsWith(normalizarTexto(PREFIXO_CARNE)))
                                : (novoItem.acrescimos || []);
                              setNovoItem({
                                ...novoItem,
                                marmitexSemCarne: semCarne,
                                marmitexPermitirExtra: semCarne ? false : novoItem.marmitexPermitirExtra,
                                acrescimos: acrescimosSemCarnes
                              });
                            }}
                          >
                            <span>Sem carne</span>
                          </div>

                          <div
                            className={`acrescimo-item ${novoItem.marmitexPermitirExtra ? 'selected' : ''}`}
                            style={{ opacity: (novoItem.marmitexSemCarne || !carneExtraCfg || carneExtraCfg.disponivel === false) ? 0.5 : 1 }}
                            onClick={() => {
                              if (novoItem.marmitexSemCarne) return;
                              if (!carneExtraCfg || carneExtraCfg.disponivel === false) {
                                toast.error('Carne extra não configurada/indisponível.');
                                return;
                              }
                              setNovoItem({ ...novoItem, marmitexPermitirExtra: !novoItem.marmitexPermitirExtra });
                            }}
                          >
                            <span>Carne extra{carneExtraCfg ? ` (+ R$ ${precoExtra.toFixed(2)} cada)` : ''}</span>
                          </div>
                        </div>

                        <div className="acrescimos-grid">
                          {carnesDoDia.map((carne) => {
                            const nomeMarcado = `${PREFIXO_CARNE}${carne.nome}`;
                            const selecionada = (novoItem.acrescimos || []).find((a) => normalizarTexto(a?.nome) === normalizarTexto(nomeMarcado));
                            const checked = Boolean(selecionada);
                            const qtdAtual = Math.min(20, Math.max(1, Number(selecionada?.quantidade || 1)));

                            return (
                              <div
                                key={carne._id}
                                className={`acrescimo-item ${checked ? 'selected' : ''}`}
                                onClick={() => {
                                  if (novoItem.marmitexSemCarne) return;
                                  const totalAtual = contarCarnesSelecionadas(novoItem.acrescimos);
                                  if (!checked) {
                                    const novoTotal = totalAtual + 1;
                                    if (novoTotal > limiteCarnes && !novoItem.marmitexPermitirExtra) {
                                      toast.error(`Limite de carnes: ${limiteCarnes}. Marque a opção de carne extra para adicionar mais.`);
                                      return;
                                    }
                                    if (novoTotal > limiteCarnes && (!carneExtraCfg || carneExtraCfg.disponivel === false)) {
                                      toast.error('Carne extra não configurada/indisponível.');
                                      return;
                                    }
                                  }
                                  toggleAcrescimo({ nome: nomeMarcado, preco: 0 });
                                }}
                              >
                                <span>{carne.nome}</span>
                                {checked && !novoItem.marmitexSemCarne && (
                                  <input
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={qtdAtual}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const qtdNova = Math.min(20, Math.max(1, Number(e.target.value || 1)));
                                      const totalCarnesAgora = contarCarnesSelecionadas(novoItem.acrescimos);
                                      const totalSemEste = totalCarnesAgora - qtdAtual;
                                      const novoTotal = totalSemEste + qtdNova;
                                      if (novoTotal > limiteCarnes && !novoItem.marmitexPermitirExtra) {
                                        toast.error(`Limite de carnes: ${limiteCarnes}. Marque a opção de carne extra para aumentar a quantidade.`);
                                        return;
                                      }
                                      if (novoTotal > limiteCarnes && (!carneExtraCfg || carneExtraCfg.disponivel === false)) {
                                        toast.error('Carne extra não configurada/indisponível.');
                                        return;
                                      }
                                      atualizarQuantidadeAcrescimo(nomeMarcado, qtdNova);
                                    }}
                                    style={{ width: '70px', marginLeft: '0.5rem' }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()
              )}

              {/* Adicionais Globais */}
              {adicionaisGlobais && adicionaisGlobais.length > 0 && (
                <div className="form-group">
                  <label>Adicionais (opcional)</label>
                  {(() => {
                    const isMarmitexModal = isProdutoMarmitex(produtoSelecionadoModal);
                    const adicionaisFiltrados = isMarmitexModal
                      ? adicionaisGlobais.filter((a) => {
                          const cat = normalizarTexto(a?.categoria);
                          return cat !== normalizarTexto(CATEGORIA_CARNES_DO_DIA) && cat !== normalizarTexto(CATEGORIA_CARNE_EXTRA);
                        })
                      : adicionaisGlobais;

                    if (!adicionaisFiltrados || adicionaisFiltrados.length === 0) return null;

                    // Agrupar adicionais por categoria
                    const adicionaisPorCategoria = adicionaisFiltrados.reduce((acc, adicional) => {
                      const categoria = adicional.categoria || 'Geral';
                      if (!acc[categoria]) {
                        acc[categoria] = [];
                      }
                      acc[categoria].push(adicional);
                      return acc;
                    }, {});

                    return Object.entries(adicionaisPorCategoria).map(([categoria, adicionais]) => (
                      <div key={categoria} style={{ marginBottom: '1rem' }}>
                        {Object.keys(adicionaisPorCategoria).length > 1 && (
                          <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#6B7280', marginBottom: '0.5rem' }}>
                            {categoria}
                          </h4>
                        )}
                        <div className="acrescimos-grid">
                          {adicionais.map((adicional) => (
                            <div
                              key={adicional._id}
                              className={`acrescimo-item ${novoItem.acrescimos.find(a => a._id === adicional._id || a.nome === adicional.nome) ? 'selected' : ''}`}
                              onClick={() => toggleAcrescimo({ _id: adicional._id, nome: adicional.nome, preco: adicional.preco })}
                            >
                              <span>{adicional.nome}</span>
                              <span className="acrescimo-preco">+ R$ {adicional.preco.toFixed(2)}</span>
                              {novoItem.acrescimos.find(a => a._id === adicional._id || a.nome === adicional.nome) && (
                                <input
                                  type="number"
                                  min={1}
                                  max={20}
                                  value={novoItem.acrescimos.find(a => a._id === adicional._id || a.nome === adicional.nome)?.quantidade || 1}
                                  onChange={(e) => atualizarQuantidadeAcrescimo(adicional.nome, e.target.value)}
                                  style={{ width: '70px', marginLeft: '0.5rem' }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}

              {/* Observações (opcional) */}
              <div className="form-group">
                <label>Observações (opcional)</label>
                <textarea
                  value={novoItem.observacoes}
                  onChange={(e) => setNovoItem({ ...novoItem, observacoes: e.target.value })}
                  placeholder="Ex: Sem cebola, sem passas, bem passado, etc"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModalAdicionarItem(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleAdicionarItemCarrinho}
              >
                Adicionar ao Carrinho
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cliente/Endereço para Retirada */}
      {modalClienteRetirada && (
        <div className="modal-overlay" onClick={() => setModalClienteRetirada(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>📋 Dados do Cliente - Retirada</h3>
              <button className="btn-close-modal" onClick={() => setModalClienteRetirada(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>
                  Telefone do Cliente
                </label>
                <input
                  type="text"
                  value={cliente.telefone}
                  onChange={(e) => {
                    const telefone = e.target.value;
                    setCliente({ ...cliente, telefone });
                    if (telefone.length >= 10) {
                      buscarClientePorTelefone(telefone);
                    }
                  }}
                  placeholder="(00) 00000-0000"
                  disabled={buscandoCliente}
                />
                {buscandoCliente && <small style={{ color: '#666', display: 'block', marginTop: '0.25rem' }}>Buscando cliente...</small>}
              </div>

              <div className="form-group">
                <label>
                  Nome do Cliente
                </label>
                <input
                  type="text"
                  value={cliente.nome}
                  onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                  placeholder="Nome completo"
                />
              </div>

              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #e5e7eb' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>
                  📍 Endereço (opcional - para retirada)
                </h4>
                <div className="form-group">
                  <label>CEP</label>
                  <input
                    type="text"
                    value={enderecoRetirada.cep}
                    onChange={(e) => setEnderecoRetirada({ ...enderecoRetirada, cep: e.target.value })}
                    placeholder="00000-000"
                  />
                </div>
                <div className="form-group">
                  <label>Rua</label>
                  <input
                    type="text"
                    value={enderecoRetirada.rua}
                    onChange={(e) => setEnderecoRetirada({ ...enderecoRetirada, rua: e.target.value })}
                    placeholder="Nome da rua"
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Número</label>
                    <input
                      type="text"
                      value={enderecoRetirada.numero}
                      onChange={(e) => setEnderecoRetirada({ ...enderecoRetirada, numero: e.target.value })}
                      placeholder="123"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 2 }}>
                    <label>Complemento</label>
                    <input
                      type="text"
                      value={enderecoRetirada.complemento}
                      onChange={(e) => setEnderecoRetirada({ ...enderecoRetirada, complemento: e.target.value })}
                      placeholder="Apto, Bloco..."
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Bairro</label>
                  <input
                    type="text"
                    value={enderecoRetirada.bairro}
                    onChange={(e) => setEnderecoRetirada({ ...enderecoRetirada, bairro: e.target.value })}
                    placeholder="Nome do bairro"
                  />
                </div>
                <div className="form-group">
                  <label>Referência</label>
                  <input
                    type="text"
                    value={enderecoRetirada.referencia}
                    onChange={(e) => setEnderecoRetirada({ ...enderecoRetirada, referencia: e.target.value })}
                    placeholder="Ponto de referência"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModalClienteRetirada(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleConfirmarRetirada}
              >
                Confirmar e Enviar para Produção
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Troco/Pagamento antes de Enviar para Produção */}
      {modalTrocoProducao && (
        <div className="modal-overlay" onClick={() => setModalTrocoProducao(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>💳 Pagamento - {tipoEntrega === 'delivery' ? 'Delivery' : 'Produção'}</h3>
              <button className="btn-close-modal" onClick={() => setModalTrocoProducao(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Resumo com taxa de entrega quando for delivery */}
              {tipoEntrega === 'delivery' ? (
                <div style={{ 
                  background: calcularTaxaEntrega() > 0 ? '#fef3c7' : '#d1fae5', 
                  border: `2px solid ${calcularTaxaEntrega() > 0 ? '#f59e0b' : '#10b981'}`, 
                  borderRadius: '8px', 
                  padding: '1rem', 
                  marginBottom: '1.5rem',
                  display: 'block'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: '0.75rem',
                    fontSize: '0.95rem',
                    fontWeight: '500'
                  }}>
                    <span>Subtotal dos Produtos:</span>
                    <strong style={{ fontWeight: '600' }}>
                      R$ {carrinho.reduce((acc, item) => {
                        const itemTotal = item.subtotal || (item.precoUnitario * item.quantidade);
                        return acc + (itemTotal - (item.desconto || 0));
                      }, 0).toFixed(2).replace('.', ',')}
                    </strong>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '0.5rem',
                    border: `2px solid ${calcularTaxaEntrega() > 0 ? '#f59e0b' : '#10b981'}`
                  }}>
                    <span style={{ fontWeight: '600', fontSize: '1.1rem', color: '#1f2937' }}>💰 Taxa de Entrega:</span>
                    <strong style={{ 
                      color: calcularTaxaEntrega() > 0 ? '#92400e' : '#059669',
                      fontSize: '1.2rem',
                      fontWeight: '700'
                    }}>
                      {enderecoEntrega.bairro && enderecoEntrega.bairro.trim()
                        ? (calcularTaxaEntrega() > 0 
                            ? `R$ ${calcularTaxaEntrega().toFixed(2).replace('.', ',')}`
                            : 'Grátis')
                        : '⚠️ Informe o bairro'}
                    </strong>
                  </div>
                  {enderecoEntrega.bairro && enderecoEntrega.bairro.trim() && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#64748b',
                      marginBottom: '0.75rem',
                      fontStyle: 'italic',
                      paddingLeft: '0.5rem'
                    }}>
                      📍 Bairro: {enderecoEntrega.bairro}
                    </div>
                  )}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingTop: '0.75rem', 
                    borderTop: `2px solid ${calcularTaxaEntrega() > 0 ? '#f59e0b' : '#10b981'}`,
                    fontSize: '1.2rem',
                    fontWeight: '700'
                  }}>
                    <span>Total do Pedido:</span>
                    <strong style={{ color: '#059669', fontSize: '1.3rem' }}>
                      R$ {calcularTotal().toFixed(2).replace('.', ',')}
                    </strong>
                  </div>
                  {pagamentosSequenciais.length > 0 && (
                    <>
                      <div style={{ 
                        marginTop: '0.75rem',
                        paddingTop: '0.75rem',
                        borderTop: '1px solid rgba(0,0,0,0.1)'
                      }}>
                        <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
                          Valor Pago: R$ {pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0).toFixed(2).replace('.', ',')}
                        </p>
                        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#ef4444', fontWeight: 600 }}>
                          Restante: R$ {(calcularTotal() - pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0)).toFixed(2).replace('.', ',')}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ background: '#f9fafb', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Total do Pedido</p>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                    R$ {calcularTotal().toFixed(2).replace('.', ',')}
                  </p>
                  {pagamentosSequenciais.length > 0 && (
                    <>
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                        Valor Pago: R$ {pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0).toFixed(2).replace('.', ',')}
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#ef4444', fontWeight: 600 }}>
                        Restante: R$ {(calcularTotal() - pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0)).toFixed(2).replace('.', ',')}
                      </p>
                    </>
                  )}
                </div>
              )}

              {carrinho.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>
                    Itens do Pedido
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {carrinho.map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: '0.75rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.5rem',
                          background: '#ffffff'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                          <strong style={{ color: '#111827' }}>{item.quantidade}x {item.produto?.nome || 'Produto'}</strong>
                          <strong style={{ color: '#111827' }}>R$ {(item.subtotal || (item.precoUnitario * item.quantidade)).toFixed(2).replace('.', ',')}</strong>
                        </div>
                        {item.clienteItem && (
                          <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#6b7280' }}>👤 {item.clienteItem}</div>
                        )}
                        {item.observacoes && (
                          <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#6b7280' }}>📝 {item.observacoes}</div>
                        )}
                        {item.acrescimos && item.acrescimos.length > 0 && (
                          <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
                            ➕ {item.acrescimos.map(a => `${Math.min(20, Math.max(1, Number(a?.quantidade || 1)))}x ${a.nome}${typeof a.preco === 'number' ? ` (+R$ ${a.preco.toFixed(2).replace('.', ',')})` : ''}`).join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ✅ NOVO: Lista de pagamentos sequenciais */}
              {pagamentosSequenciais.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>Pagamentos Adicionados:</h4>
                  {pagamentosSequenciais.map((pag, idx) => {
                    const formaPagamentoTexto = {
                      'dinheiro': '💵 Dinheiro',
                      'pix': '📱 PIX',
                      'cartao_credito': '💳 Cartão de Crédito',
                      'cartao_debito': '💳 Cartão de Débito',
                      'vale_refeicao': '🍽️ Vale Refeição'
                    }[pag.tipo] || pag.tipo;
                    
                    return (
                      <div key={idx} style={{ 
                        background: '#f3f4f6', 
                        padding: '0.75rem', 
                        borderRadius: '0.5rem', 
                        marginBottom: '0.5rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>
                            {formaPagamentoTexto}: R$ {pag.valor.toFixed(2)}
                          </p>
                          {pag.tipo === 'dinheiro' && pag.troco > 0 && (
                            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#059669' }}>
                              Troco: R$ {pag.troco.toFixed(2)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => removerPagamentoSequencial(idx)}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ✅ Opção para dividir pagamento - SEMPRE VISÍVEL */}
              {!modoDividirPagamento && pagamentosSequenciais.length === 0 && (
                <>
                  <div className="form-group">
                    <label>Forma de Pagamento <span style={{ color: '#ef4444' }}>*</span></label>
                    <select
                      value={pagamentoProducao.tipo}
                      onChange={(e) => {
                        const novoTipo = e.target.value;
                        const total = calcularTotal();
                        setPagamentoProducao({
                          tipo: novoTipo,
                          valorRecebido: novoTipo === 'dinheiro' ? pagamentoProducao.valorRecebido || total : total,
                          troco: 0
                        });
                      }}
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                    >
                      <option value="dinheiro">💵 Dinheiro</option>
                      <option value="pix">📱 PIX</option>
                      <option value="cartao_credito">💳 Cartão de Crédito</option>
                      <option value="cartao_debito">💳 Cartão de Débito</option>
                      <option value="vale_refeicao">🍽️ Vale Refeição</option>
                    </select>
                  </div>

                  {pagamentoProducao.tipo === 'dinheiro' && (
                    <>
                      <div className="form-group">
                        <label>Troco para quanto? <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          type="number"
                          step="0.01"
                          min={calcularTotal()}
                          value={pagamentoProducao.valorRecebido}
                          onChange={(e) => {
                            const valor = parseFloat(e.target.value) || calcularTotal();
                            const total = calcularTotal();
                            setPagamentoProducao({
                              ...pagamentoProducao,
                              valorRecebido: valor,
                              troco: Math.max(0, valor - total)
                            });
                          }}
                          placeholder="0.00"
                          style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                        />
                      </div>
                      
                      {pagamentoProducao.troco > 0 && (
                        <div style={{ background: '#d1fae5', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem', border: '2px solid #10b981' }}>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: '#065f46', fontWeight: 600 }}>
                            💵 Troco: R$ {pagamentoProducao.troco.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  <div style={{ 
                    background: '#dbeafe', 
                    padding: '1rem', 
                    borderRadius: '0.5rem', 
                    marginBottom: '1.5rem',
                    border: '2px solid #3b82f6',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => {
                    const total = calcularTotal();
                    setModoDividirPagamento(true);
                    setNovoPagamento({ 
                      tipo: 'dinheiro', 
                      valor: total, 
                      valorRecebido: total, 
                      troco: 0 
                    });
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#bfdbfe'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#dbeafe'}
                  >
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af', fontWeight: 600, textAlign: 'center' }}>
                      💡 Clique aqui para dividir o pagamento em múltiplas formas
                    </p>
                  </div>
                </>
              )}

              {/* ✅ NOVO: Adicionar novo pagamento sequencial - aparece quando modoDividirPagamento está ativo OU quando há pagamentos sequenciais */}
              {(modoDividirPagamento || pagamentosSequenciais.length > 0) && (
                <div style={{ 
                  border: '2px dashed #d1d5db', 
                  padding: '1rem', 
                  borderRadius: '0.5rem', 
                  marginBottom: '1.5rem',
                  background: pagamentosSequenciais.length > 0 ? '#fef3c7' : '#f0f9ff'
                }}>
                  <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>
                    {pagamentosSequenciais.length > 0 ? '➕ Adicionar Outro Pagamento' : '💳 Adicionar Pagamento'}
                  </h4>
                  
                  <div className="form-group">
                    <label>Forma de Pagamento <span style={{ color: '#ef4444' }}>*</span></label>
                    <select
                      value={novoPagamento.tipo}
                      onChange={(e) => {
                        const novoTipo = e.target.value;
                        const total = calcularTotal();
                        const valorPago = pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0);
                        const valorRestante = total - valorPago;
                        setNovoPagamento({
                          tipo: novoTipo,
                          valor: novoPagamento.valor || valorRestante,
                          valorRecebido: novoTipo === 'dinheiro' ? (novoPagamento.valorRecebido || novoPagamento.valor || valorRestante) : (novoPagamento.valor || valorRestante),
                          troco: 0
                        });
                      }}
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                    >
                      <option value="dinheiro">💵 Dinheiro</option>
                      <option value="pix">📱 PIX</option>
                      <option value="cartao_credito">💳 Cartão de Crédito</option>
                      <option value="cartao_debito">💳 Cartão de Débito</option>
                      <option value="vale_refeicao">🍽️ Vale Refeição</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Valor <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={calcularTotal() - pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0)}
                      value={novoPagamento.valor}
                      onChange={(e) => {
                        const valor = parseFloat(e.target.value) || 0;
                        setNovoPagamento({
                          ...novoPagamento,
                          valor: valor,
                          valorRecebido: novoPagamento.tipo === 'dinheiro' ? (novoPagamento.valorRecebido || valor) : valor
                        });
                      }}
                      placeholder="0.00"
                      style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                    />
                  </div>

                  {novoPagamento.tipo === 'dinheiro' && (
                    <>
                      <div className="form-group">
                        <label>Troco para quanto? <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          type="number"
                          step="0.01"
                          min={novoPagamento.valor}
                          value={novoPagamento.valorRecebido}
                          onChange={(e) => {
                            const valor = parseFloat(e.target.value) || novoPagamento.valor;
                            setNovoPagamento({
                              ...novoPagamento,
                              valorRecebido: valor,
                              troco: Math.max(0, valor - novoPagamento.valor)
                            });
                          }}
                          placeholder="0.00"
                          style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '2px solid #e5e7eb', fontSize: '1rem' }}
                        />
                      </div>
                      
                      {novoPagamento.troco > 0 && (
                        <div style={{ background: '#d1fae5', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '0.75rem', border: '2px solid #10b981' }}>
                          <p style={{ margin: 0, fontSize: '0.875rem', color: '#065f46', fontWeight: 600 }}>
                            💵 Troco: R$ {novoPagamento.troco.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  <button
                    onClick={adicionarPagamentoSequencial}
                    disabled={!novoPagamento.tipo || !novoPagamento.valor || novoPagamento.valor <= 0}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      background: (!novoPagamento.tipo || !novoPagamento.valor || novoPagamento.valor <= 0) ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      fontWeight: 600,
                      cursor: (!novoPagamento.tipo || !novoPagamento.valor || novoPagamento.valor <= 0) ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      marginTop: '0.5rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    ➕ {pagamentosSequenciais.length === 0 ? 'Adicionar Primeiro Pagamento' : 'Adicionar Pagamento'}
                  </button>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModalTrocoProducao(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={handleConfirmarTrocoProducao}
                disabled={
                  criarComandaProducaoMutation.isLoading || 
                  criarPedidoDeliveryMutation.isLoading ||
                  (pagamentosSequenciais.length === 0 && pagamentoProducao.tipo === 'dinheiro' && pagamentoProducao.valorRecebido < calcularTotal()) ||
                  (pagamentosSequenciais.length > 0 && pagamentosSequenciais.reduce((acc, p) => acc + p.valor, 0) < calcularTotal())
                }
              >
                <Send size={18} />
                {criarComandaProducaoMutation.isLoading || criarPedidoDeliveryMutation.isLoading
                  ? 'Enviando...'
                  : 'Enviar para Produção'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pagamento do Pedido */}
      {modalPagamentoPedido && pedidoSelecionadoPagamento && (
        <div className="modal-overlay" onClick={() => setModalPagamentoPedido(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Marcar Pedido como Pago</h3>
              <button className="btn-close-modal" onClick={() => setModalPagamentoPedido(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Pedido #{pedidoSelecionadoPagamento.numero}</label>
                <p><strong>Total:</strong> R$ {pedidoSelecionadoPagamento.total?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="form-group">
                <label>Forma de Pagamento *</label>
                <select
                  value={formaPagamentoPedido}
                  onChange={(e) => setFormaPagamentoPedido(e.target.value)}
                >
                  <option value="dinheiro">Dinheiro</option>
                  <option value="pix">PIX</option>
                  <option value="cartao_credito">Cartão de Crédito</option>
                  <option value="cartao_debito">Cartão de Débito</option>
                  <option value="vale_refeicao">Vale Refeição</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModalPagamentoPedido(false)}>
                Cancelar
              </button>
              <button
                className="btn-primary"
                onClick={async () => {
                  try {
                    await api.patch(`/api/pedidos/${pedidoSelecionadoPagamento._id}/pagamento`, { 
                      statusPagamento: 'Pago',
                      formaPagamento: formaPagamentoPedido
                    });
                    toast.success('Pagamento marcado como pago!');
                    setModalPagamentoPedido(false);
                    setPedidoSelecionadoPagamento(null);
                    refetchPedidos();
                  } catch (error) {
                    toast.error(error.response?.data?.message || 'Erro ao atualizar pagamento');
                  }
                }}
              >
                <CheckCircle size={16} />
                Confirmar Pagamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NOVO: Modal de Solicitação */}
      {modalSolicitacao && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                {tipoSolicitacao === 'cancelar' ? '🔴 Solicitar Cancelamento' : '✏️ Solicitar Edição'}
              </h2>
              <button className="btn-close" onClick={() => setModalSolicitacao(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="info-comanda-solicitacao">
                <p><strong>Comanda:</strong> {comandaParaSolicitar?.mesa}</p>
                <p><strong>Cliente:</strong> {getNomeCliente(comandaParaSolicitar?.cliente)}</p>
                <p><strong>Total:</strong> R$ {comandaParaSolicitar?.total?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="form-group">
                <label>
                  {tipoSolicitacao === 'cancelar' 
                    ? 'Motivo do cancelamento' 
                    : 'Motivo da edição'} <span style={{color: '#dc2626'}}>*</span>
                </label>
                <textarea
                  value={motivoSolicitacao}
                  onChange={(e) => setMotivoSolicitacao(e.target.value)}
                  placeholder="Descreva o motivo da solicitação (obrigatório)..."
                  rows={4}
                  required
                  style={{
                    border: `2px solid ${!motivoSolicitacao.trim() ? '#fca5a5' : '#e5e7eb'}`
                  }}
                />
                {!motivoSolicitacao.trim() && (
                  <small style={{color: '#dc2626', display: 'block', marginTop: '0.5rem'}}>
                    O motivo é obrigatório
                  </small>
                )}
              </div>
              <div className="alert-info">
                <AlertCircle size={20} />
                <p>
                  {tipoSolicitacao === 'cancelar'
                    ? 'Esta solicitação será enviada para o administrador. A comanda só será cancelada após aprovação.'
                    : 'Esta solicitação será enviada para o administrador. Após aprovação, você poderá editar a comanda novamente.'}
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setModalSolicitacao(false)}
                disabled={solicitarCancelamentoMutation.isLoading || solicitarEdicaoMutation.isLoading}
              >
                Cancelar
              </button>
              <button
                className={tipoSolicitacao === 'cancelar' ? 'btn-danger' : 'btn-primary'}
                onClick={enviarSolicitacao}
                disabled={!motivoSolicitacao.trim() || solicitarCancelamentoMutation.isLoading || solicitarEdicaoMutation.isLoading}
                style={{
                  opacity: !motivoSolicitacao.trim() ? 0.5 : 1
                }}
              >
                {solicitarCancelamentoMutation.isLoading || solicitarEdicaoMutation.isLoading
                  ? 'Enviando...'
                  : tipoSolicitacao === 'cancelar'
                  ? 'Solicitar Cancelamento'
                  : 'Solicitar Edição'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NOVO: Modal de Solicitação de Remoção de Item */}
      {modalSolicitacaoRemoverItem && itemParaRemover && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>⚠️ Solicitar Remoção de Item</h2>
              <button className="btn-close" onClick={() => setModalSolicitacaoRemoverItem(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="info-comanda-solicitacao">
                <p><strong>Comanda:</strong> {comandaParaSolicitar?.mesa}</p>
                <p><strong>Cliente:</strong> {getNomeCliente(comandaParaSolicitar?.cliente)}</p>
                <p><strong>Item a remover:</strong> {itemParaRemover.quantidade}x {itemParaRemover.produto?.nome || 'Produto'}</p>
                <p><strong>Valor do item:</strong> R$ {itemParaRemover.subtotal?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="form-group">
                <label>Motivo da remoção <span style={{color: '#dc2626'}}>*</span>:</label>
                <textarea
                  value={motivoRemocaoItem}
                  onChange={(e) => setMotivoRemocaoItem(e.target.value)}
                  placeholder="Descreva o motivo da remoção (obrigatório)..."
                  rows={4}
                  required
                />
                {!motivoRemocaoItem.trim() && (
                  <small style={{color: '#dc2626', display: 'block', marginTop: '0.5rem'}}>
                    O motivo é obrigatório para solicitar remoção de item
                  </small>
                )}
              </div>
              <div className="alert-info">
                <AlertCircle size={20} />
                <p>
                  Esta solicitação será enviada para o administrador. O item só será removido após aprovação.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setModalSolicitacaoRemoverItem(false)}
                disabled={solicitarRemoverItemMutation.isLoading}
              >
                Cancelar
              </button>
              <button
                className="btn-warning"
                onClick={enviarSolicitacaoRemoverItem}
                disabled={solicitarRemoverItemMutation.isLoading || !motivoRemocaoItem.trim()}
                style={{
                  background: '#fef3c7',
                  color: '#d97706',
                  border: '1px solid #d97706',
                  opacity: !motivoRemocaoItem.trim() ? 0.5 : 1
                }}
              >
                {solicitarRemoverItemMutation.isLoading ? 'Enviando...' : 'Solicitar Remoção'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Delivery */}
      <ModalDelivery
        isOpen={modalDelivery}
        onClose={() => setModalDelivery(false)}
        estabelecimento={estabelecimento}
        caixaId={caixaData?._id}
      />
    </div>
  );
};

export default CaixaFullscreen;

